// =============================================================================
// Partner Sales Template Library — Profit+ go-to-market
// Used by BDRs, AEs, Managers, Executives, and Marketing inside AIP.
// AIP is the platform; Profit+ is the product Partner AEs and BDRs sell.
// =============================================================================

export type Persona = "finance" | "operations" | "hr";
export type ScriptChannel = "email_opening" | "email_followup" | "linkedin_dm" | "phone_script" | "voicemail_script";

export interface TemplateScript {
  id: string;
  channel: ScriptChannel;
  persona: Persona;
  title: string;
  subject?: string;
  body: string;
  tokens: string[];
  notes?: string;
}

export interface TemplateObjection {
  id: string;
  objection: string;
  response: string;
  pivot_question: string;
  category: "budget" | "timing" | "authority" | "need" | "trust" | "competition" | "adoption" | "decision_process";
}

export interface TemplateQuestion {
  id: string;
  persona: Persona;
  question: string;
  why_it_matters: string;
  good_answer_reveals: string;
  category: "pain" | "budget" | "authority" | "need" | "timeline" | "decision_process" | "competition" | "adoption";
}

export type Competitor = "spreadsheets" | "manual_records" | "homegrown" | "brand_x";
export interface TemplateBattleCard {
  id: string;
  competitor: Competitor;
  positioning: string;
  strengths: string[];
  weaknesses: string[];
  pivot: string;
}

// =============================================================================
// CADENCE SCRIPTS (150 total)
// =============================================================================

export const CADENCE_SCRIPTS: TemplateScript[] = [
  // ─── EMAIL OPENING (30: 10 Finance, 10 Operations, 10 HR) ─────────────────
  // ─── FINANCE ──────────────────────────────────────────────
  {
    id: "eo-fin-001",
    channel: "email_opening",
    persona: "finance",
    title: "CFO cold open — validated vs executed savings gap",
    subject: "The gap between validated and executed savings at {company}",
    body: `Hi {first_name},

Quick question. Most CFOs I talk to in {industry} can tell me their cost-savings target for the year. Very few can tell me, in real time, how much of that number is validated versus actually executed and flowing through the P&L.

That gap — ideas approved but sitting between sign-off and G/L verification — is where profitability slips through the cracks. Not because of lack of intent, but because there is no system holding each initiative accountable from idea to realized savings.

Profit+ is a cost-optimization platform built to close that gap. Ideas are captured from every function and site, scored and ranked, routed through a standardized lifecycle (Idea → Validate → Evaluate → Implement → Executed → Realized), and Finance verifies the savings against the actual G/L before the number goes on the board.

A medical device manufacturer with six production sites we work with moved from chasing the savings number quarterly to managing it weekly once this discipline was in place. The platform is what made the savings trackable and defensible.

Worth fifteen minutes to compare notes on how your current process handles verification?

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{industry}", "{rep_signature}"],
    notes: "Lead with the validated-vs-executed gap. Best for CFOs who have published savings targets but cannot verify flow-through. Do not open with product features.",
  },
  {
    id: "eo-fin-002",
    channel: "email_opening",
    persona: "finance",
    title: "CFO cold open — leading vs lagging indicators",
    subject: "Are you seeing cost savings in real time, or at quarter-end?",
    body: `Hi {first_name},

One pattern I keep hearing from finance leaders across manufacturing: they find out cost initiatives missed the plan when the quarter closes. By then the corrective action is already late.

The root cause is usually structural — cost-saving ideas live in spreadsheets or project-tracking tools that nobody updates consistently. Finance sees the lagging number. Operations carries the blame. No one has a shared view of what is on track versus stalled.

Profit+ gives the CFO a real-time dashboard showing gap to goal, performance to budget, and gains versus losses across every plant and department — before the quarter closes, not after.

If you would find it useful to see how manufacturers have shifted from reactive to proactive cost management, I am happy to walk you through it in fifteen minutes.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Works well when you have a signal the company missed a cost target last quarter. Emphasizes the shift from lagging to leading indicators.",
  },
  {
    id: "eo-fin-003",
    channel: "email_opening",
    persona: "finance",
    title: "CFO cold open — P&L verification angle",
    subject: "How does Finance verify that cost savings actually hit the P&L at {company}?",
    body: `Hi {first_name},

Short question — when a cost-saving initiative is marked complete by Operations or Procurement, how does Finance verify that the savings actually showed up in the G/L?

At most manufacturers I work with, the honest answer is: it does not happen consistently. There is a handoff gap between execution and financial verification, and the result is that the reported savings number and the realized number diverge over time.

Profit+ closes that loop. Every idea goes through a standardized lifecycle that ends with G/L verification by Finance — not self-reported by the team that ran the project. The CFO gets a defensible, auditable savings number.

We work with manufacturers across industrial, medical device, and aerospace production — companies where the savings number has to hold up in a board presentation. If that gap is real at {company}, I think fifteen minutes would be worth your time.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Use when targeting Controller or VP Finance who owns financial verification. The G/L verification angle resonates most with accounting-minded finance leaders.",
  },
  {
    id: "eo-fin-004",
    channel: "email_opening",
    persona: "finance",
    title: "CFO cold open — EBITDA discipline angle",
    subject: "EBITDA improvement that shows up in the G/L — not just in the deck",
    body: `Hi {first_name},

Most cost-optimization programs generate impressive slides for the board but struggle to show a clean line to EBITDA improvement. The initiatives are real, the intentions are good, but the execution discipline is inconsistent — especially across multiple plants or divisions.

The problem is structural: there is no common operating system connecting idea generation to financial realization. Each plant runs its own version of the program, Finance reconciles the numbers after the fact, and the gap between projected and realized savings is always uncomfortable.

Profit+ is a platform designed to give multi-site manufacturers that execution discipline — a single system where every cost-saving idea, from any function or location, moves through a structured lifecycle and does not leave the system until Finance has verified the savings in the G/L.

I work with manufacturers in {industry}. Happy to share what the discipline has done for operating margin at comparable companies — no pitch, just a fifteen-minute conversation.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{industry}", "{rep_signature}"],
    notes: "Lead with EBITDA/board-level credibility gap. Best for CFOs preparing for a board review where savings numbers need to be defensible.",
  },
  {
    id: "eo-fin-005",
    channel: "email_opening",
    persona: "finance",
    title: "CFO cold open — multi-site visibility",
    subject: "Consolidated cost savings view across {site_count} — does Finance have it?",
    body: `Hi {first_name},

When you are managing cost-optimization initiatives across {site_count}, does Finance have one consolidated view of where each initiative stands — or does someone rebuild that view manually each month?

At most manufacturers, the honest answer is manual. Plant controllers submit their numbers, FP&A consolidates, and the result is always a few weeks behind reality.

Profit+ has a multi-tier target model where corporate targets cascade down to division, plant, and department level — and roll back up automatically. Finance sees the real-time picture: gap to goal, performance to budget, which locations are ahead, which are stalled, and what the risk is to the full-year number.

A building-materials manufacturer running nine plants we work with replaced their monthly consolidation exercise with a live dashboard the CFO checks every Monday. If that kind of visibility would change how Finance runs the business review at {company}, I would value fifteen minutes.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{site_count}", "{rep_signature}"],
    notes: "Use the {site_count} token when you have plant count data. Best for VP Finance or FP&A Director at multi-plant manufacturers.",
  },
  {
    id: "eo-fin-006",
    channel: "email_opening",
    persona: "finance",
    title: "CFO cold open — cost culture as a Finance problem",
    subject: "Cost discipline as a cultural problem — not just an operations problem",
    body: `Hi {first_name},

The manufacturers who close the gap between cost targets and realized savings consistently share one thing: Finance is in the loop from idea generation to G/L verification, not just at month-end close.

When Finance is only a recipient of the savings number — rather than a participant in the accountability process — the number is always a negotiation. Operations says they delivered. Finance says it did not show up. The board sees the gap.

Profit+ gives Finance a seat at the table at every stage: targets set at the corporate level, distributed to each location and function, tracked in real time, and verified against the actual G/L before a savings dollar is counted.

I am reaching out to finance leaders in {industry} this month. If cost discipline is a live priority for {company}, fifteen minutes is a good place to start.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{industry}", "{rep_signature}"],
    notes: "Best for CFOs who feel they are always reacting to the savings number rather than setting accountability up front. Positions Finance as a co-owner of the program.",
  },
  {
    id: "eo-fin-007",
    channel: "email_opening",
    persona: "finance",
    title: "CFO cold open — ERP integration angle",
    subject: "Cost savings verification that connects to your financial system",
    body: `Hi {first_name},

One question I often ask CFOs in manufacturing: when a cost-saving initiative is closed out, does the savings figure get reconciled directly against your financial system, or does it live in a separate tool that Finance never opens?

For most companies it is the latter — and that means the savings number is always an estimate, not a verified figure. The CFO is reporting a projection, not a result.

Profit+ integrates with ERP and financial systems via API to pull actual cost data and verify G/L impact at the point of realization. Every idea that reaches the Realized stage has a financial signature behind it that Finance can stand behind.

We have implemented this for manufacturers across industrial equipment, aerospace, and contract electronics — companies where the ERP is already the source of truth and the cost-optimization layer needs to connect to it. If that is the situation at {company}, I would welcome fifteen minutes.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Strong angle for Controllers and technology-forward CFOs who have already invested in ERP and want the cost-savings layer to connect to it.",
  },
  {
    id: "eo-fin-008",
    channel: "email_opening",
    persona: "finance",
    title: "CFO cold open — idea ranking and capital prioritization",
    subject: "How does {company} prioritize which cost initiatives to fund this year?",
    body: `Hi {first_name},

Every manufacturer I work with has more cost-saving ideas than capital to pursue them. The question is whether there is a systematic way to rank them — by maturity, by expected value, by readiness to execute — or whether prioritization happens in a budget meeting based on whoever made the strongest case.

When prioritization is informal, Finance ends up funding the ideas that are best presented, not necessarily the ones with the highest return or the highest probability of realization.

Profit+ scores and ranks every idea by maturity and value, routes them through a structured evaluation and approval workflow, and gives Finance a real-time view of which initiatives have the best risk-adjusted return — before capital is committed.

This matters as much at an IoT hardware manufacturer scaling a new production line as it does at a heavy-equipment OEM with a large capital budget. If structured prioritization is missing from the process at {company}, worth fifteen minutes to walk through it.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Best timing is pre-budget season or during a strategic cost review. Positions Profit+ as a capital allocation tool, not just a tracking tool.",
  },
  {
    id: "eo-fin-009",
    channel: "email_opening",
    persona: "finance",
    title: "CFO cold open — cross-functional accountability",
    subject: "When a cost initiative stalls, who is accountable at {company}?",
    body: `Hi {first_name},

Here is a scenario I hear from CFOs regularly: a cost initiative is approved, assigned to a cross-functional team, and six months later Finance pulls the thread and finds out it never moved past the planning stage. Nobody flagged it. Nobody owned the delay.

The core problem is that most cost-tracking tools are either Finance tools that Operations does not use, or Operations tools that Finance cannot see. There is no shared platform, so accountability falls through the middle.

Profit+ gives every stakeholder — Finance, Operations, HR, Procurement — a single view of every initiative, where it stands, who owns the next step, and whether it is on track to close the gap to goal. When something stalls, it is visible to everyone, including the CFO.

This is as relevant at an aerospace components manufacturer managing cross-functional programs as at a contract manufacturer with a distributed plant network. If cross-functional accountability is a live problem at {company}, happy to discuss.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Works well at companies where cost initiatives fall through the cracks due to diffuse ownership. The shared accountability angle is powerful for CFOs frustrated with siloed reporting.",
  },
  {
    id: "eo-fin-010",
    channel: "email_opening",
    persona: "finance",
    title: "CFO cold open — Monday-morning savings dashboard",
    subject: "What does your cost-savings dashboard look like on a Monday morning?",
    body: `Hi {first_name},

One diagnostic question I use with finance leaders in manufacturing: on a Monday morning, can you open a single screen and see your gap to cost-savings goal, broken down by plant, function, and initiative stage — or do you have to wait for someone to compile the report?

At most manufacturers the answer is the latter. The number is assembled weekly or monthly from site-level inputs; by the time Finance sees it, the week is already underway with no ability to course-correct.

Profit+ is built to give the CFO that Monday-morning view in real time — gap to goal, performance to budget, which initiatives are in-flight versus stalled, and a heat map of which plants are carrying the program versus which are at risk.

I work with manufacturers in {industry} who have made this shift. If that kind of visibility would change how Finance leads the cost conversation at {company}, fifteen minutes is a reasonable investment.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{industry}", "{rep_signature}"],
    notes: "The Monday-morning dashboard is a vivid, concrete frame. Best for CFOs frustrated with the lag between when issues happen and when Finance learns about them.",
  },
  // ─── OPERATIONS ──────────────────────────────────────────────
  {
    id: "eo-ops-001",
    channel: "email_opening",
    persona: "operations",
    title: "COO cold open — ideas die without execution discipline",
    subject: "Cost-saving ideas at {company} — how many survive past the whiteboard?",
    body: `Hi {first_name},

Most plant operations teams I talk to have no shortage of cost-saving ideas. The problem is what happens after the brainstorming session — ideas end up on flip charts, get captured in spreadsheets, and then weeks go by with no movement. Eventually the list goes stale and the next workshop starts from scratch.

Profitability is slipping through the cracks — not because of lack of intent, but due to lack of discipline in execution.

Profit+ gives every idea the same structured lifecycle as a capital project: Idea → Validate → Evaluate → Implement → Executed → Realized. Every initiative has an owner, a stage, and a target date. Nothing disappears into a spreadsheet.

We work with operations leaders across industrial manufacturing, aerospace, and contract production — companies where the savings are real but the execution discipline has been the missing piece. If that is the gap at {company}, I would value fifteen minutes.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Use with COOs and VP Operations who have active lean or CI programs but struggle with follow-through. The whiteboard-to-action gap is universally recognized across all manufacturing verticals.",
  },
  {
    id: "eo-ops-002",
    channel: "email_opening",
    persona: "operations",
    title: "COO cold open — read-across between plants",
    subject: "Are your plants sharing what works — or reinventing the wheel?",
    body: `Hi {first_name},

Here is a pattern I see often at multi-site manufacturers: Site A solves a cycle time problem on a production line. Sites B and C have the same problem, but nobody knows about the fix at Site A. Two years later someone discovers it by accident.

The cost of reinventing the wheel across plants is real, but it is invisible on the P&L because you never see the savings you did not capture.

Profit+ has a Read-Across feature that lets any plant search the shared idea library, clone a proven savings idea from another site, and adapt it for local conditions. Proven savings travel across the organization instead of dying at the plant gate.

A contract electronics manufacturer we work with used this to replicate a materials-waste reduction idea from one production cell across eleven lines in eight weeks. If ideas are not flowing between your sites at {company}, that is a conversation worth having.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{site_count}", "{rep_signature}"],
    notes: "Best for multi-plant VP Operations. The read-across angle is a clear differentiator — most operations leaders immediately recognize the reinventing-the-wheel problem.",
  },
  {
    id: "eo-ops-003",
    channel: "email_opening",
    persona: "operations",
    title: "COO cold open — lean program without measurable savings",
    subject: "Lean program at {company} — are the savings measurable?",
    body: `Hi {first_name},

Most manufacturers running lean or continuous improvement programs face the same honest question after a few years: how much of the activity is translating into dollars the CFO can see on the P&L?

Lean tools are great at identifying waste and running kaizen events, but they were not designed to track ideas from shop-floor capture through to G/L-verified savings. The two worlds do not connect, and Finance is left trying to reconcile activity with financial results.

Profit+ sits at that intersection — a platform that captures every improvement idea regardless of source (lean events, shop-floor operators, supplier negotiations, department heads), routes them through a standard lifecycle with approval gates, and ends with Finance verifying the savings against the actual G/L.

We have implemented this with lean teams at medical device manufacturers, industrial equipment OEMs, and aerospace suppliers. If you are running a lean program at {company} and want the savings to be defensible, I think fifteen minutes is worth your time.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Strong angle for VP Lean / Op-Ex Manager / Director CI. Positions Profit+ as the financial tracking layer that makes lean programs credible to Finance.",
  },
  {
    id: "eo-ops-004",
    channel: "email_opening",
    persona: "operations",
    title: "Plant Manager cold open — shop-floor idea capture",
    subject: "Every operator has ideas — is {company} capturing them?",
    body: `Hi {first_name},

The people closest to the work usually have the best ideas for improving it. But at most plants, there is no structured way for an operator or technician to get an idea into the system quickly — so the ideas either never get raised or get lost in a verbal conversation that nobody follows up on.

Profit+ includes a mobile and QR-code idea entry system so any employee on the production floor can submit a cost-saving idea in under two minutes. Those ideas flow directly into the platform, get reviewed by the appropriate owner, and — if they pass evaluation — move through the same standardized lifecycle as a top-down initiative.

The result is a broader pool of ideas and a feedback loop back to the submitter so they know what happened. Whether you are running a single plant or a multi-site network, that combination changes how engaged the floor is in the cost program.

Happy to show you what that looks like in practice at {company}.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Works well with Plant Managers and Directors of Operations across all manufacturing verticals. Pairs the operational and engagement angles naturally.",
  },
  {
    id: "eo-ops-005",
    channel: "email_opening",
    persona: "operations",
    title: "Operations cold open — central function targets to plant floor",
    subject: "How do cost targets from corporate reach the plant floor at {company}?",
    body: `Hi {first_name},

One of the harder organizational challenges in manufacturing is getting corporate cost targets to land at the plant level in a way that is specific, trackable, and owned. The typical path — corporate sets a number, plants get a spreadsheet, finance follows up quarterly — means three months of drift before anyone knows a plant is off track.

Profit+ has a multi-tier target model where corporate targets cascade down to division, plant, and department level automatically. Each location sees its own target, its current gap, and the initiatives in-flight to close it. Central function targets are distributed to each location with full transparency.

When something is off track, it is visible in real time — not at quarter-end. This matters whether you are running {site_count} in one country or plants across multiple continents.

If target distribution and plant-level accountability is a challenge at {company}, I would value a fifteen-minute conversation.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{site_count}", "{rep_signature}"],
    notes: "Best for COO or VP Operations at companies with complex org structures where corporate targets fail to translate into plant-level accountability.",
  },
  {
    id: "eo-ops-006",
    channel: "email_opening",
    persona: "operations",
    title: "VP Procurement cold open — savings-quota operating system",
    subject: "Procurement savings quota at {company} — validated vs executed",
    body: `Hi {first_name},

Most procurement leaders I talk to are carrying a gap between validated savings and executed savings — category programs that cleared approval but stalled at rollout because execution lives in spreadsheets that nobody updates from week to week.

That gap — validated but not executed — is where procurement's savings number gets eroded between approval and the CFO's monthly review.

Profit+ is a platform that gives procurement teams a structured operating system for their savings programs: category initiatives captured and ranked, routed through evaluation and approval workflows, tracked to implementation across sites, and verified by Finance when savings hit the G/L.

We work with procurement teams in industrial manufacturing, heavy equipment, and contract production — wherever there is a savings quota and the execution discipline to back it up is still being built. If closing that gap at {company} is a live priority, fifteen minutes is a good place to start.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Lead procurement touch. Use with VP Procurement or CPO who carries a published savings number. Reference a specific category if you have intelligence.",
  },
  {
    id: "eo-ops-007",
    channel: "email_opening",
    persona: "operations",
    title: "VP Procurement cold open — piece-price and supplier savings tracking",
    subject: "Supplier negotiation savings — how does {company} track them to the P&L?",
    body: `Hi {first_name},

Supplier negotiation programs generate significant savings on paper. The harder question is how those savings get tracked from commitment to actual piece-price change to G/L verification — especially when the rollout touches multiple production sites.

At most manufacturers, piece-price changes are tracked in separate tools by plant or by commodity team, Finance gets an aggregate number, and reconciling back to the original commitment is a manual exercise that takes weeks.

Profit+ gives procurement and finance a single platform to track supplier negotiation savings from initial approval through piece-price implementation at each site, with financial verification built in at the end of the process.

This is a common pain for procurement teams in automotive, aerospace, and industrial equipment supply chains. If piece-price tracking and P&L verification are live challenges at {company}, I would welcome fifteen minutes.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Targets VP Procurement and CPO. The piece-price tracking angle is specific and credible for manufacturing procurement across all verticals.",
  },
  {
    id: "eo-ops-008",
    channel: "email_opening",
    persona: "operations",
    title: "VP Procurement cold open — supplier ecosystem engagement",
    subject: "Are your suppliers contributing to cost savings at {company}?",
    body: `Hi {first_name},

Some of the best cost-saving ideas in manufacturing come from the supply base — suppliers who see inefficiencies that the plant team has been living with for years. But most procurement programs have no structured channel for capturing supplier-generated ideas and routing them through evaluation and approval.

The result is that supplier savings opportunities either never surface or get captured informally in email threads that nobody tracks.

Profit+ includes a supplier ecosystem engagement channel — a structured way for suppliers, contractors, and internal teams to submit ideas through the same platform. Every idea, regardless of source, moves through the same standardized lifecycle and gets financially verified the same way.

An aerospace components manufacturer we work with opened this channel to their top forty suppliers and identified savings in tooling and logistics that procurement had not previously surfaced internally. If engaging your supply base as an idea source is something {company} has been thinking about, I would value a conversation.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Works for CPOs and Directors of Sourcing at companies with large supply bases. Positions Profit+ as a supplier collaboration tool, not just an internal tracker.",
  },
  {
    id: "eo-ops-009",
    channel: "email_opening",
    persona: "operations",
    title: "VP Procurement cold open — indirect spend and MRO",
    subject: "Indirect spend and MRO savings — does {company} have a scoreboard?",
    body: `Hi {first_name},

Indirect spend and MRO consolidation programs are some of the highest-return cost initiatives in manufacturing — and also some of the most difficult to track to actual savings. Approvals happen at the category level, implementation happens at the plant level, and Finance ends up verifying through a reconciliation exercise that is always backward-looking.

Without a shared scoreboard, procurement cannot show the CFO a real-time view of which categories are on track and which are stalled.

Profit+ gives procurement a real-time platform where every category initiative — indirect, MRO, freight, packaging, energy — has an owner, a stage, a target, and a G/L verification step at the end. The CFO sees the same number procurement reports because it comes from the same system.

Whether you are running this at a medical device manufacturer with complex indirect categories or a heavy-equipment OEM with large MRO spend, the scoreboard problem is the same. If it is live at {company}, fifteen minutes is worthwhile.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Specifically targets indirect/MRO programs. Best for procurement leaders at manufacturers where indirect spend is a major cost category with limited tracking discipline.",
  },
  {
    id: "eo-ops-010",
    channel: "email_opening",
    persona: "operations",
    title: "Director CI cold open — op-ex results without financial proof",
    subject: "Continuous improvement results at {company} — are they showing up in the P&L?",
    body: `Hi {first_name},

Continuous improvement programs generate a lot of activity — kaizen events, lean workshops, process redesigns, energy audits. The harder question is how much of that activity is translating into savings that Finance can verify in the G/L.

At most manufacturers, CI teams report activity metrics because the financial tracking is too difficult to maintain. The CFO sees the activity report and still asks the same question: where is the margin improvement?

Profit+ connects CI activity to financial results — every idea captured through a lean event or shop-floor submission moves through a lifecycle that ends with G/L verification by Finance. Activity and financial results live in the same system.

This has been particularly useful at manufacturers running ISO or AS9100-aligned programs — where documentation and verification discipline are already part of the culture, and Profit+ adds the financial verification layer. If making your CI program financially defensible is a goal at {company}, I would value fifteen minutes.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Best for Directors of CI and Op-Ex Managers frustrated that their programs do not produce verifiable financial results. The activity-vs-results tension is a known pain.",
  },
  // ─── HR ──────────────────────────────────────────────
  {
    id: "eo-hr-001",
    channel: "email_opening",
    persona: "hr",
    title: "CHRO cold open — every employee has ideas",
    subject: "What if every employee at {company} had a voice in cost optimization?",
    body: `Hi {first_name},

One thing that distinguishes manufacturers with strong cost cultures from those without: they have found a way to give every employee — from the production floor to the boardroom — a structured channel to contribute ideas that get taken seriously and acted upon.

The challenge is usually not motivation. Employees have ideas. The challenge is the absence of a feedback loop. Ideas go into a suggestion box or get raised in a team meeting and nothing visibly happens. Over time, people stop contributing.

Profit+ includes a citizen idea channel with mobile and QR-code entry — any employee can submit a cost-saving idea in minutes. Each submission gets reviewed, and the idea submitter receives a feedback update at each stage so they know what happened. When ideas get implemented, the submitter sees the result.

That feedback loop is what sustains engagement over time rather than burning it out in the first year. It works the same way whether you are running a single plant or a multi-site network spanning different languages and time zones.

Happy to share what this looks like in practice if it is relevant to where {company} is heading.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Lead HR touch. Position the feedback loop as the key differentiator. Do not lead with cost savings numbers; lead with engagement.",
  },
  {
    id: "eo-hr-002",
    channel: "email_opening",
    persona: "hr",
    title: "CHRO cold open — culture of continuous improvement",
    subject: "Making cost optimization part of the culture at {company}",
    body: `Hi {first_name},

The manufacturers who sustain cost improvement year over year share a common characteristic: cost optimization is part of how they work, not a program that runs for a quarter and then fades. It is in their DNA.

Building that culture requires more than leadership communication. It requires a system that gives every employee a meaningful role — a way to contribute, a way to see the impact of their contribution, and a feedback loop that reinforces participation over time.

Profit+ is built for that purpose. The citizen idea channel, mobile idea entry, QR codes at each workstation, and automated feedback to every submitter are designed to make contribution easy and visible, regardless of whether someone is on a production floor, in an engineering bay, or at a remote facility.

A heavy-equipment OEM we work with described the shift after their first year: the cost optimization conversation stopped being something leadership drove and started being something everyone owned. If building that kind of culture at {company} is a priority this year, I would welcome a conversation.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Use for CHROs and VP HR working on culture and engagement programs. Connect the engagement angle to cost culture naturally.",
  },
  {
    id: "eo-hr-003",
    channel: "email_opening",
    persona: "hr",
    title: "VP HR cold open — tying engagement to business outcomes",
    subject: "Employee engagement tied to measurable outcomes — how does {company} do it?",
    body: `Hi {first_name},

One challenge HR leaders in manufacturing often raise: employee engagement scores look good, but the connection to actual business outcomes — productivity, cost savings, retention — is hard to demonstrate to the CEO or CFO.

When engagement is measured in surveys and business outcomes are measured in financial reports, the two live in different worlds and the link stays theoretical.

Profit+ creates a direct, measurable connection: employee ideas flow into the platform, ideas that get implemented show a financial result, and the engagement metric (ideas submitted, ideas implemented, percentage of employees contributing) sits alongside the financial result in the same dashboard.

That gives HR a concrete story to bring to the CFO about what employee engagement is actually producing for the business — whether you are at a medical device manufacturer, a contract electronics company, or an industrial equipment OEM.

If that kind of connection is something you have been working toward at {company}, I would value fifteen minutes.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Best for VP HR and Director of OD who need to demonstrate the business impact of engagement programs. Positions HR as a contributor to financial results.",
  },
  {
    id: "eo-hr-004",
    channel: "email_opening",
    persona: "hr",
    title: "VP HR cold open — employee-based incentive programs",
    subject: "Incentive programs tied to cost savings at {company} — structured or informal?",
    body: `Hi {first_name},

When employees contribute ideas that generate real cost savings, the recognition and incentive question eventually surfaces: how does the company acknowledge that contribution in a structured, consistent way?

Most manufacturers handle this informally — manager discretion, ad hoc recognition — which means the incentive is inconsistent and not visible enough to reinforce the behavior broadly.

Profit+ gives HR the infrastructure to run structured employee-based incentive programs. Because every idea and its financial outcome are tracked in the platform, the data for a fair, transparent incentive calculation is already there. HR defines the recognition rules, and the system handles the tracking.

This is as relevant at a contract manufacturer with workers across multiple sites as it is at a single-plant precision-parts manufacturer. If building a structured program for cost-savings contributions is something {company} has been considering, I would welcome a brief conversation.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Strong angle for HR leaders being asked by Operations or the CEO to build a more structured incentive program tied to cost improvement.",
  },
  {
    id: "eo-hr-005",
    channel: "email_opening",
    persona: "hr",
    title: "VP HR cold open — global employee engagement across sites",
    subject: "How does {company} run employee engagement across {site_count}?",
    body: `Hi {first_name},

Running a consistent employee engagement program across {site_count} is one of the harder organizational challenges in manufacturing. What works at the flagship plant does not always translate to smaller sites. Language, culture, and shift structure all create friction.

Profit+ is built for global, multi-site manufacturing: multi-lingual idea entry, mobile-ready for production-floor employees without desk access, QR codes at each workstation, and a feedback loop that works regardless of where an employee sits.

The engagement metric — percentage of employees contributing ideas, ideas per site, ideas implemented per quarter — rolls up to a single dashboard that HR and the CEO can read the same way.

An industrial manufacturer with plants across North America and Europe we work with uses this to run a consistent program across fifteen sites in five languages. If consistency and scale in employee engagement is a challenge at {company}, I think fifteen minutes is worth your time.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{site_count}", "{rep_signature}"],
    notes: "Best for VP HR at multi-site manufacturers who struggle with consistency across plants. The multi-lingual and mobile angles are practical differentiators at scale.",
  },
  {
    id: "eo-hr-006",
    channel: "email_opening",
    persona: "hr",
    title: "Director Employee Engagement cold open — shop floor to boardroom",
    subject: "Shop floor to boardroom — does everyone at {company} play a role?",
    body: `Hi {first_name},

There is a meaningful difference between a company where cost optimization is something leadership talks about and a company where every employee — at the machine, in the clean room, in the office — has a visible, structured role in it.

The second company is harder to build, but the results are measurable and the culture is more durable.

Profit+ makes it practical: an operator on the production floor can submit a cost-saving idea via a QR code on their workstation in under two minutes. That idea moves through the same evaluation and approval process as a corporate initiative. When it is implemented, the submitter receives a notification showing what happened.

When everyone plays a role and everyone sees feedback, the culture of continuous improvement becomes self-sustaining. Happy to share what this looks like at {company}'s scale if it is relevant.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Best for Director of Employee Engagement or VP People and Culture. The shop-floor-to-boardroom arc resonates with engagement professionals across all manufacturing verticals.",
  },
  {
    id: "eo-hr-007",
    channel: "email_opening",
    persona: "hr",
    title: "CHRO cold open — feedback loop to idea submitters",
    subject: "What happens to employee ideas at {company} after they are submitted?",
    body: `Hi {first_name},

Most employee suggestion programs fail for the same reason: there is no visible feedback loop. An idea gets submitted and the employee hears nothing for weeks. Eventually the idea gets quietly closed or implemented without credit, and the employee stops contributing.

The feedback loop is not a nice-to-have. It is the mechanism that determines whether the program sustains engagement or burns it out.

Profit+ has an automated feedback loop built into the lifecycle of every idea. When an idea advances from Idea to Validation, the submitter is notified. When it is implemented, they are notified. When it is not moving forward, they receive a reason. The transparency turns a one-way suggestion process into a two-way conversation.

This matters whether you are managing engagement at a three-plant industrial manufacturer or a medical device company with global production. If rebuilding trust in the idea-contribution process at {company} is something you are working on, I would value fifteen minutes.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Works for any HR persona who has run a suggestion program that failed due to poor follow-through. The feedback loop is a concrete, operational differentiator.",
  },
  {
    id: "eo-hr-008",
    channel: "email_opening",
    persona: "hr",
    title: "VP Talent cold open — linking talent strategy to operational results",
    subject: "Does your talent strategy connect to operational cost results at {company}?",
    body: `Hi {first_name},

One question I hear from talent leaders in manufacturing: how do you make the case to the CFO that talent and culture investments are producing operational results — not just softer outcomes?

The challenge is that the data usually lives in separate systems. Engagement scores in the HRIS. Cost savings in the ERP. The connection between them is a narrative, not a number.

Profit+ creates the infrastructure to make that connection measurable. When employees contribute ideas that generate verified cost savings, both the contribution and the financial result are in the same platform. HR can show the CFO: employees from these plants contributed X ideas, Y were implemented, the realized savings were Z.

That is a different conversation than presenting an engagement score and asking for budget. I have seen this work for talent leaders at IoT hardware manufacturers, aerospace suppliers, and building-materials companies alike. If building that data bridge is a goal at {company}, I would welcome a conversation.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Best for VP Talent and Chief People Officers trying to demonstrate ROI on talent and culture investments.",
  },
  {
    id: "eo-hr-009",
    channel: "email_opening",
    persona: "hr",
    title: "Director OD cold open — accountability infrastructure for cost culture",
    subject: "How does {company} build accountability for cost improvement across functions?",
    body: `Hi {first_name},

Organizational development leaders often get pulled into cost transformation programs at the cultural layer — helping build the mindset and behaviors that make the program sustainable. The practical challenge is that culture without structure does not hold.

When there is no platform giving every employee a visible role, a feedback loop, and a connection to business outcomes, the cultural program becomes dependent on individual leaders rather than the organization.

Profit+ provides the structural layer that makes the cultural program stick: mobile idea entry for every employee, approval workflows that give managers a clear role, feedback loops that keep contributors engaged, and dashboards that show every team their contribution to the overall cost goal.

Whether you are supporting a transformation at a contract manufacturer or an industrial equipment OEM, the structural challenge is the same. If you are working on the accountability and engagement infrastructure for a cost program at {company}, I think this is worth a brief conversation.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Best for Director of OD supporting a cost transformation program. Positions Profit+ as the structure that makes the cultural work sustainable.",
  },
  {
    id: "eo-hr-010",
    channel: "email_opening",
    persona: "hr",
    title: "CHRO cold open — meeting employees where they work",
    subject: "Meeting employees where they work at {company}",
    body: `Hi {first_name},

One practical barrier to employee idea programs in manufacturing is friction at the point of contribution. If submitting an idea requires logging into a separate system, navigating to a form, and waiting for a response, most employees will not bother — especially frontline workers on shift without regular computer access.

Profit+ is designed to reduce that friction: mobile-ready native app for iOS and Android, QR code idea entry at the workstation, and MS Teams integration so office-based employees can submit ideas from the tools they are already in every day.

The lower the friction, the broader the participation. Broader participation means a more representative picture of where improvement opportunities exist — not just the ideas that make it through the management filter.

Whether your workforce spans a single building-materials plant or a global IoT electronics network, the friction problem is the same. If removing the practical barriers to participation is something you are thinking about at {company}, I would value a brief conversation.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Use for CHROs at companies that have invested in MS Teams or mobile-first workforce tools. The frictionless contribution angle is practical and differentiating.",
  },
  // ─── EMAIL FOLLOWUP (30: 10 Finance, 10 Operations, 10 HR) ─────────────────
  // ─── FINANCE ──────────────────────────────────────────────
  {
    id: "ef-fin-001",
    channel: "email_followup",
    persona: "finance",
    title: "Finance follow-up — savings-lost is invisible on the P&L",
    subject: "Re: cost savings at {company} — one more thought",
    body: `Hi {first_name},

Following up on my earlier note. One thing I did not mention: the cost of a weak savings-tracking process is invisible on the P&L.

There is no line item for "ideas that died in implementation" or "savings approved but never verified." You just see margin that is lower than it should be and no clear explanation for why.

Profit+ makes the invisible visible — every idea that stalls, every initiative that misses its realization date, every savings dollar that was committed but not delivered shows up on the dashboard before it becomes a P&L problem.

If that framing is relevant to a conversation happening at {company} right now, I am happy to schedule fifteen minutes.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Day 4-7 follow-up to finance cold email. Short, adds a new frame. The invisible P&L cost angle resonates across all manufacturing verticals.",
  },
  {
    id: "ef-fin-002",
    channel: "email_followup",
    persona: "finance",
    title: "Finance follow-up — Finance as participant not auditor",
    subject: "One question about how Finance is involved in cost programs at {company}",
    body: `Hi {first_name},

One more thought since I have not heard back. A question worth asking: in your current cost-optimization process, is Finance involved from the beginning — when targets are set and ideas are generated — or does Finance come in at the end to verify the number?

Most manufacturers structure it as verification-only for Finance. The cost of that structure is that Finance is always reacting rather than shaping. By the time Finance sees the gap, the quarter is already committed.

Profit+ is designed to give Finance a seat at the table from target-setting through G/L verification — not just at month-end close. The result is a savings number Finance owns, not one it is handed.

Happy to show you what that looks like in practice. Fifteen minutes at your convenience.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Second follow-up. Reframes Finance's role from auditor to participant — resonates with proactive CFOs across all manufacturing sectors.",
  },
  {
    id: "ef-fin-003",
    channel: "email_followup",
    persona: "finance",
    title: "Finance follow-up — operating margin proof point",
    subject: "A data point that might be relevant to {company}",
    body: `Hi {first_name},

One data point before I stop following up. A Tier 1 automotive supplier we work with moved their operating income margin from 3% to over 7% across five years through disciplined cost optimization — not through revenue growth or restructuring.

The same discipline has produced meaningful margin improvements at an aerospace components manufacturer and a multi-plant industrial equipment OEM we work with. The common thread is not the industry — it is capturing every idea from every function and site, and verifying each one against the G/L until the savings are real.

If that kind of sustained margin improvement is relevant to {company}, I think fifteen minutes is worth your time. If the timing is off, I am happy to reconnect whenever the conversation is live.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Third follow-up. Uses anonymized proof points with industry variety. Never name the source. Creates a reason to reply even if the prospect is not immediately ready.",
  },
  {
    id: "ef-fin-004",
    channel: "email_followup",
    persona: "finance",
    title: "Finance follow-up — breakup",
    subject: "Closing the loop, {first_name}",
    body: `Hi {first_name},

I have reached out a few times without hearing back, so I will take that as a signal that either the timing is off or this is not a priority right now at {company}.

I will stop following up after this note. If cost-savings tracking and P&L verification become a live topic — whether it is a missed savings target, a new initiative, or a CFO asking where the margin went — I am easy to find.

One last thought worth leaving you with: profitability is rarely lost because of bad ideas. It is lost because good ideas do not make it from approval to G/L verification with any discipline. Profit+ is built to close that gap.

Take care.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Day 14 breakup. Short, neutral, and leaves a useful thought. No sarcasm, no pressure.",
  },
  {
    id: "ef-fin-005",
    channel: "email_followup",
    persona: "finance",
    title: "Finance follow-up — event-triggered",
    subject: "Saw the {company} announcement — quick follow-up",
    body: `Hi {first_name},

Saw the recent news from {company}. Cost discipline tends to become a more visible priority after moments like this — either because there is new pressure on margin or because there is a new mandate to demonstrate savings rigor.

I reached out a couple of weeks ago about Profit+, a platform for tracking cost-saving initiatives from generation to G/L-verified realization across multi-site manufacturers.

If the current environment at {company} has made this a more timely conversation, I am happy to reconnect — fifteen minutes, no deck, just a comparison of notes on how finance leaders in {industry} are approaching the problem.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{industry}", "{rep_signature}"],
    notes: "Use when a trigger event occurs — earnings call, executive change, cost reduction announcement. Reference the trigger specifically in the first line.",
  },
  {
    id: "ef-fin-006",
    channel: "email_followup",
    persona: "finance",
    title: "Finance follow-up — multi-tier target visibility",
    subject: "One thing I forgot to mention about target visibility, {first_name}",
    body: `Hi {first_name},

One detail I did not cover in my first note that tends to resonate with finance leaders: Profit+ has a multi-tier target model where the corporate cost savings goal cascades down to each division, plant, and department automatically.

That means when the CFO sets a target for the year, every location has a visible share of that target — and every location's gap to goal rolls back up to the corporate dashboard in real time.

No manual consolidation, no waiting for plant controllers to submit their numbers. Finance sees the same picture as operations, at the same time. This is as relevant at a building-materials manufacturer with distributed sites as it is at a contract electronics company with a single large campus.

If that kind of consolidated visibility is something Finance is currently missing at {company}, worth a quick conversation.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Day 4-7 follow-up introducing the multi-tier target angle. Best when you know the company has multiple plants or divisions.",
  },
  {
    id: "ef-fin-007",
    channel: "email_followup",
    persona: "finance",
    title: "Finance follow-up — idea ranking and capital allocation",
    subject: "Which cost initiatives deserve capital first at {company}?",
    body: `Hi {first_name},

Following up with a different angle: one of the less-discussed problems in cost management is not tracking savings after the fact, but deciding which initiatives to fund before the fact.

When every initiative is presented with equal urgency and Finance has no objective way to rank them, capital tends to flow to the ideas that are politically well-positioned rather than the ones with the best financial return.

Profit+ scores and ranks every cost-saving idea by maturity and expected value before it reaches the approval stage. Finance gets an objective basis for prioritization — not a judgment call, but a ranked list built from consistent criteria applied across all functions and plants.

This matters across all types of manufacturing — whether the ideas are coming from an MRO team at an industrial equipment OEM or a value-engineering group at a medical device manufacturer. If that kind of structured prioritization is missing from the current process at {company}, it is worth fifteen minutes.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Second or third follow-up. Introduces idea scoring and prioritization as a Finance-specific angle. Works well in pre-budget season.",
  },
  {
    id: "ef-fin-008",
    channel: "email_followup",
    persona: "finance",
    title: "Finance follow-up — ERP integration preempt",
    subject: "Quick follow-up: does Profit+ connect to your financial system?",
    body: `Hi {first_name},

Following up on my earlier note. A question I often get from finance leaders: does Profit+ connect to existing ERP and financial systems, or does it create another silo?

The answer is that it integrates via API with your existing financial system to pull actual cost data for G/L verification. The savings number in Profit+ and the number in your ERP are reconciled, not estimated.

That integration is what makes the savings figure defensible in a board presentation — it is not a parallel system, it is a layer on top of the financial source of truth you already own. We have implemented this alongside SAP, Oracle, and several other platforms at manufacturers in industrial, aerospace, and medical device production.

If the integration question was a practical concern at {company}, happy to address it in fifteen minutes.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Use when you suspect ERP integration is a barrier. Preempts the 'we already have tools' objection by clarifying that Profit+ works alongside existing financial systems.",
  },
  {
    id: "ef-fin-009",
    channel: "email_followup",
    persona: "finance",
    title: "Finance follow-up — verified vs self-reported savings",
    subject: "The defensible savings number — does {company} have one?",
    body: `Hi {first_name},

One last follow-up before I close the loop. A simple question: when your CFO presents the cost savings result to the board, is the number verified against the G/L, or is it self-reported by the teams that ran the programs?

That distinction matters more than it might seem. A verified number is a fact. A self-reported number is an opinion. In a board meeting, they are treated very differently.

Profit+ is built to produce the verified number — every realized savings idea has a G/L verification step before it counts. The result is a defensible figure that Finance can stand behind.

This is equally relevant whether you are presenting to a board at a heavy-equipment OEM or to a parent company at a contract manufacturer. If that matters to {company}'s next board review, I am easy to reach.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Strong final follow-up. The verified vs self-reported distinction creates a clear, memorable frame. Works well when the prospect has an upcoming board review.",
  },
  {
    id: "ef-fin-010",
    channel: "email_followup",
    persona: "finance",
    title: "Finance follow-up — plant heat map and performance to budget",
    subject: "Which of your plants is carrying the cost program — and which are at risk?",
    body: `Hi {first_name},

A different angle in this follow-up: in most multi-site manufacturers, a small number of plants carry a disproportionate share of the cost-savings delivery. The rest are underperforming against their targets — but Finance does not know which plants until it is too late to do anything about it.

Profit+ includes a heat map dashboard that shows performance to budget by plant, in real time. Finance can see which locations are ahead, which are behind, and what the risk is to the full-year number — while there is still time to act.

Whether you are running six ISO-certified production sites or twenty plants across three continents, the visibility gap is the same. If plant-level performance visibility is missing from Finance's current toolkit at {company}, I think it is worth fifteen minutes.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Works well as a later-stage follow-up. The heat map angle is concrete and visual. Best for CFOs managing large portfolios of plants.",
  },
  // ─── OPERATIONS ──────────────────────────────────────────────
  {
    id: "ef-ops-001",
    channel: "email_followup",
    persona: "operations",
    title: "Operations follow-up — stalled program angle",
    subject: "Re: cost programs at {company} — one more thought",
    body: `Hi {first_name},

Following up on my earlier note. One thing worth adding: the most common root cause of a stalled cost program is not a lack of good ideas — it is that ideas stop moving after approval because there is no owner, no stage, and no deadline attached to them.

Profit+ enforces a standard implementation lifecycle on every idea — every initiative has an assigned owner and a current stage, and nothing reaches the Executed status until it has been through the approval gates. The visibility makes it uncomfortable for anything to stall unnoticed.

Whether the stalled program is an MRO consolidation at an industrial equipment plant or a cycle-time initiative at a contract manufacturer, the lifecycle discipline is the same.

If there is a program at {company} that has been sitting in a spreadsheet longer than it should, that is the right test case for a fifteen-minute conversation.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Day 4-7 follow-up to operations cold email. Short and specific — references a stalled program scenario that most ops leaders recognize.",
  },
  {
    id: "ef-ops-002",
    channel: "email_followup",
    persona: "operations",
    title: "Operations follow-up — plant comparison and heat map",
    subject: "Which of your plants is leading on cost savings — and do you know why?",
    body: `Hi {first_name},

A follow-up question worth raising: across your plants, do you have a way to compare cost-savings performance by site — to see which plants are generating the most ideas, implementing the most, and realizing the most savings?

Most operations leaders can name the top plant intuitively, but they do not have a data-driven view to understand why it is outperforming or how to replicate the conditions at lower-performing sites.

Profit+ gives you that comparative view: a heat map by location, performance to savings target by site, and a shared idea library so the practices from the top plant can be read across to the rest.

An aerospace components manufacturer we work with used this to close a significant performance gap between their best and worst sites over two years. If that kind of plant-to-plant visibility is missing at {company}, I think it is worth a brief conversation.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Second follow-up to operations. Introduces heat map and read-across together. Strong combination for VP Operations managing multiple plants.",
  },
  {
    id: "ef-ops-003",
    channel: "email_followup",
    persona: "operations",
    title: "Operations follow-up — lean results and financial verification",
    subject: "Are your lean results showing up in the P&L at {company}?",
    body: `Hi {first_name},

One follow-up thought: lean and kaizen programs are excellent at generating improvement activity. The gap that most operations leaders eventually run into is showing the CFO that the activity produced verifiable financial results.

Activity reports and kaizen completion counts are not enough for Finance. The question is always: where is it in the P&L?

Profit+ connects the operational execution of lean events to G/L-verified financial results by routing every idea through a lifecycle that ends with Finance verifying the savings against the actual G/L. Operations does the work. Finance confirms the result.

I have seen this work at lean teams in automotive supply, medical device manufacturing, and industrial equipment. If you are running a lean program at {company} and need to make the financial case more convincingly, happy to discuss.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Best for VP Lean or Director of CI. The lean-to-finance credibility gap is a known frustration in operations across all manufacturing sectors.",
  },
  {
    id: "ef-ops-004",
    channel: "email_followup",
    persona: "operations",
    title: "Operations follow-up — breakup",
    subject: "Closing the loop, {first_name}",
    body: `Hi {first_name},

I have sent a few notes without hearing back, so I will leave it here unless the timing changes.

If cost-savings discipline — getting ideas off the whiteboard, through implementation, and verified in the P&L — becomes a live priority at {company}, I am easy to find.

The short version: Profit+ gives operations and procurement a single platform to capture, track, and verify every cost-saving initiative from shop floor to G/L. Enterprise discipline without enterprise complexity.

Take care.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Day 14 breakup. Uses the tagline cleanly. Neutral and respectful.",
  },
  {
    id: "ef-ops-005",
    channel: "email_followup",
    persona: "operations",
    title: "Operations follow-up — mobile idea capture for shift workers",
    subject: "One thing I did not mention about shop-floor idea capture",
    body: `Hi {first_name},

One detail I did not cover in my first note: at plants where most employees are on shift and do not have desk access, getting ideas into a system has always been a friction point.

Profit+ has a mobile-ready native app and QR codes that can be placed at any workstation. Any operator can scan the code and submit an idea in under two minutes from their phone, without needing to log in to a computer.

The ideas flow directly into the platform, get routed to the appropriate reviewer, and the submitter receives a status update at each stage.

This works the same way on an automotive assembly line, an electronics SMT floor, or a building-materials plant with mixed-shift teams. If frictionless capture is something that would make a difference at {company}, happy to show you how it works.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Second follow-up for operations. Mobile/QR angle is practical and differentiating for shift-heavy environments across all manufacturing verticals.",
  },
  {
    id: "ef-ops-006",
    channel: "email_followup",
    persona: "operations",
    title: "Procurement follow-up — closing the validated-to-executed gap",
    subject: "Following up on savings execution at {company}",
    body: `Hi {first_name},

Following up on my last note. A quick framing that tends to land with procurement leaders: the validated-to-executed gap is not a sourcing problem — it is a discipline problem.

The sourcing team does the hard work of identifying the saving and getting it validated. The gap happens during rollout, when the implementation lives in a spreadsheet that nobody updates from week to week and the approved saving sits on a shelf between approval and G/L entry.

Profit+ is the operating system that closes that gap — every approved initiative has a named owner, a stage, a target date, and a Finance verification step. Nothing gets credited until it is real.

This plays out the same way whether you are managing indirect spend at a heavy-equipment OEM or MRO consolidation at a contract manufacturer. If that is the gap at {company}, worth fifteen minutes.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Second follow-up for procurement. Reframes the problem as a discipline issue, not a sourcing issue — less threatening and more actionable.",
  },
  {
    id: "ef-ops-007",
    channel: "email_followup",
    persona: "operations",
    title: "Procurement follow-up — CFO visibility into savings programs",
    subject: "Does your CFO see your savings number in real time, {first_name}?",
    body: `Hi {first_name},

One follow-up question: when your CFO reviews cost savings, are they looking at a real-time view of what procurement is executing — or a monthly report assembled after the fact?

If it is monthly, there is a window of time each month where the CFO has less information than the team running the program. That creates a credibility gap when the numbers do not land as expected.

Profit+ gives the CFO the same real-time view of savings execution that procurement has — gap to goal, initiatives in-flight versus stalled, and realized savings verified against the G/L.

This credibility gap exists at procurement teams across the manufacturing spectrum, from IoT hardware to aerospace to industrial equipment. If shortening that visibility lag is a priority at {company}, happy to discuss.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Second follow-up for procurement. Introduces the CFO visibility angle from procurement's perspective.",
  },
  {
    id: "ef-ops-008",
    channel: "email_followup",
    persona: "operations",
    title: "Procurement follow-up — category-specific stall",
    subject: "One category, one gap — a question for you, {first_name}",
    body: `Hi {first_name},

A direct follow-up question: which category in your savings program is most stalled right now — indirect spend, MRO, freight, packaging, or something else — and do you know why it is stalled?

Most procurement leaders I ask have a quick answer. The stall is usually not a mystery — it is a rollout problem, an ownership gap, or a plant that has not prioritized the implementation.

Profit+ makes those stalls visible and addressable: every initiative has a named owner, a current stage, and a deadline. When something stalls, it appears on the dashboard before it becomes a missed target.

Whether the category is packaging at a building-materials company or MRO at a contract electronics manufacturer, the stall pattern is the same. If you have a category that fits at {company}, I would welcome fifteen minutes to walk you through how similar teams have addressed it.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Third follow-up for procurement. Asks a direct question about a specific stalled category — goal is to get a reply that opens a live conversation.",
  },
  {
    id: "ef-ops-009",
    channel: "email_followup",
    persona: "operations",
    title: "Operations follow-up — supply base as idea source",
    subject: "Are your suppliers part of the cost savings program at {company}?",
    body: `Hi {first_name},

A follow-up angle I did not mention earlier: some of the most valuable cost-saving ideas at manufacturers come from the supply base — suppliers who see inefficiencies on the production floor that the plant team has stopped noticing.

The challenge is that most cost programs have no formal channel for capturing supplier-generated ideas. They come in through relationship conversations, get noted in email threads, and rarely make it into a structured evaluation and implementation process.

Profit+ includes a supplier ecosystem channel — suppliers can submit ideas directly into the platform through the same entry point used by employees, and those ideas move through the same evaluation and approval workflow.

A medical device manufacturer with six production sites we work with opened this to their supply base and found it surfaced ideas in sterilization packaging and component handling that their internal teams had not identified. If engaging your supply base as a source of ideas is something {company} has not yet structured, it is worth a brief conversation.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Later follow-up for both operations and procurement personas. The supply base angle differentiates Profit+ from internal-only tracking tools.",
  },
  {
    id: "ef-ops-010",
    channel: "email_followup",
    persona: "operations",
    title: "Operations follow-up — event-triggered expansion",
    subject: "Noticed the expansion at {company} — a relevant thought",
    body: `Hi {first_name},

Saw that {company} has been expanding its footprint. When manufacturing capacity grows, cost optimization programs typically face a harder version of the same challenge: more plants to coordinate, more ideas to track, more opportunities for savings to slip through the cracks.

Profit+ scales with the organization — multi-site, multi-currency, multi-lingual — and the Read-Across feature means that proven savings ideas from existing plants can be cloned and adapted for new locations rather than starting from scratch.

An IoT hardware manufacturer we work with used this during a rapid production scale-up to bring new facilities up to the savings-discipline level of their original plants within the first quarter of operation.

If cost discipline during a growth phase is a live question at {company}, I think fifteen minutes is worth your time.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Use when there is a public signal about expansion, new plant openings, or acquisition activity. Positions Profit+ as a scaling solution.",
  },
  // ─── HR ──────────────────────────────────────────────
  {
    id: "ef-hr-001",
    channel: "email_followup",
    persona: "hr",
    title: "HR follow-up — the feedback loop that sustains programs",
    subject: "The one thing that makes employee idea programs sustainable",
    body: `Hi {first_name},

Following up with one thought I did not include in my first note. Employee idea programs fail for one reason more than any other: the feedback loop breaks down. Ideas get submitted, nothing visibly happens, and employees stop contributing.

Profit+ has an automated feedback loop built in at every stage of the idea lifecycle. The submitter receives a notification each time their idea advances — or when it is not moving forward, along with a reason. That transparency is what makes the difference between a one-year engagement spike and a multi-year culture shift.

This holds at a single-plant automotive supplier as much as at a global electronics manufacturer with workers across time zones. If rebuilding trust in the idea-contribution process is a priority at {company}, worth a brief conversation.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Day 4-7 follow-up for HR. Introduces the feedback loop as the mechanism for sustainable engagement. Short and focused.",
  },
  {
    id: "ef-hr-002",
    channel: "email_followup",
    persona: "hr",
    title: "HR follow-up — engagement metrics for the CFO",
    subject: "Measuring employee engagement impact at {company} — a different frame",
    body: `Hi {first_name},

One follow-up thought: when HR brings employee engagement results to the CFO, the conversation often stalls at "what is the ROI?" Because engagement scores do not have a dollar value, the answer is always qualitative.

Profit+ creates a direct, measurable connection: when employee-submitted ideas get implemented, the realized savings are tracked in the same platform as the contribution. HR can show the CFO: percentage of employees contributing, ideas implemented per quarter, realized savings from employee-originated ideas.

That turns a qualitative engagement story into a quantitative business case — whether you are at a building-materials manufacturer or a precision-parts medical device company.

If making that connection is something you are trying to build at {company}, I would welcome fifteen minutes.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Second follow-up for HR. The CFO-ROI angle gives HR a compelling internal case for the platform.",
  },
  {
    id: "ef-hr-003",
    channel: "email_followup",
    persona: "hr",
    title: "HR follow-up — QR codes for the frontline",
    subject: "Reaching the employees who do not sit at a desk, {first_name}",
    body: `Hi {first_name},

A practical follow-up: in manufacturing, the employees with the most direct knowledge of where savings opportunities exist are often the ones with the least convenient access to systems. They are on the floor, on shift, without a computer nearby.

Profit+ solves this with QR-code idea entry and a mobile-ready native app. Any employee can scan a QR code at their workstation and submit a cost-saving idea from their phone in under two minutes.

The result is that frontline employees are just as accessible as office employees for idea contributions — whether they are assembling aerospace components, running a stamping line, or monitoring a continuous production process at a building-materials plant.

If reaching the frontline workforce is a practical challenge at {company}, I think this is worth seeing.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Day 4-7 follow-up for HR. The frontline access angle is practical and differentiating. Best for companies with large shift populations.",
  },
  {
    id: "ef-hr-004",
    channel: "email_followup",
    persona: "hr",
    title: "HR follow-up — structured incentive program",
    subject: "Building a fair incentive program for cost savings at {company}",
    body: `Hi {first_name},

Following up with a specific angle: one challenge with employee incentive programs tied to cost savings is fairness. When recognition is manager-discretionary, the same contribution can be treated differently by different managers — which erodes trust in the program.

Because Profit+ tracks every idea and its financial outcome in a single platform, the data for a consistent, transparent incentive calculation is already in the system. HR defines the rules, and the platform provides the objective data to apply them.

That removes the discretion problem and makes the incentive program scalable across plants without losing fairness — whether you are managing twenty people at one facility or thousands across a global contract manufacturing network.

If building that kind of structured, data-driven recognition program is something you are working on at {company}, I would value fifteen minutes.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Second follow-up for HR. Works well when you know the company is building or revamping an incentive or recognition program.",
  },
  {
    id: "ef-hr-005",
    channel: "email_followup",
    persona: "hr",
    title: "HR follow-up — breakup",
    subject: "Closing the loop, {first_name}",
    body: `Hi {first_name},

I have reached out a few times without hearing back, so I will leave it here.

If employee engagement tied to measurable cost outcomes — with a feedback loop for every contributor — becomes a live priority at {company}, I am easy to find.

The short version: Profit+ gives every employee a structured channel to contribute cost-saving ideas, with a feedback loop at every stage, and connects those contributions to verified financial results. Everyone plays a role.

Take care.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Day 14 HR breakup. Uses 'everyone plays a role' as the closing line. Neutral and respectful.",
  },
  {
    id: "ef-hr-006",
    channel: "email_followup",
    persona: "hr",
    title: "HR follow-up — culture of CI mechanism",
    subject: "Making continuous improvement part of the culture — one practical thought",
    body: `Hi {first_name},

One follow-up. When companies say they want to build a culture of continuous improvement, the practical question is: what is the mechanism? Leadership communication is necessary but not sufficient. There needs to be a system that makes contribution visible, consistent, and rewarding.

Profit+ is that mechanism — a platform where every employee has a channel to contribute, every contribution gets a response, and the aggregate of contributions shows up as a measurable improvement in cost performance.

That combination — easy contribution, visible feedback, measurable impact — is what builds the habit over time rather than producing a one-quarter spike. It works the same way at a single aerospace plant as at a heavy-equipment OEM running operations across multiple countries.

If you are building that program at {company} right now, I would welcome a conversation about what we have seen work elsewhere.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Third follow-up for HR. Returns to the culture angle with a more practical framing.",
  },
  {
    id: "ef-hr-007",
    channel: "email_followup",
    persona: "hr",
    title: "HR follow-up — global engagement consistency",
    subject: "Running employee engagement consistently across {site_count} — a practical question",
    body: `Hi {first_name},

A practical follow-up for multi-site organizations: running employee engagement programs consistently across plants with different languages, cultures, and management styles is one of the harder challenges in manufacturing HR.

Profit+ supports multiple languages in the idea submission and review workflow, is mobile-ready for plant-floor employees across every site, and has a standardized lifecycle so that the engagement experience is consistent regardless of location.

The engagement metrics — participation rate, ideas submitted, ideas implemented, feedback loop completion — roll up to one dashboard that HR reviews for the whole organization.

Whether your network spans a few domestic plants or production sites across Europe and Asia, the consistency challenge is the same. If it is the challenge at {company}, I think it is worth a brief conversation.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{site_count}", "{rep_signature}"],
    notes: "Second follow-up for global HR. Use when you know the company is multi-national or has language diversity in the workforce.",
  },
  {
    id: "ef-hr-008",
    channel: "email_followup",
    persona: "hr",
    title: "HR follow-up — HR as strategic contributor to cost outcomes",
    subject: "HR's seat at the cost-improvement table at {company}",
    body: `Hi {first_name},

One last thought on this: HR typically enters cost improvement conversations as a supporting function — helping with change management, communication, and culture. The strategic contributor role — where HR is cited as a driver of measurable cost outcomes — is harder to claim without the data.

Profit+ gives HR the data to make that claim. When employee-contributed ideas are tracked to realized savings, HR can show the CFO a number: this percentage of total savings came from employee-generated ideas, these are the departments contributing most, and here is what the engagement metric looked like during the same period.

That is a different kind of seat at the table — one that holds in a board-level cost conversation at an industrial equipment OEM as much as in a quarterly review at a contract manufacturer.

If that is a direction {company}'s HR team is working toward, I would welcome fifteen minutes.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Later-stage follow-up. Positions HR as a strategic contributor to financial outcomes — aspirational framing that resonates with ambitious CHROs.",
  },
  {
    id: "ef-hr-009",
    channel: "email_followup",
    persona: "hr",
    title: "HR follow-up — HR cost intelligence from frontline",
    subject: "Employee ideas about workforce costs — does {company} capture them?",
    body: `Hi {first_name},

A follow-up with a specific angle: some of the most actionable cost-saving ideas in HR come from employees themselves — observations about onboarding inefficiencies, redundant training requirements, scheduling improvements, or safety protocols that generate unnecessary rework.

Those ideas rarely surface in formal processes. They come up in casual conversations or exit interviews, and by then it is too late to act on them.

Profit+ gives employees a structured channel to submit those observations as cost-saving ideas — with a feedback loop so they know if their idea is being considered. HR teams have used this to surface meaningful savings in workforce-related costs that would not have been identified top-down.

This is relevant across manufacturing verticals — at a medical device manufacturer managing complex training compliance as much as at a building-materials company managing shift scheduling. If that kind of bottom-up HR cost intelligence is relevant at {company}, happy to discuss.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Use for HR leaders who own workforce cost optimization as well as engagement. Connects the employee voice angle to a specific HR-owned cost category.",
  },
  {
    id: "ef-hr-010",
    channel: "email_followup",
    persona: "hr",
    title: "HR follow-up — event-triggered new leadership",
    subject: "Following up given the recent news at {company}",
    body: `Hi {first_name},

I noticed the recent change at {company} and wanted to follow up. New HR leadership often comes with a mandate to demonstrate that the people function is contributing to business performance in a measurable way.

Profit+ has been used by HR leaders at manufacturers — industrial equipment, aerospace, contract electronics, and others — to build exactly that connection: a citizen idea channel with automated feedback, mobile and QR-code access for frontline employees, and engagement metrics that tie directly to realized cost savings.

If connecting culture and engagement to financial outcomes is part of the mandate right now, I would welcome fifteen minutes to share what we have seen work in comparable organizations.

{rep_signature}`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Event-triggered follow-up for HR. Use when there is a recent CHRO appointment, reorg, or public culture initiative. Reference the trigger specifically.",
  },
  // ─── LINKEDIN DM (30: 10 Finance, 10 Operations, 10 HR) ─────────────────
  // ─── FINANCE ──────────────────────────────────────────────
  {
    id: "ld-fin-001",
    channel: "linkedin_dm",
    persona: "finance",
    title: "Finance LinkedIn — validated vs executed gap",
    body: `{first_name}, I work with CFOs in {industry} who carry a gap between validated and executed savings — approved initiatives that never quite made it to the G/L. Curious whether that is a live problem at {company} or whether you have a process that closes it consistently. Happy to compare notes if it is relevant.`,
    tokens: ["{first_name}", "{company}", "{industry}"],
    notes: "Connection request or first DM. Keep under 300 characters for connection note. Validated-vs-executed framing is the strongest opening for finance personas.",
  },
  {
    id: "ld-fin-002",
    channel: "linkedin_dm",
    persona: "finance",
    title: "Finance LinkedIn — P&L verification",
    body: `{first_name}, one question I ask CFOs in manufacturing: when Operations marks a cost initiative complete, does Finance verify the savings in the G/L independently, or does the number get self-reported? The gap between those two answers is usually worth a conversation. Happy to discuss if it is timely at {company}.`,
    tokens: ["{first_name}", "{company}"],
    notes: "Works well as a connection request note or first-touch DM. Direct question format gets replies from financially-minded executives.",
  },
  {
    id: "ld-fin-003",
    channel: "linkedin_dm",
    persona: "finance",
    title: "Finance LinkedIn — real-time visibility",
    body: `{first_name}, came across your profile while looking at how finance leaders in {industry} are getting real-time visibility into cost savings programs — not just at quarter-end but week to week. Curious how {company} handles that, and whether fifteen minutes to compare notes would be useful.`,
    tokens: ["{first_name}", "{company}", "{industry}"],
    notes: "Good for a first DM after connecting. The real-time framing creates a clear gap without being aggressive.",
  },
  {
    id: "ld-fin-004",
    channel: "linkedin_dm",
    persona: "finance",
    title: "Finance LinkedIn — EBITDA and execution discipline",
    body: `{first_name}, I work with finance leaders in manufacturing on closing the gap between cost-optimization intent and EBITDA delivery. Most of the gap is not a sourcing problem — it is an execution discipline problem. If that pattern is familiar at {company}, I would value fifteen minutes.`,
    tokens: ["{first_name}", "{company}"],
    notes: "Use for VP Finance or CFO at companies that have publicly discussed EBITDA improvement. Keep the EBITDA reference brief — hook, not feature.",
  },
  {
    id: "ld-fin-005",
    channel: "linkedin_dm",
    persona: "finance",
    title: "Finance LinkedIn — multi-site consolidated view",
    body: `{first_name}, I work with CFOs at multi-site manufacturers — automotive, industrial, medical device, aerospace — who are trying to get a single consolidated view of cost savings execution across plants without waiting for plant controllers to submit monthly. If that is a challenge at {company}, I would welcome a brief comparison of notes.`,
    tokens: ["{first_name}", "{company}"],
    notes: "Best for CFOs at companies with five or more plants. The multi-site consolidation pain is universally recognized.",
  },
  {
    id: "ld-fin-006",
    channel: "linkedin_dm",
    persona: "finance",
    title: "Finance LinkedIn — idea ranking and capital prioritization",
    body: `{first_name}, curious how {company} prioritizes which cost-saving initiatives get capital each year — whether it is a structured ranking based on maturity and expected return, or a judgment call in a budget meeting. I work with CFOs who are building more systematic approaches to this across manufacturing sectors. Happy to compare notes if it is relevant.`,
    tokens: ["{first_name}", "{company}"],
    notes: "Good pre-budget season DM. The structured vs judgment-call framing creates a clear gap without criticizing the prospect's process.",
  },
  {
    id: "ld-fin-007",
    channel: "linkedin_dm",
    persona: "finance",
    title: "Finance LinkedIn — reported vs verified savings",
    body: `{first_name}, I work with finance leaders in {industry} on a specific problem: how to move from a cost-savings number that is reported to one that is verified against the G/L. The difference usually shows up in operating margin over a two-to-three-year horizon. If that distinction is live at {company}, I would value a brief conversation.`,
    tokens: ["{first_name}", "{company}", "{industry}"],
    notes: "Works well as a follow-up DM after connecting. The reported-vs-verified distinction is thought-provoking for finance leaders across all manufacturing sectors.",
  },
  {
    id: "ld-fin-008",
    channel: "linkedin_dm",
    persona: "finance",
    title: "Finance LinkedIn — cross-functional accountability",
    body: `{first_name}, one pattern I see across manufacturing: cost-saving initiatives get approved by Finance but accountability for execution lives in Operations, Procurement, and HR — and Finance finds out about the slip at month-end close. I work with CFOs who are building shared accountability systems to close that lag. If it is relevant at {company}, happy to connect.`,
    tokens: ["{first_name}", "{company}"],
    notes: "Cross-functional accountability angle. Works well for CFOs who have mentioned execution discipline publicly.",
  },
  {
    id: "ld-fin-009",
    channel: "linkedin_dm",
    persona: "finance",
    title: "Finance LinkedIn — ERP integration",
    body: `{first_name}, I work with finance leaders who are trying to connect their cost-optimization tracking to their actual financial system — so the savings number in the tracking tool and the number in the ERP are the same figure, not two different estimates. We work with manufacturers across industrial, aerospace, and medical device production. If that gap exists at {company}, I would welcome fifteen minutes.`,
    tokens: ["{first_name}", "{company}"],
    notes: "Technical/integration angle. Best for Controllers or CFOs thinking about ERP integration for cost tracking.",
  },
  {
    id: "ld-fin-010",
    channel: "linkedin_dm",
    persona: "finance",
    title: "Finance LinkedIn — defensible board number",
    body: `{first_name}, when your CFO presents cost savings to the board, is the number verified against the G/L or self-reported by the executing teams? I work with finance leaders at manufacturers — heavy equipment, contract manufacturing, aerospace, and others — who are building the infrastructure to make that number defensible. If it is a relevant conversation at {company}, happy to connect.`,
    tokens: ["{first_name}", "{company}"],
    notes: "Board-presentation angle. Best when you have a signal of an upcoming board review or investor day.",
  },
  // ─── OPERATIONS ──────────────────────────────────────────────
  {
    id: "ld-ops-001",
    channel: "linkedin_dm",
    persona: "operations",
    title: "Operations LinkedIn — stalled programs",
    body: `{first_name}, I work with operations leaders at manufacturers — industrial equipment, aerospace, contract electronics — whose cost programs stall at the rollout step: ideas approved, spreadsheets built, and then six months later the saving is still sitting between sign-off and execution. If that pattern is familiar at {company}, I would value fifteen minutes. No pitch.`,
    tokens: ["{first_name}", "{company}"],
    notes: "First operations DM. The stalled program framing is direct and specific — recognized immediately across all manufacturing verticals.",
  },
  {
    id: "ld-ops-002",
    channel: "linkedin_dm",
    persona: "operations",
    title: "Operations LinkedIn — read-across between plants",
    body: `{first_name}, came across your name while looking at how manufacturing operations leaders are running read-across between plants — sharing what works at one site so others do not reinvent the wheel. Curious how {company} handles that. Fifteen minutes to compare notes if it is useful.`,
    tokens: ["{first_name}", "{company}"],
    notes: "Read-across angle. Best for multi-plant VP Operations or Director of Manufacturing Excellence across all manufacturing sectors.",
  },
  {
    id: "ld-ops-003",
    channel: "linkedin_dm",
    persona: "operations",
    title: "Operations LinkedIn — lean savings without financial proof",
    body: `{first_name}, I work with lean and CI leaders in {industry} who are doing real work but struggling to show the CFO that the results are in the P&L. If that tension exists at {company}, I would value a brief conversation about how other operations teams have closed it.`,
    tokens: ["{first_name}", "{company}", "{industry}"],
    notes: "For VP Lean or Director of CI. The lean-activity-vs-financial-results tension is a known frustration across all manufacturing verticals.",
  },
  {
    id: "ld-ops-004",
    channel: "linkedin_dm",
    persona: "operations",
    title: "Operations LinkedIn — shop-floor idea capture",
    body: `{first_name}, one question I ask plant operations leaders: what percentage of cost-saving ideas that make it into your program come from the production floor versus from management? At most manufacturers the ratio is lower than it should be. If that is the case at {company}, I would welcome a brief conversation.`,
    tokens: ["{first_name}", "{company}"],
    notes: "Shop-floor capture angle. Works well as a first DM to Plant Managers or VP Operations across all manufacturing verticals.",
  },
  {
    id: "ld-ops-005",
    channel: "linkedin_dm",
    persona: "operations",
    title: "Operations LinkedIn — plant performance benchmarking",
    body: `{first_name}, I work with operations leaders who are building an objective, data-driven view of which plants are leading on cost savings and why — so they can replicate the conditions rather than just identify the gap. If that is a live question at {company}, happy to compare notes.`,
    tokens: ["{first_name}", "{company}"],
    notes: "Performance benchmarking angle. Works for COO or VP Operations at multi-plant manufacturers. Connects to heat map and read-across features.",
  },
  {
    id: "ld-ops-006",
    channel: "linkedin_dm",
    persona: "operations",
    title: "Procurement LinkedIn — savings-quota gap",
    body: `{first_name}, I work with procurement leaders in {industry} who carry a savings number and are trying to close the gap between validated and executed — initiatives that cleared approval but stalled at rollout. Curious how {company} manages that gap. Fifteen minutes if it is relevant.`,
    tokens: ["{first_name}", "{company}", "{industry}"],
    notes: "First procurement DM. Validated-vs-executed framing is the strongest opening for procurement. Keep under 300 characters for connection note.",
  },
  {
    id: "ld-ops-007",
    channel: "linkedin_dm",
    persona: "operations",
    title: "Procurement LinkedIn — piece-price tracking",
    body: `{first_name}, one question for procurement leaders in manufacturing: when a piece-price reduction is agreed with a supplier, how do you track the savings from commitment to actual G/L impact across plants? At most companies it is a manual reconciliation. If there is a better approach worth discussing at {company}, I am happy to connect.`,
    tokens: ["{first_name}", "{company}"],
    notes: "Piece-price tracking angle. Specific and credible for procurement in automotive, aerospace, medical device, and industrial manufacturing.",
  },
  {
    id: "ld-ops-008",
    channel: "linkedin_dm",
    persona: "operations",
    title: "Procurement LinkedIn — supplier ecosystem",
    body: `{first_name}, I work with CPOs and sourcing leaders who are structuring their supply base as an active source of cost-saving ideas — not just as cost-reduction targets. We see this across automotive, industrial equipment, and aerospace supply chains. If that is a direction {company}'s procurement program is heading, I would value fifteen minutes.`,
    tokens: ["{first_name}", "{company}"],
    notes: "Supplier ecosystem angle for CPO or Director Sourcing. Positions the supply base as a value-generating partner.",
  },
  {
    id: "ld-ops-009",
    channel: "linkedin_dm",
    persona: "operations",
    title: "Procurement LinkedIn — CFO visibility into savings",
    body: `{first_name}, a question I ask procurement leaders: when your CFO reviews savings execution, are they looking at the same data you are in real time — or does your number arrive at Finance through a monthly consolidation? The lag between those two versions is usually where credibility gaps form. Happy to discuss if it is relevant at {company}.`,
    tokens: ["{first_name}", "{company}"],
    notes: "CFO-visibility angle from procurement's perspective. Works well as a second DM after connecting.",
  },
  {
    id: "ld-ops-010",
    channel: "linkedin_dm",
    persona: "operations",
    title: "Operations LinkedIn — energy and asset optimization",
    body: `{first_name}, I work with operations leaders in {industry} on capturing and tracking cost-saving ideas in energy efficiency, preventive maintenance, and asset optimization — categories where the ideas exist but the discipline to track them from concept to verified savings is often missing. If that is relevant at {company}, I would welcome a brief conversation.`,
    tokens: ["{first_name}", "{company}", "{industry}"],
    notes: "Energy and asset angle. Works for Plant Managers and VP Operations at energy-intensive manufacturers across industrial, heavy-equipment, and building-materials sectors.",
  },
  // ─── HR ──────────────────────────────────────────────
  {
    id: "ld-hr-001",
    channel: "linkedin_dm",
    persona: "hr",
    title: "HR LinkedIn — citizen ideas and feedback loop",
    body: `{first_name}, I work with HR leaders in manufacturing who are building genuine employee idea programs — not suggestion boxes, but structured channels where submissions get a visible response at every stage. If building that feedback loop at {company} is something you are working on, happy to compare notes.`,
    tokens: ["{first_name}", "{company}"],
    notes: "First HR DM. Leads with the feedback loop distinction. Avoids overselling the employee channel as the primary savings driver.",
  },
  {
    id: "ld-hr-002",
    channel: "linkedin_dm",
    persona: "hr",
    title: "HR LinkedIn — culture of continuous improvement",
    body: `{first_name}, came across your profile while looking at how HR leaders in manufacturing are building cultures of continuous improvement that stick beyond the first year. Curious what {company} is doing on that front and whether there is a conversation worth having.`,
    tokens: ["{first_name}", "{company}"],
    notes: "Culture angle. Open-ended and non-threatening. Works well as a connection request note.",
  },
  {
    id: "ld-hr-003",
    channel: "linkedin_dm",
    persona: "hr",
    title: "HR LinkedIn — engagement tied to cost outcomes",
    body: `{first_name}, one question I ask HR leaders in manufacturing: can you show the CFO a number that connects employee engagement to cost outcomes — not a survey score, but verified savings from employee-contributed ideas? If building that connection is a goal at {company}, I would welcome fifteen minutes.`,
    tokens: ["{first_name}", "{company}"],
    notes: "CFO-ROI angle. Works well for CHROs and VP HR under pressure to demonstrate the business impact of engagement programs.",
  },
  {
    id: "ld-hr-004",
    channel: "linkedin_dm",
    persona: "hr",
    title: "HR LinkedIn — QR code and mobile for frontline",
    body: `{first_name}, I work with HR leaders at manufacturers on a practical challenge: how do you give production-floor employees — whether on an assembly line, in a clean room, or at a heavy-equipment plant — a structured way to contribute improvement ideas when most do not have desk access? Mobile and QR-code entry has made a meaningful difference. Happy to share what we have seen if it is relevant at {company}.`,
    tokens: ["{first_name}", "{company}"],
    notes: "Practical frontline-access angle. Works for HR leaders at shift-heavy manufacturing environments across all verticals.",
  },
  {
    id: "ld-hr-005",
    channel: "linkedin_dm",
    persona: "hr",
    title: "HR LinkedIn — employee-based incentive programs",
    body: `{first_name}, I work with HR leaders in manufacturing who are building structured incentive programs tied to cost-saving contributions — where the recognition is consistent, transparent, and data-driven rather than manager-discretionary. If that is a direction {company} is moving, I would value a brief conversation.`,
    tokens: ["{first_name}", "{company}"],
    notes: "Incentive program angle. Best for HR leaders at companies revamping recognition and rewards programs.",
  },
  {
    id: "ld-hr-006",
    channel: "linkedin_dm",
    persona: "hr",
    title: "HR LinkedIn — global multi-site engagement",
    body: `{first_name}, running a consistent employee engagement program across multiple plants with different languages and cultures is one of the harder challenges in manufacturing HR — whether you are in automotive, electronics, or industrial equipment. I work with HR leaders solving it with multi-lingual, mobile-ready idea channels. If consistency at scale is a live challenge at {company}, happy to compare notes.`,
    tokens: ["{first_name}", "{company}"],
    notes: "Global/multi-site angle. Best for global HR leaders or VP HR at companies with international plant footprints.",
  },
  {
    id: "ld-hr-007",
    channel: "linkedin_dm",
    persona: "hr",
    title: "HR LinkedIn — shop floor to boardroom",
    body: `{first_name}, your team is one of the few I have seen working to connect production-floor engagement to boardroom cost outcomes. If a program that gives every employee a structured voice — with a feedback loop and financial verification at the end — would be useful context, I would be happy to share what we have seen across manufacturing organizations.`,
    tokens: ["{first_name}", "{company}"],
    notes: "Use when you can reference something specific from the prospect's profile or public communications about employee engagement and cost culture.",
  },
  {
    id: "ld-hr-008",
    channel: "linkedin_dm",
    persona: "hr",
    title: "HR LinkedIn — HR as strategic contributor",
    body: `{first_name}, one conversation I keep having with HR leaders across manufacturing: how to shift HR from a supporting function in cost programs to a measured contributor to financial results. I have seen it work at industrial equipment OEMs, aerospace suppliers, and contract manufacturers. If that is a direction you are working toward at {company}, I would welcome fifteen minutes.`,
    tokens: ["{first_name}", "{company}"],
    notes: "Strategic positioning angle for CHRO or VP HR. Best for senior HR leaders actively working to demonstrate business impact.",
  },
  {
    id: "ld-hr-009",
    channel: "linkedin_dm",
    persona: "hr",
    title: "HR LinkedIn — everyone plays a role",
    body: `{first_name}, I work with HR leaders at manufacturers who are making cost optimization genuinely organization-wide — not just a leadership program, but a platform where every employee from the production floor to the boardroom has a visible role and receives feedback on their contribution. If that is a priority at {company}, happy to share what we have seen work.`,
    tokens: ["{first_name}", "{company}"],
    notes: "Everyone-plays-a-role framing. Strong cultural language that resonates with engagement-focused HR leaders across all manufacturing sectors.",
  },
  {
    id: "ld-hr-010",
    channel: "linkedin_dm",
    persona: "hr",
    title: "HR LinkedIn — OD and accountability infrastructure",
    body: `{first_name}, I work with organizational development leaders in manufacturing on the structural infrastructure that makes culture programs sustainable — the idea contribution channel, feedback loop, and accountability mechanism that give cost culture something concrete to hold on to beyond training and communication. Whether it is an IoT hardware company or a heavy-equipment OEM, the structural challenge is the same. If it is relevant at {company}, I would welcome a brief conversation.`,
    tokens: ["{first_name}", "{company}"],
    notes: "Best for Director of OD. Connects the structural/systems angle to the cultural outcome — a natural frame for OD practitioners.",
  },
  // ─── PHONE SCRIPT (30: 10 Finance, 10 Operations, 10 HR) ─────────────────
  // ─── FINANCE ──────────────────────────────────────────────
  {
    id: "ps-fin-001",
    channel: "phone_script",
    persona: "finance",
    title: "Finance phone — validated vs executed savings",
    body: `OPENER:
{first_name}, this is Demo BDR from Meridian. I am calling because I work with CFOs in {industry} who are carrying a gap between validated savings and savings that have actually flowed through the P&L — and I wanted to ask one direct question before I take any more of your time. Is that gap real at {company}?

[If yes or unknown:] Good to know. Can I ask: when a cost-saving initiative is approved, is there a system that tracks it through implementation and verifies the savings against the G/L before it gets counted — or does that verification happen manually at month-end?

DISCOVERY QUESTIONS:
1. What is {company}'s savings target for the year, and how much of that number is visible to Finance in real time versus at month-end close?
2. When savings miss the plan, how quickly does Finance find out — and what is the typical root cause?
3. How many plants or sites are contributing to the cost program, and does Finance have a consolidated view of execution across all of them?

VALUE BRIDGE:
Profit+ gives Finance real-time visibility into every cost-saving initiative — from idea generation through G/L-verified realization. The CFO sees the same gap-to-goal number that Operations and Procurement are managing against, updated in real time. No waiting for month-end. No manual consolidation.

CLOSE:
I am not asking you to evaluate anything today. Would fifteen minutes on the calendar this week be worth it to see how the dashboard works for a manufacturer in {industry}?`,
    tokens: ["{first_name}", "{company}", "{industry}"],
    notes: "Lead finance phone script. Opens with a direct question to qualify quickly. If they say 'no gap,' probe on verification method before accepting the answer.",
  },
  {
    id: "ps-fin-002",
    channel: "phone_script",
    persona: "finance",
    title: "Finance phone — P&L verification and defensible number",
    body: `OPENER:
{first_name}, Demo BDR from Meridian. Quick question — when a cost-saving initiative is completed at {company}, who verifies that the savings actually showed up in the G/L, and how do they verify it?

[Listen and probe on the verification process.]

DISCOVERY QUESTIONS:
1. Is Finance involved in the verification step, or does the number get self-reported by the team that ran the project?
2. If the board asked for a savings figure that was auditable against the G/L, could you produce that number today?
3. What is the typical gap between what gets reported as savings and what Finance can actually verify?

VALUE BRIDGE:
The reason I am asking is that every cost initiative tracked in Profit+ ends with a Finance-driven G/L verification step before the saving is counted. The result is a defensible savings number — one that Finance owns and can stand behind in a board presentation, not one it is handed from Operations. This matters as much at a contract electronics manufacturer as at an industrial equipment OEM.

CLOSE:
That distinction tends to matter most when savings targets are under scrutiny. If it is a live issue at {company}, fifteen minutes this week would be enough to see how the verification step works in practice. Is there a time that works?`,
    tokens: ["{first_name}", "{company}"],
    notes: "G/L verification angle. Best for Controllers and technically-oriented CFOs. The 'auditable against the G/L' question is a useful qualifier.",
  },
  {
    id: "ps-fin-003",
    channel: "phone_script",
    persona: "finance",
    title: "Finance phone — multi-site consolidated view",
    body: `OPENER:
{first_name}, Demo BDR from Meridian. I work with CFOs at multi-plant manufacturers on one specific challenge: getting a real-time, consolidated view of cost savings execution across all plants without waiting for month-end reports from plant controllers. Does that challenge sound familiar at {company}?

[Listen. If yes:] Can I ask a few quick questions to understand how you are handling it today?

DISCOVERY QUESTIONS:
1. How many plants or locations are in scope for the cost savings program?
2. How often does Finance get an updated view of each location's performance to target — weekly, monthly, quarterly?
3. When a plant is behind on its savings target, how quickly does Finance know, and who is accountable for the course correction?

VALUE BRIDGE:
Profit+ has a multi-tier target model where the corporate savings goal cascades to each plant automatically, and every plant's performance rolls back up to the CFO dashboard in real time. Finance sees which plants are leading, which are behind, and what the risk is to the full-year number — before the quarter closes. We see this at manufacturers across industrial, medical device, aerospace, and contract production.

CLOSE:
If getting ahead of that lag is something Finance is working on at {company}, I would like to show you what the dashboard looks like for a manufacturer with a similar footprint. Fifteen minutes is enough. Is there time this week?`,
    tokens: ["{first_name}", "{company}"],
    notes: "Multi-site angle. Best for CFOs or FP&A Directors managing large plant networks. The consolidated visibility pain is immediately recognizable.",
  },
  {
    id: "ps-fin-004",
    channel: "phone_script",
    persona: "finance",
    title: "Finance phone — leading vs lagging indicators",
    body: `OPENER:
{first_name}, Demo BDR from Meridian. Short question: at {company}, does Finance manage cost savings with leading indicators or lagging indicators — meaning, do you see where initiatives stand in real time, or do you find out how the quarter went when it closes?

[Listen. Probe if they say leading: 'What system gives you that real-time view?']

DISCOVERY QUESTIONS:
1. When an initiative is stalling in implementation, what is the earliest signal Finance receives?
2. How much of your cost program is visible to Finance during the quarter versus only at month-end close?
3. What would it change about how Finance runs the monthly business review if you had a real-time view of gap to goal?

VALUE BRIDGE:
Profit+ is built on a simple principle: the CFO should never learn about a cost-savings miss from a month-end report. The platform gives Finance a live dashboard — gap to goal, performance to budget, gains and losses, heat map by plant — updated continuously, not assembled manually each month.

CLOSE:
It is a fifteen-minute demo if you want to see what that looks like for a manufacturer in your space. Is there time in the next few days?`,
    tokens: ["{first_name}", "{company}"],
    notes: "Leading vs lagging framing. Works as a cold call opener. The question format creates immediate engagement by inviting the prospect to evaluate their own situation.",
  },
  {
    id: "ps-fin-005",
    channel: "phone_script",
    persona: "finance",
    title: "Finance phone — cross-functional accountability follow-up",
    body: `OPENER:
{first_name}, Demo BDR from Meridian following up on a note I sent last week. I will be brief — one question I did not ask in my email: at {company}, when a cost initiative is approved by Finance but stalls in execution, who does Finance call first — Operations, Procurement, or HR?

[Listen.] The reason I ask: at most manufacturers, the accountability for execution is diffuse enough that Finance ends up being the one who tracks down what happened, rather than having a platform where the stall is visible to everyone.

DISCOVERY QUESTIONS:
1. How do you currently track which cost initiatives are in-flight versus stalled across functions?
2. Is there a shared platform where Finance, Operations, and Procurement see the same view of initiative status?
3. When cross-functional initiatives miss their targets, how long does it typically take Finance to identify the root cause?

VALUE BRIDGE:
Profit+ is a shared platform for every function — Finance, Operations, Procurement, HR. Every initiative has a stage, an owner, and a target date. When something stalls, it is visible to everyone in real time — including the CFO — before it becomes a P&L problem.

CLOSE:
Worth fifteen minutes to see how it works for a multi-function manufacturer? I can work around your schedule.`,
    tokens: ["{first_name}", "{company}"],
    notes: "Follow-up call script. References the earlier email. The 'who does Finance call first' question is disarming and specific.",
  },
  {
    id: "ps-fin-006",
    channel: "phone_script",
    persona: "finance",
    title: "Finance phone — EBITDA and operating margin",
    body: `OPENER:
{first_name}, Demo BDR from Meridian. I work with CFOs in manufacturing on the gap between cost-savings commitments and EBITDA delivery — specifically, why organizations with good cost-savings programs often have a harder time showing it in the margin line than the savings number suggests. Is that a pattern you recognize at {company}?

[Listen.] Can I ask a couple of quick questions?

DISCOVERY QUESTIONS:
1. When you set a cost savings target for the year, what percentage would you say is typically verified in the G/L versus reported by the executing teams?
2. Has the CFO or board ever asked for a more rigorous verification of the savings number, and how did that conversation go?
3. What is the current tool or process for tracking initiatives from approval to G/L impact?

VALUE BRIDGE:
What we do is give Finance a platform where every cost-saving initiative — from any function, any plant, any category — is tracked through a standardized lifecycle ending with G/L verification by Finance. The savings figure Finance reports is not an estimate; it is verified against the actual G/L.

A Tier 1 automotive supplier we work with moved their operating income margin from 3% to over 7% over five years using this discipline. We have seen comparable results at aerospace and industrial equipment manufacturers. The platform was what made the savings trackable and defensible at board level.

CLOSE:
If delivering a defensible EBITDA improvement is on the agenda at {company}, fifteen minutes is a reasonable first step. Is there time this week?`,
    tokens: ["{first_name}", "{company}"],
    notes: "EBITDA angle. Use the anonymized proof points carefully — never name the source.",
  },
  {
    id: "ps-fin-007",
    channel: "phone_script",
    persona: "finance",
    title: "Finance phone — ERP integration",
    body: `OPENER:
{first_name}, Demo BDR from Meridian. Quick question: does the system {company} uses to track cost-saving initiatives connect directly to your financial system — so the savings number in the tracking tool and the number in the ERP are the same figure — or are they reconciled manually?

[Listen. Most will say manually or 'we do not have a system.']

DISCOVERY QUESTIONS:
1. What tool or process does Finance currently use to verify that savings actually hit the G/L?
2. How long does the reconciliation process take, and how often does it produce a gap between the reported number and the verified number?
3. If those two numbers were always aligned in real time, how would that change the monthly finance review?

VALUE BRIDGE:
Profit+ integrates with ERP and financial systems via API to pull actual cost data for G/L verification. The savings number in Profit+ is reconciled against the ERP continuously, not manually at month-end. Finance gets a number it can trust, not one it has to audit. We have implemented this alongside SAP, Oracle, and other platforms at manufacturers in medical device, industrial, and aerospace production.

CLOSE:
If eliminating that reconciliation gap is worth exploring at {company}, I can show you how the ERP integration works in fifteen minutes. Is there time this week?`,
    tokens: ["{first_name}", "{company}"],
    notes: "ERP integration angle. Best for technically-oriented CFOs or Controllers who have already invested in ERP.",
  },
  {
    id: "ps-fin-008",
    channel: "phone_script",
    persona: "finance",
    title: "Finance phone — budget season prioritization",
    body: `OPENER:
{first_name}, Demo BDR from Meridian. I am calling around budget season because one problem I see repeatedly across manufacturing: when Finance reviews cost-saving proposals, there is no objective way to rank them by expected return and readiness to execute — so the process ends up being more political than analytical. Does that sound familiar at {company}?

[Listen.] Can I ask a couple of quick questions?

DISCOVERY QUESTIONS:
1. How does {company} currently prioritize which cost-saving initiatives to fund in the annual budget?
2. Is there a scoring or ranking process, or does it primarily happen in budget meetings based on stakeholder advocacy?
3. What would it mean for Finance if every proposal came in pre-scored by maturity and expected return, so the budget conversation was about which high-confidence ideas to advance rather than which ideas to believe?

VALUE BRIDGE:
Profit+ scores and ranks every cost-saving idea by maturity and expected value before it reaches the budget approval stage. Finance gets an objective basis for prioritization — not a judgment call, but a ranked list built from consistent criteria applied across all functions and plants. We see this deliver the most value at manufacturers with large, diverse cost programs — from IoT hardware companies to heavy-equipment OEMs.

CLOSE:
If better prioritization is a goal for this budget cycle at {company}, fifteen minutes is worth your time. When works this week?`,
    tokens: ["{first_name}", "{company}"],
    notes: "Budget-season angle. Best timing is 60-90 days before the annual budget process.",
  },
  {
    id: "ps-fin-009",
    channel: "phone_script",
    persona: "finance",
    title: "Finance phone — plant heat map",
    body: `OPENER:
{first_name}, Demo BDR from Meridian. Short question: on a Monday morning, can Finance open a screen and see which of your plants are on track to their cost savings targets and which are at risk — or does that view have to be assembled manually from plant-level reports?

[Listen.] If it is assembled manually, how long does that take, and how current is the data when Finance finally sees it?

DISCOVERY QUESTIONS:
1. How many locations are in scope for the cost savings program?
2. When a plant is behind on its target, how does Finance find out — and what is the typical lag from when the problem starts to when Finance sees it?
3. If Finance had a real-time heat map of plant performance to savings target, how would that change the monthly business review?

VALUE BRIDGE:
Profit+ gives Finance a real-time heat map of cost savings performance by plant — updated continuously, not assembled from reports. The CFO can see in one view which plants are ahead, which are behind, which are at risk, and what initiatives are in-flight to close the gap. This is as useful at a building-materials manufacturer with nine sites as at a contract electronics company with a single large campus segmented by product line.

CLOSE:
I can show you what that looks like for a manufacturer with a similar footprint in fifteen minutes. Is there time this week?`,
    tokens: ["{first_name}", "{company}"],
    notes: "Heat map angle. Vivid and concrete — most CFOs can immediately picture the Monday morning scenario.",
  },
  {
    id: "ps-fin-010",
    channel: "phone_script",
    persona: "finance",
    title: "Finance phone — idea scoring and weighted value",
    body: `OPENER:
{first_name}, Demo BDR from Meridian. Quick question: when cost-saving ideas are submitted at {company} — whether from procurement, operations, or employees — is there a consistent way to score them by maturity and expected value, or does every idea go into a queue and get evaluated case by case?

[Listen.] The reason I ask is that without a consistent scoring methodology, the ideas that get funded tend to be the ones with the most internal sponsorship, not necessarily the ones with the best return.

DISCOVERY QUESTIONS:
1. How many ideas are typically in the pipeline at any given time, and how does Finance decide which ones to prioritize?
2. Is there a weighted scoring model that adjusts for implementation risk, site readiness, and expected financial return?
3. How much time does Finance spend evaluating and prioritizing ideas versus verifying that approved ideas were actually implemented?

VALUE BRIDGE:
Profit+ applies a weighted value scoring model to every idea — adjusted for maturity level, implementation confidence, and expected financial return. Finance spends less time evaluating proposals and more time on the high-confidence ideas that are ready to move. This has made a measurable difference at manufacturers running large, diverse idea pipelines — from medical device OEMs to industrial equipment companies.

CLOSE:
If better idea prioritization would change how Finance allocates resources at {company}, fifteen minutes would show you how the scoring model works. Is there a time this week?`,
    tokens: ["{first_name}", "{company}"],
    notes: "Idea scoring angle. Best for Controllers and FP&A Directors who own capital allocation.",
  },
  // ─── OPERATIONS ──────────────────────────────────────────────
  {
    id: "ps-ops-001",
    channel: "phone_script",
    persona: "operations",
    title: "Operations phone — stalled program cold call",
    body: `OPENER:
{first_name}, Demo BDR from Meridian. I will be direct — I am calling because I work with operations leaders in {industry} who have cost-savings programs that stall between approval and execution. I have one quick question: at {company}, what happens to a cost initiative after it gets approved — is there a system tracking it through implementation, or does it live in a spreadsheet that the team updates when they can?

[Listen.] That answer tells me a lot.

DISCOVERY QUESTIONS:
1. What is your most significant stalled cost program right now, and where is it stuck?
2. When you look across your plants, do you have a way to see which initiatives are on track and which have gone quiet?
3. When something stalls, who catches it first — you, Finance, or nobody until the quarter closes?

VALUE BRIDGE:
Profit+ is a platform that enforces execution discipline on every cost initiative. Each idea has a stage, an owner, and a target date. When something stalls, it is visible to everyone — including the CFO — while there is still time to course-correct. The lifecycle runs from Idea Generation through to Finance-verified Realization, with approval gates at each step.

CLOSE:
I am not asking you to evaluate anything today. If you have a stalled program that would be a good test case, fifteen minutes is enough to see whether Profit+ could help. Is there time this week?`,
    tokens: ["{first_name}", "{company}", "{industry}"],
    notes: "Lead operations phone script. The direct question about what happens after approval is an excellent qualifier across all manufacturing verticals.",
  },
  {
    id: "ps-ops-002",
    channel: "phone_script",
    persona: "operations",
    title: "Operations phone — read-across between plants",
    body: `OPENER:
{first_name}, Demo BDR from Meridian. Quick question: when one of your plants solves a problem — a cycle time improvement, an energy reduction, a scrap rate fix — is there a structured way for your other plants to learn about it and implement the same fix, or does that sharing happen informally?

[Listen.] The informal answer is the most common one. Most manufacturers are reinventing the wheel across plants.

DISCOVERY QUESTIONS:
1. How many plants do you manage, and are they running similar processes that could benefit from shared learnings?
2. Is there a shared idea library where proven improvements from one site are visible to others?
3. When you think about the savings potential sitting in your existing knowledge base — improvements proven at one plant but not yet deployed elsewhere — what is your rough estimate?

VALUE BRIDGE:
Profit+ has a Read-Across feature that lets any plant search the shared idea library across the entire organization, find a proven improvement from another site, and clone it for local implementation. The savings from Site A travel to Sites B, C, and D. A contract electronics manufacturer we work with used this to replicate a materials-handling improvement across eleven lines in eight weeks.

CLOSE:
If ideas are not flowing between your plants at {company}, I think it is worth fifteen minutes to see how the Read-Across feature works in practice. When does your schedule open up this week?`,
    tokens: ["{first_name}", "{company}"],
    notes: "Read-across angle. Works as a cold call or follow-up for multi-plant VP Operations across all manufacturing sectors.",
  },
  {
    id: "ps-ops-003",
    channel: "phone_script",
    persona: "operations",
    title: "Operations phone — lean program financial verification",
    body: `OPENER:
{first_name}, Demo BDR from Meridian. I work with lean and continuous improvement leaders on a specific challenge: how do you show the CFO that lean events and kaizen activity are producing verifiable financial results — not just activity metrics? Has that conversation come up at {company}?

[Listen.] Can I ask a couple of quick questions?

DISCOVERY QUESTIONS:
1. How does {company} currently track the financial impact of lean events and CI initiatives?
2. When Finance reviews the lean program results, are the savings verified against the G/L, or are they reported by the CI team?
3. What would it change if every lean improvement was tracked through to G/L verification, and Finance could see the financial result in real time?

VALUE BRIDGE:
Profit+ sits at the intersection of operations and finance: every idea from a lean event, kaizen, or shop-floor observation moves through a standardized lifecycle — Idea Generation, Validate, Evaluate, Implement, Executed, Realized — and ends with Finance verifying the savings against the G/L. Operations does the work. Finance confirms the result. Both see the same number. We have implemented this at lean teams in industrial equipment, medical device manufacturing, and aerospace supply.

CLOSE:
If connecting your lean program to a defensible financial result is a priority at {company}, fifteen minutes would be enough to see how it works. Is there time this week?`,
    tokens: ["{first_name}", "{company}"],
    notes: "Lean-to-finance verification angle. Best for VP Lean, Op-Ex Manager, or Director CI across all manufacturing verticals.",
  },
  {
    id: "ps-ops-004",
    channel: "phone_script",
    persona: "operations",
    title: "Operations phone — shop-floor idea capture and mobile",
    body: `OPENER:
{first_name}, Demo BDR from Meridian. Short question: at {company}, do frontline employees on the production floor have a structured way to submit cost-saving ideas — something as easy as scanning a QR code on their phone — or does idea capture primarily happen through management channels?

[Listen.]

DISCOVERY QUESTIONS:
1. What percentage of your cost-saving ideas come from production-floor employees versus management or engineering?
2. What is the biggest practical barrier to frontline idea capture — is it a lack of a submission mechanism, a lack of follow-through, or something else?
3. If every operator had a phone-based way to submit an idea and received a visible status update on what happened, how would that change participation?

VALUE BRIDGE:
Profit+ includes a mobile-ready citizen idea channel with QR codes that can be placed at each workstation. An operator scans, submits an idea in under two minutes, and receives an automated feedback update at each stage. The ideas flow into the platform alongside procurement and management initiatives through the same standardized lifecycle. This works on an automotive line, a medical device clean room, or an electronics SMT floor equally well.

CLOSE:
The combination of easy submission and visible feedback is what changes frontline participation from occasional to consistent. If that is worth seeing in practice, I can walk you through it in fifteen minutes. When works this week?`,
    tokens: ["{first_name}", "{company}"],
    notes: "Shop-floor capture and mobile angle. Best for Plant Managers and VP Operations across all manufacturing verticals.",
  },
  {
    id: "ps-ops-005",
    channel: "phone_script",
    persona: "operations",
    title: "Operations phone — central function targets to plants",
    body: `OPENER:
{first_name}, Demo BDR from Meridian. One question: at {company}, when corporate sets a cost savings target for the year, how does that number reach the plant floor in a way that is specific, owned, and tracked — or does it stay at the division level and plants manage against their own versions of the target?

[Listen.] The cascading-target problem is one of the most common accountability gaps I see in manufacturing.

DISCOVERY QUESTIONS:
1. Do plant managers at {company} have a specific savings target tied to their performance — or is the target at the corporate or division level only?
2. Is there a system that shows each plant their gap to target in real time, or does that information come through monthly reporting?
3. When a plant is behind, who is accountable and how quickly does that become visible to senior leadership?

VALUE BRIDGE:
Profit+ has a multi-tier target model where the corporate savings goal cascades to division, plant, and department level automatically. Each location has a specific target, can see their real-time gap, and has a dashboard showing which initiatives are in-flight to close it. Central function targets are distributed to each location with full transparency — whether you are running five plants or fifty, across one country or many.

CLOSE:
If that target-cascading accountability is missing at {company}, it is worth fifteen minutes to see how it works. Is there a time this week?`,
    tokens: ["{first_name}", "{company}"],
    notes: "Multi-tier target angle. Best for COO or VP Operations struggling to get plant-level accountability on corporate cost targets.",
  },
  {
    id: "ps-ops-006",
    channel: "phone_script",
    persona: "operations",
    title: "Procurement phone — savings quota operating system",
    body: `OPENER:
{first_name}, Demo BDR from Meridian. I will be direct: I work with procurement leaders who carry a savings number and are looking for a better operating system to run it — specifically, something that closes the gap between validated savings and executed savings. Is that gap real at {company}?

[Listen.] Can I ask a couple of quick questions?

DISCOVERY QUESTIONS:
1. What is your savings target for the year, and what is the current gap between what has been validated and what has been executed and verified by Finance?
2. Which category is most stalled right now — indirect, MRO, freight, packaging, VAVE, energy — and do you know why?
3. How often does Finance see your savings execution number, and is it the same view you are looking at or a separately assembled report?

VALUE BRIDGE:
Profit+ is a platform designed for procurement-led savings programs: every initiative captured and ranked by category and value, routed through an evaluation and approval workflow, tracked to implementation at each site, and verified by Finance when savings hit the G/L. The CFO sees the same number procurement reports because it comes from the same system. We work with procurement teams across industrial manufacturing, aerospace, medical device, and contract production.

CLOSE:
If closing the validated-to-executed gap is a priority at {company} this year, fifteen minutes would be a good place to start. When works?`,
    tokens: ["{first_name}", "{company}"],
    notes: "Lead procurement phone script. Opens with a direct qualification question. Validated-to-executed framing resonates immediately with procurement leaders.",
  },
  {
    id: "ps-ops-007",
    channel: "phone_script",
    persona: "operations",
    title: "Procurement phone — piece-price tracking and supplier savings",
    body: `OPENER:
{first_name}, Demo BDR from Meridian. Quick question: when a piece-price reduction is negotiated with a supplier at {company}, how do you track that saving from commitment to actual G/L impact across your plants — is there a system for that, or is it a manual reconciliation?

[Listen.] That is the most common answer I get from procurement teams in manufacturing.

DISCOVERY QUESTIONS:
1. How many sites are typically affected by a single piece-price negotiation, and is there a consistent way for each site to confirm implementation?
2. How long does it typically take from a signed supplier agreement to confirmed G/L impact at all affected plants?
3. When Finance asks procurement to reconcile the savings number, how long does that take and how often is there a gap?

VALUE BRIDGE:
Profit+ gives procurement and finance a single platform to track supplier negotiation savings from initial approval through piece-price implementation at each plant — with Finance verification built in at the end. No manual reconciliation, no gap between the commitment and the verified result. We see this most acutely in automotive, aerospace, and industrial equipment supply chains where multi-plant supplier agreements are common.

CLOSE:
If piece-price tracking and multi-plant reconciliation are live challenges at {company}, I can show you how it works in fifteen minutes. Is there time this week?`,
    tokens: ["{first_name}", "{company}"],
    notes: "Piece-price tracking angle. Highly specific and credible for procurement in automotive, aerospace, and industrial manufacturing.",
  },
  {
    id: "ps-ops-008",
    channel: "phone_script",
    persona: "operations",
    title: "Procurement phone — supplier ecosystem as idea source",
    body: `OPENER:
{first_name}, Demo BDR from Meridian. Short question: does {company}'s procurement program have a structured channel for suppliers to contribute cost-saving ideas — a formal way for them to submit recommendations that get evaluated and implemented — or does that happen primarily through relationship conversations?

[Listen.]

DISCOVERY QUESTIONS:
1. How much of your savings pipeline comes from supplier-initiated ideas versus procurement-initiated negotiations?
2. Is there a structured process for evaluating and implementing supplier-generated ideas alongside internal initiatives?
3. If suppliers had a direct, structured channel to submit ideas and see feedback on what happened, would that change the quality or volume of what they bring to you?

VALUE BRIDGE:
Profit+ includes a supplier ecosystem channel — suppliers, contractors, and internal employees all submit ideas through the same platform. Those ideas are evaluated through the same standardized lifecycle as internal initiatives, with Finance verifying the savings at the end. An aerospace components manufacturer we work with opened this to their top forty suppliers and surfaced ideas that procurement had not identified internally. Suppliers become active contributors to the savings program, not just targets for it.

CLOSE:
If engaging your supply base as an active idea source is something {company} has been thinking about, fifteen minutes would be worthwhile. Is there a time this week?`,
    tokens: ["{first_name}", "{company}"],
    notes: "Supplier ecosystem angle. Best for CPO or Director Sourcing at companies with large, active supply bases across all manufacturing verticals.",
  },
  {
    id: "ps-ops-009",
    channel: "phone_script",
    persona: "operations",
    title: "Operations phone — energy and asset optimization",
    body: `OPENER:
{first_name}, Demo BDR from Meridian. I work with operations leaders in {industry} on a specific category of cost savings: energy efficiency, preventive versus reactive maintenance, and asset optimization. These are areas where the improvement ideas exist on the plant floor, but the discipline to capture them, evaluate them, and track them through to verified savings is often missing. Does that ring true at {company}?

[Listen.]

DISCOVERY QUESTIONS:
1. How does {company} currently manage energy efficiency and maintenance cost initiatives — is there a structured tracking process or does it happen project by project?
2. When an energy reduction or maintenance improvement is implemented, how does Finance verify the savings — and how long does that take?
3. If ideas from production employees on energy and asset improvements were captured in a structured system and tracked to verified savings, what would that change?

VALUE BRIDGE:
Profit+ captures every type of cost-saving idea — including energy efficiency, preventive maintenance, and asset optimization — in the same standardized lifecycle. Ideas from operators, engineers, and management are all routed through evaluation, approval, and implementation tracking, ending with Finance-verified realization. We see this applied at heavy-equipment manufacturers, industrial equipment OEMs, and building-materials plants with significant energy exposure.

CLOSE:
If building more discipline around energy and asset savings is a priority at {company}, fifteen minutes would be enough to see how the tracking works. When does your schedule open up?`,
    tokens: ["{first_name}", "{company}", "{industry}"],
    notes: "Energy and asset angle. Best for Plant Managers and operations leaders at energy-intensive manufacturers across industrial, heavy-equipment, and building-materials sectors.",
  },
  {
    id: "ps-ops-010",
    channel: "phone_script",
    persona: "operations",
    title: "Operations phone — CI benchmarking across plants",
    body: `OPENER:
{first_name}, Demo BDR from Meridian. Quick question: when you are managing a continuous improvement program across multiple plants, how do you benchmark performance between sites — is there an objective metric that shows which plants are generating the most ideas, implementing the most, and realizing the most savings — or is that comparison informal?

[Listen.] The informal answer is the most common one.

DISCOVERY QUESTIONS:
1. How many plants or sites are in scope for the CI program, and do you have a single view of comparative performance?
2. When one plant outperforms others on cost savings, is there a structured way to understand why and replicate the conditions?
3. What would it mean for the CI program if plant managers could see exactly where they stood relative to peers in real time?

VALUE BRIDGE:
Profit+ gives operations leaders a real-time performance comparison across all plants — ideas generated, ideas implemented, savings realized — with a heat map view that makes it immediately clear who is leading and who is lagging. The shared idea library means the best practices from the top plant are available to every other site. We see the most benefit at manufacturers running parallel production environments — contract manufacturers, multi-plant industrial OEMs, and electronics companies with multiple product lines.

CLOSE:
If better plant-to-plant benchmarking and idea sharing is worth exploring at {company}, I can walk you through the comparison dashboards in fifteen minutes. Is there time this week?`,
    tokens: ["{first_name}", "{company}"],
    notes: "Benchmarking and CI angle. Best for Director of CI or VP Lean managing multiple plants across any manufacturing vertical.",
  },
  // ─── HR ──────────────────────────────────────────────
  {
    id: "ps-hr-001",
    channel: "phone_script",
    persona: "hr",
    title: "HR phone — citizen ideas and feedback loop",
    body: `OPENER:
{first_name}, Demo BDR from Meridian. Quick question: does {company} have a structured employee idea program — one where employees submit cost-saving ideas, receive visible feedback at each stage, and can see when their ideas get implemented — or is idea submission primarily informal through management channels?

[Listen.] The feedback loop question is the one that tells me the most.

DISCOVERY QUESTIONS:
1. What happens to an employee idea after it is submitted — is there a visible process the submitter can track, or does it go into a queue and the employee hears back informally?
2. What is the participation rate — what percentage of employees are actively submitting ideas on a regular basis?
3. When employee ideas have been implemented and generated real savings, how does the organization recognize and communicate that?

VALUE BRIDGE:
Profit+ has a citizen idea channel with mobile and QR-code entry, and an automated feedback loop built in at every stage. Employees can submit an idea in under two minutes, and they receive an automated notification each time it advances, is implemented, or is not moving forward with an explanation. That feedback loop is what makes participation sustainable rather than a one-quarter event — whether you are at a single-plant industrial manufacturer or a global electronics company.

CLOSE:
If rebuilding that feedback loop is a priority at {company}, fifteen minutes to see how it works in practice would be worthwhile. Is there time this week?`,
    tokens: ["{first_name}", "{company}"],
    notes: "Lead HR phone script. The feedback loop question is the strongest HR qualifier. Most HR leaders who have run idea programs will immediately recognize the feedback problem.",
  },
  {
    id: "ps-hr-002",
    channel: "phone_script",
    persona: "hr",
    title: "HR phone — connecting engagement to cost outcomes",
    body: `OPENER:
{first_name}, Demo BDR from Meridian. A direct question: when you bring employee engagement results to the CFO, is there a number that connects engagement to financial outcomes — verified cost savings from employee-contributed ideas — or is the connection primarily qualitative?

[Listen.] If it is qualitative, can I ask a couple of quick questions?

DISCOVERY QUESTIONS:
1. Does {company} currently track which cost-saving ideas originate from employees versus management, procurement, or suppliers?
2. When employee-originated ideas are implemented, is the financial result tracked back to the originating employee in a way that HR can report on?
3. If you could show the CFO: these employees contributed X ideas, Y were implemented, the realized savings were Z — would that change the budget conversation for HR programs?

VALUE BRIDGE:
Profit+ creates that direct connection. Every idea is tracked from submission through to Finance-verified realization in the same platform. HR can report on employee participation rate, ideas implemented, and realized savings from employee contributions — all from the same data source that Finance uses. This connection has been valuable for HR leaders at contract manufacturers, aerospace suppliers, and medical device companies alike.

CLOSE:
If building that connection is a goal at {company}, fifteen minutes would be enough to see how the data flows in the platform. When works this week?`,
    tokens: ["{first_name}", "{company}"],
    notes: "CFO-ROI angle for HR. The qualitative vs quantitative distinction creates an immediate self-assessment moment. Best for CHROs preparing for a CFO review.",
  },
  {
    id: "ps-hr-003",
    channel: "phone_script",
    persona: "hr",
    title: "HR phone — culture of continuous improvement",
    body: `OPENER:
{first_name}, Demo BDR from Meridian. Short question: when {company} talks about building a culture of continuous improvement, what is the practical mechanism that makes it more than a communication initiative — the system or process that gives every employee a structured, visible role in the effort?

[Listen.] The mechanism question separates culture statements from culture programs.

DISCOVERY QUESTIONS:
1. What channels do employees currently have to contribute cost-saving or improvement ideas — and how visible is the response they receive?
2. How is the culture of continuous improvement measured at {company} — is there a metric that tracks participation or engagement in improvement activity?
3. What would it look like for you to tell the CEO that the culture initiative is working — what evidence would you point to?

VALUE BRIDGE:
Profit+ provides the structural mechanism that makes a culture of continuous improvement tangible: mobile idea entry at the production floor, automated feedback to every submitter, and a dashboard that tracks participation and realized savings across the organization. Culture becomes measurable rather than just aspirational — whether you are at a medical device manufacturer, a heavy-equipment OEM, or a building-materials company.

CLOSE:
If building that infrastructure is a priority at {company}, I would like to show you what it looks like in practice. Fifteen minutes — is there time this week?`,
    tokens: ["{first_name}", "{company}"],
    notes: "Culture-of-CI angle. The 'mechanism' question is a sharp probe that separates companies doing real CI work from those with aspirational statements.",
  },
  {
    id: "ps-hr-004",
    channel: "phone_script",
    persona: "hr",
    title: "HR phone — employee incentive programs",
    body: `OPENER:
{first_name}, Demo BDR from Meridian. Quick question: does {company} have a structured incentive or recognition program tied to employee cost-savings contributions — one that applies consistently across all plants and functions — or is recognition primarily at manager discretion?

[Listen.] Manager discretion is the most common answer.

DISCOVERY QUESTIONS:
1. What is the current recognition mechanism for employees who contribute ideas that generate real savings?
2. Is the program consistent across plants — does an employee at one site receive the same recognition as an employee at another for a comparable contribution?
3. If there were an objective, data-driven way to calculate recognition based on verified savings outcomes, would that change how employees perceive the program?

VALUE BRIDGE:
Profit+ tracks every employee idea and its financial outcome in a single platform. Because every realized saving has a verified financial figure, HR can define a consistent, transparent incentive calculation — across all plants, all functions, all languages — and the platform provides the objective data to apply it fairly. This removes the discretion problem and makes the incentive program scalable, whether you are at an industrial equipment OEM or a contract manufacturer with workers across multiple continents.

CLOSE:
If building a fair, scalable recognition program is something you are working on at {company}, fifteen minutes would show you how the data infrastructure supports it. When works this week?`,
    tokens: ["{first_name}", "{company}"],
    notes: "Incentive program angle. Best for HR leaders actively building or revamping recognition programs. The cross-plant consistency angle is a differentiator.",
  },
  {
    id: "ps-hr-005",
    channel: "phone_script",
    persona: "hr",
    title: "HR phone — global engagement at scale",
    body: `OPENER:
{first_name}, Demo BDR from Meridian. Short question: how does {company} run employee engagement programs consistently across {site_count} with different languages, time zones, and management cultures — is there a platform for it, or does each site adapt the program locally?

[Listen.] Local adaptation is the most common answer, and it is why engagement metrics are hard to compare across sites.

DISCOVERY QUESTIONS:
1. Is there a consistent metric across all sites — participation rate, ideas submitted per employee, feedback completion rate — that HR reviews at the global level?
2. What are the biggest practical barriers to running a consistent program globally — language, technology access, management buy-in?
3. If engagement in the cost-improvement program were measurable and comparable across every site, what would that change for how HR makes the case internally?

VALUE BRIDGE:
Profit+ is multi-lingual, mobile-ready, and operates consistently across any number of sites. Every plant runs the same citizen idea process, uses the same QR-code entry system, and feeds the same engagement dashboard. HR reviews one global view rather than piecing together local reports. We work with manufacturers running this across Europe, North America, and Asia — from aerospace suppliers to IoT hardware companies with distributed production.

CLOSE:
If consistency at scale is the challenge at {company}, I can show you how the global dashboard works in fifteen minutes. Is there time this week?`,
    tokens: ["{first_name}", "{company}", "{site_count}"],
    notes: "Global scale angle. Best for VP HR at multi-national manufacturers. Use the {site_count} token when you have plant count data.",
  },
  {
    id: "ps-hr-006",
    channel: "phone_script",
    persona: "hr",
    title: "HR phone — everyone plays a role",
    body: `OPENER:
{first_name}, Demo BDR from Meridian. Short question: when {company} talks about cost optimization, is it a program that lives at the leadership level — in Procurement, Operations, and Finance — or do production-floor employees have a visible, structured role in contributing and seeing the results?

[Listen.] The gap between the two is usually the culture gap.

DISCOVERY QUESTIONS:
1. How do frontline employees at {company} contribute to the cost optimization program today — and how often does that contribution result in something they can see was acted upon?
2. What is the biggest barrier to broader participation — lack of a submission channel, lack of feedback, lack of recognition?
3. If every employee from the assembly technician to the department head had a structured role with visible feedback, what would change about the culture?

VALUE BRIDGE:
Profit+ is designed so that everyone plays a role — from the operator who scans a QR code to submit an idea from the production floor, to the CFO who verifies that savings hit the G/L. Every contributor receives feedback at each stage. The result is an organization where cost optimization is genuinely part of the culture, not just a leadership initiative. We see this play out at manufacturers across industrial, medical device, aerospace, and electronics production.

CLOSE:
If building that kind of broad-based engagement is a priority at {company}, I would like to show you how it works in practice. Is there fifteen minutes available this week?`,
    tokens: ["{first_name}", "{company}"],
    notes: "Everyone-plays-a-role framing. Strong cultural language for CHRO or VP HR. The leadership-vs-organization distinction creates a useful self-assessment moment.",
  },
  {
    id: "ps-hr-007",
    channel: "phone_script",
    persona: "hr",
    title: "HR phone — follow-up call after cold email",
    body: `OPENER:
{first_name}, Demo BDR from Meridian following up on a note I sent last week about employee engagement tied to cost-savings outcomes. I wanted to ask one direct question before I take any more of your time: at {company}, is there currently a system that connects employee idea contributions to verified financial results — or are those two things tracked separately?

[Listen.] That is the core gap I work with HR leaders to close.

DISCOVERY QUESTIONS:
1. How does HR currently demonstrate the ROI of employee engagement programs to the CFO or CEO?
2. Is there a metric today that connects participation in the cost improvement program to a verified financial outcome?
3. If you had that connection — a number HR could show the CFO tying engagement to realized savings — would it change the internal investment in the program?

VALUE BRIDGE:
Profit+ gives HR the data bridge to make that connection. Employee contributions, idea lifecycle status, and Finance-verified savings outcomes all live in the same platform. HR can show the CFO a single report that connects people-program investment to realized cost results — at a medical device manufacturer, a contract electronics company, or a heavy-equipment OEM.

CLOSE:
If that connection is worth building at {company}, fifteen minutes would show you how the data flows. Is there a time that works?`,
    tokens: ["{first_name}", "{company}"],
    notes: "Follow-up call referencing a prior email. Opens by acknowledging the prior touch. The direct qualification question respects the prospect's time.",
  },
  {
    id: "ps-hr-008",
    channel: "phone_script",
    persona: "hr",
    title: "HR phone — OD and accountability infrastructure",
    body: `OPENER:
{first_name}, Demo BDR from Meridian. I work with OD leaders in manufacturing on a specific challenge: building the structural infrastructure that makes a continuous improvement culture sustainable beyond the initial launch. I wanted to ask — at {company}, what is the mechanism that keeps employees engaged in cost improvement month over month, not just during a launch event?

[Listen.] The mechanism question is the one that separates durable programs from event-driven ones.

DISCOVERY QUESTIONS:
1. How does {company} maintain momentum in the cost improvement program between major events — new campaigns, launches, leadership communications?
2. Is there a feedback loop that gives contributing employees visible evidence that their ideas are being taken seriously?
3. What would it mean for the OD program if participation were measurable and consistently visible to leadership?

VALUE BRIDGE:
Profit+ provides the structural infrastructure for durable engagement: easy idea entry at the point of work (mobile, QR code), automated feedback at every stage, recognition tied to verified outcomes, and a dashboard that tracks participation over time. The platform creates the habit rather than depending on periodic campaigns to reinvigorate it. We have helped OD leaders build this at industrial equipment OEMs, aerospace suppliers, and contract manufacturers.

CLOSE:
If building that sustained infrastructure is a priority at {company}, I would like to walk you through what it looks like in fifteen minutes. When works?`,
    tokens: ["{first_name}", "{company}"],
    notes: "OD-specific angle. The 'mechanism for momentum' question is a strong probe for OD practitioners working on sustained culture change.",
  },
  {
    id: "ps-hr-009",
    channel: "phone_script",
    persona: "hr",
    title: "HR phone — mobile and QR for shift workers",
    body: `OPENER:
{first_name}, Demo BDR from Meridian. Quick question: when employees at {company} are on shift without desk access, is there a practical way for them to contribute a cost-saving idea in the moment — or does that channel only really work for salaried employees who are at a computer?

[Listen.] The shift-worker access gap is one of the most common practical barriers in manufacturing HR.

DISCOVERY QUESTIONS:
1. What percentage of {company}'s employee population is on the production floor without regular computer access?
2. Does the current idea submission process require a computer login, or is there a phone-based alternative?
3. If a QR code at every workstation let any employee submit an idea in under two minutes from their phone, would that change the participation rate you see today?

VALUE BRIDGE:
Profit+ has a native mobile app for iOS and Android and QR-code idea entry that can be placed at any workstation. No login required for the initial submission. The idea routes into the platform immediately, the right reviewer is notified, and the submitter receives a feedback update at each stage. This removes the access barrier for shift workers in automotive, electronics, building materials, and heavy-equipment manufacturing alike.

CLOSE:
Removing the access barrier is one of the most cost-effective changes a manufacturer can make to its engagement program. If it is relevant at {company}, fifteen minutes to see the mobile flow would be worthwhile. Is there time this week?`,
    tokens: ["{first_name}", "{company}"],
    notes: "Mobile/QR angle for shift workers. Best for HR leaders at manufacturers with large shift populations across all verticals.",
  },
  {
    id: "ps-hr-010",
    channel: "phone_script",
    persona: "hr",
    title: "HR phone — HR's strategic seat at the cost table",
    body: `OPENER:
{first_name}, Demo BDR from Meridian. Short question: in the cost-optimization governance at {company} — the meetings where targets are set, progress is reviewed, and results are verified — is HR at the table as a contributor to measured outcomes, or as a supporting function?

[Listen.] The distinction matters more than it might seem for HR's internal standing.

DISCOVERY QUESTIONS:
1. How is HR involved in the cost-savings program today — through culture and change management, through the employee idea channel, or both?
2. When the CFO reviews cost program results, does the HR-driven contribution show up as a line item or as a qualitative footnote?
3. If HR's contribution to realized savings were measurable and verifiable — a number the CFO could see in the same platform as Operations and Procurement — would that change HR's seat at the table?

VALUE BRIDGE:
Profit+ gives HR a measurable seat at the table. Employee-contributed ideas are tracked through the same Finance-verified lifecycle as procurement and operations initiatives. The CFO sees the same number for employee contributions that Finance has verified, not a self-reported engagement score. We have helped HR leaders make this shift at industrial manufacturers, medical device companies, and aerospace suppliers.

CLOSE:
If giving HR a quantifiable contribution to the cost program is a priority at {company}, I would like to show you how it works in fifteen minutes. Is there time this week?`,
    tokens: ["{first_name}", "{company}"],
    notes: "Strategic positioning angle. Best for senior HR leaders who want to elevate HR's standing in cost-optimization governance across all manufacturing sectors.",
  },
  // ─── VOICEMAIL SCRIPT (30: 10 Finance, 10 Operations, 10 HR) ─────────────────
  // ─── FINANCE ──────────────────────────────────────────────
  {
    id: "vs-fin-001",
    channel: "voicemail_script",
    persona: "finance",
    title: "Finance voicemail — validated vs executed savings",
    body: `Hi {first_name}, this is Demo BDR with Meridian. I am calling because most CFOs in {industry} I talk to are carrying a gap between validated savings and savings that have actually flowed through the P&L — approved initiatives that never quite made it to G/L verification. If that gap is real at {company}, I think fifteen minutes is worth your time. I will follow up by email today. You can reach me at {rep_signature}.`,
    tokens: ["{first_name}", "{company}", "{industry}", "{rep_signature}"],
    notes: "Lead finance voicemail. 20-25 seconds spoken. Pair with same-day email. The validated-vs-executed frame is specific enough to stand out across all manufacturing sectors.",
  },
  {
    id: "vs-fin-002",
    channel: "voicemail_script",
    persona: "finance",
    title: "Finance voicemail — P&L verification",
    body: `Hi {first_name}, Demo BDR with Meridian. Short message — one question I ask CFOs in manufacturing: when a cost initiative is marked complete by Operations, does Finance verify the savings in the G/L independently, or is the number self-reported? If that distinction matters at {company}, I would value fifteen minutes. I will send a note today. Thank you.`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "20 seconds spoken. The self-reported vs verified distinction creates a reason to call back. Always pair with a same-day email.",
  },
  {
    id: "vs-fin-003",
    channel: "voicemail_script",
    persona: "finance",
    title: "Finance voicemail — real-time savings dashboard",
    body: `Hi {first_name}, Demo BDR from Meridian. Quick message — I work with CFOs in {industry} who want a real-time view of their cost savings program — gap to goal, performance to budget, which plants are ahead and which are at risk — before the quarter closes, not after. If that is a live gap at {company}, worth fifteen minutes. I will follow up by email today.`,
    tokens: ["{first_name}", "{company}", "{industry}", "{rep_signature}"],
    notes: "Real-time dashboard angle. 20 seconds spoken. The 'before the quarter closes' timing frame creates urgency without being artificial.",
  },
  {
    id: "vs-fin-004",
    channel: "voicemail_script",
    persona: "finance",
    title: "Finance voicemail — EBITDA discipline",
    body: `Hi {first_name}, Demo BDR with Meridian. I work with finance leaders in manufacturing on the gap between cost-savings commitments and EBITDA delivery — specifically, why that gap exists and how to close it with a verified savings process rather than a self-reported one. Manufacturers across industrial, aerospace, and contract production face the same pattern. If it is relevant at {company}, I will follow up by email today. Thank you.`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "EBITDA angle. 20 seconds spoken. Brief and credible. Let the follow-up email carry the substance.",
  },
  {
    id: "vs-fin-005",
    channel: "voicemail_script",
    persona: "finance",
    title: "Finance voicemail — multi-site consolidated view",
    body: `Hi {first_name}, Demo BDR with Meridian. Quick message — I work with CFOs at multi-plant manufacturers who want a consolidated real-time view of cost savings performance across all locations, without waiting for plant controllers to submit monthly. Whether you are running six sites or sixty, the consolidation lag is the same problem. If that is a challenge at {company}, fifteen minutes would be worthwhile. I will send a note today. Thank you.`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Multi-site angle. 20 seconds. Best for CFOs managing more than five plants.",
  },
  {
    id: "vs-fin-006",
    channel: "voicemail_script",
    persona: "finance",
    title: "Finance voicemail — follow-up after cold email",
    body: `Hi {first_name}, Demo BDR with Meridian following up on a note I sent last week about cost savings verification at {company}. I wanted to ask one direct question: does Finance at {company} verify realized savings against the G/L, or is the savings number self-reported by the teams that ran the programs? I will send a brief note today. Thank you.`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Follow-up voicemail referencing the prior email. 20 seconds. The direct G/L verification question gives the prospect a reason to call back or reply.",
  },
  {
    id: "vs-fin-007",
    channel: "voicemail_script",
    persona: "finance",
    title: "Finance voicemail — operating margin proof point",
    body: `Hi {first_name}, Demo BDR with Meridian. Brief message — I work with finance leaders in {industry} on the kind of cost discipline that moves operating income margin over a multi-year horizon. A Tier 1 automotive supplier we work with moved their margin from 3% to over 7% across five years. We have seen comparable results at aerospace and industrial manufacturers. If that outcome is relevant to {company}, I will follow up by email today. Thank you.`,
    tokens: ["{first_name}", "{company}", "{industry}", "{rep_signature}"],
    notes: "Proof point voicemail. 20-25 seconds. Use the anonymized proof point carefully. Never name the source.",
  },
  {
    id: "vs-fin-008",
    channel: "voicemail_script",
    persona: "finance",
    title: "Finance voicemail — budget season prioritization",
    body: `Hi {first_name}, Demo BDR with Meridian. Calling around budget season because I work with CFOs on building a more objective way to prioritize cost-saving proposals — ranked by maturity and expected return before the budget meeting, not decided in the meeting based on stakeholder advocacy. Relevant across manufacturing sectors from medical device to heavy equipment. If that kind of structure would be useful at {company}, I will follow up by email today.`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Budget season angle. 20 seconds. Use between September and November when companies are in budget planning.",
  },
  {
    id: "vs-fin-009",
    channel: "voicemail_script",
    persona: "finance",
    title: "Finance voicemail — breakup",
    body: `Hi {first_name}, Demo BDR with Meridian. I have reached out a few times and will leave it here. If cost savings verification and P&L flow-through become a live topic at {company}, I am easy to find. I will send one final note today. Thank you for your time.`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Day 14 breakup voicemail. Under 20 seconds. Respectful and clean.",
  },
  {
    id: "vs-fin-010",
    channel: "voicemail_script",
    persona: "finance",
    title: "Finance voicemail — stall visibility",
    body: `Hi {first_name}, Demo BDR with Meridian. Quick message — when a cost initiative stalls at {company}, how long before Finance knows about it? Most CFOs I work with find out at month-end, across industrial manufacturing, aerospace, and contract production alike. Profit+ makes stalls visible in real time, to Finance and to the team responsible for execution. I will follow up today with a brief note. Thank you.`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Real-time stall visibility angle. 20 seconds. The 'how long before Finance knows' question creates an immediate self-assessment. Best as a Day 7 voicemail.",
  },
  // ─── OPERATIONS ──────────────────────────────────────────────
  {
    id: "vs-ops-001",
    channel: "voicemail_script",
    persona: "operations",
    title: "Operations voicemail — stalled programs",
    body: `Hi {first_name}, Demo BDR with Meridian. Calling because I work with operations leaders in {industry} whose cost programs stall between approval and execution — ideas approved, spreadsheets built, and then months go by with no movement. If that pattern is real at {company}, I would value fifteen minutes. I will follow up by email today. Thank you.`,
    tokens: ["{first_name}", "{company}", "{industry}", "{rep_signature}"],
    notes: "Lead operations voicemail. 20 seconds. The stalled-program frame is specific and immediately recognizable. Pair with same-day email.",
  },
  {
    id: "vs-ops-002",
    channel: "voicemail_script",
    persona: "operations",
    title: "Operations voicemail — read-across between plants",
    body: `Hi {first_name}, Demo BDR with Meridian. Short message — I work with operations leaders at multi-plant manufacturers on making sure proven cost-saving ideas from one site travel to other sites, rather than getting reinvented at each location. We see this in automotive, industrial equipment, electronics, and aerospace — everywhere multi-site manufacturing runs. If that read-across discipline is something {company} is working on, fifteen minutes would be worthwhile. I will send a note today.`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Read-across angle. 20 seconds. Best for multi-plant VP Operations across all manufacturing sectors.",
  },
  {
    id: "vs-ops-003",
    channel: "voicemail_script",
    persona: "operations",
    title: "Operations voicemail — lean savings and financial proof",
    body: `Hi {first_name}, Demo BDR with Meridian. Quick message — I work with lean and continuous improvement leaders who want to show the CFO that their program is producing verifiable financial results, not just activity metrics. If that gap exists at {company}, I will follow up by email today with a brief explanation of how we close it. Thank you.`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Lean-to-finance verification angle. 20 seconds. Best for VP Lean or Director of CI.",
  },
  {
    id: "vs-ops-004",
    channel: "voicemail_script",
    persona: "operations",
    title: "Operations voicemail — shop-floor idea capture",
    body: `Hi {first_name}, Demo BDR with Meridian. Short message — at most manufacturers, the employees closest to the work have the best ideas for improving it, but there is no easy channel to capture them from the production floor. Profit+ gives any employee a QR-code scan to submit an idea in under two minutes. If that kind of frontline capture is relevant at {company}, I will send a note today.`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Shop-floor capture angle. 20 seconds. The QR-code detail is concrete and differentiating.",
  },
  {
    id: "vs-ops-005",
    channel: "voicemail_script",
    persona: "operations",
    title: "Operations voicemail — plant performance comparison",
    body: `Hi {first_name}, Demo BDR with Meridian. Quick message — I work with operations leaders in {industry} who want a real-time view of which plants are leading on cost savings and why — not a monthly report, but a live heat map across sites. If that kind of plant-to-plant visibility is something {company} is building toward, I will follow up by email today. Thank you.`,
    tokens: ["{first_name}", "{company}", "{industry}", "{rep_signature}"],
    notes: "Plant performance heat map angle. 20 seconds. Best for COO or VP Operations managing multiple plants.",
  },
  {
    id: "vs-ops-006",
    channel: "voicemail_script",
    persona: "operations",
    title: "Procurement voicemail — savings-quota gap",
    body: `Hi {first_name}, Demo BDR with Meridian. Calling because most procurement leaders I talk to in {industry} are carrying a gap between validated savings and executed savings — initiatives that cleared approval but stalled at rollout. If that gap is real at {company}, fifteen minutes would be worthwhile. I will follow up by email today. Thank you.`,
    tokens: ["{first_name}", "{company}", "{industry}", "{rep_signature}"],
    notes: "Lead procurement voicemail. 20 seconds. Validated-vs-executed frame resonates immediately with procurement leaders carrying a quota.",
  },
  {
    id: "vs-ops-007",
    channel: "voicemail_script",
    persona: "operations",
    title: "Procurement voicemail — piece-price tracking",
    body: `Hi {first_name}, Demo BDR with Meridian. Short message — when a piece-price reduction is negotiated with a supplier, how does {company} track that saving from commitment to G/L impact across plants? At most manufacturers across automotive, aerospace, and industrial production, that is a manual reconciliation. If there is a better approach worth exploring, I will follow up by email today. Thank you.`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Piece-price tracking angle. 20 seconds. Highly specific and credible for manufacturing procurement. Creates an immediate self-assessment.",
  },
  {
    id: "vs-ops-008",
    channel: "voicemail_script",
    persona: "operations",
    title: "Operations voicemail — follow-up after cold email",
    body: `Hi {first_name}, Demo BDR with Meridian following up on a note I sent last week about cost-savings execution at {company}. One direct question: does Operations have a system that tracks every cost initiative from approval through to Finance-verified realization, or does that tracking live in spreadsheets that Finance cannot see in real time? I will follow up today. Thank you.`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Follow-up voicemail referencing prior email. 25 seconds. The direct question about spreadsheets vs system gives the prospect something concrete to react to.",
  },
  {
    id: "vs-ops-009",
    channel: "voicemail_script",
    persona: "operations",
    title: "Operations voicemail — supplier ecosystem",
    body: `Hi {first_name}, Demo BDR with Meridian. Quick message — I work with operations and procurement leaders who are structuring their supply base as an active source of cost-saving ideas — with a formal channel for supplier submissions that gets evaluated and tracked alongside internal initiatives. We see this across automotive, medical device, and industrial manufacturing supply chains. If that is a direction {company} is considering, I will send a note today. Thank you.`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Supplier ecosystem angle. 20 seconds. Works as a Day 7 voicemail after a cold email.",
  },
  {
    id: "vs-ops-010",
    channel: "voicemail_script",
    persona: "operations",
    title: "Operations voicemail — breakup",
    body: `Hi {first_name}, Demo BDR with Meridian. I have reached out a few times and will leave it here. If cost-savings execution discipline — getting ideas from the production floor through to verified P&L impact — becomes a live priority at {company}, I am easy to find. I will send one final note today. Thank you for your time.`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Day 14 breakup voicemail. Under 20 seconds. Clean and respectful.",
  },
  // ─── HR ──────────────────────────────────────────────
  {
    id: "vs-hr-001",
    channel: "voicemail_script",
    persona: "hr",
    title: "HR voicemail — citizen ideas and feedback loop",
    body: `Hi {first_name}, Demo BDR with Meridian. Brief message — I work with HR leaders in manufacturing on employee idea programs where every submission receives a visible, automated response at each stage — not a suggestion box that goes quiet after the first month. Whether you are at an industrial manufacturer or a medical device company, the feedback loop problem is the same. If building it at {company} is relevant, I will follow up by email today. Thank you.`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Lead HR voicemail. 20 seconds. The feedback loop distinction separates Profit+ from generic suggestion tools.",
  },
  {
    id: "vs-hr-002",
    channel: "voicemail_script",
    persona: "hr",
    title: "HR voicemail — engagement tied to cost outcomes",
    body: `Hi {first_name}, Demo BDR with Meridian. Quick message — I work with HR leaders who want to show the CFO a verified connection between employee engagement and cost outcomes: not a survey score, but realized savings from employee-contributed ideas. If building that connection at {company} is a goal, I will follow up by email today. Thank you.`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "CFO-ROI angle. 20 seconds. The 'not a survey score, but realized savings' contrast is sharp and memorable.",
  },
  {
    id: "vs-hr-003",
    channel: "voicemail_script",
    persona: "hr",
    title: "HR voicemail — culture of continuous improvement",
    body: `Hi {first_name}, Demo BDR with Meridian. Short message — I work with HR leaders in manufacturing on the structural infrastructure that makes a culture of continuous improvement sustainable — not just a launch event, but a platform that keeps employees contributing and receiving feedback month after month. If that is relevant at {company}, I will send a note today. Thank you.`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Culture-of-CI angle. 20 seconds. The launch-vs-sustained distinction is credible and non-threatening.",
  },
  {
    id: "vs-hr-004",
    channel: "voicemail_script",
    persona: "hr",
    title: "HR voicemail — QR code and mobile for frontline",
    body: `Hi {first_name}, Demo BDR with Meridian. Quick message — one of the most practical barriers to employee idea programs in manufacturing is access for shift workers without a computer nearby. Profit+ uses QR codes at the workstation and a mobile app so any employee can submit an idea in under two minutes — on an assembly line, in a clean room, or on a production floor. If that kind of access matters at {company}, I will follow up today. Thank you.`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Mobile/QR angle for shift workers. 20 seconds. Concrete and practical across all manufacturing environments.",
  },
  {
    id: "vs-hr-005",
    channel: "voicemail_script",
    persona: "hr",
    title: "HR voicemail — employee incentive programs",
    body: `Hi {first_name}, Demo BDR with Meridian. Brief message — I work with HR leaders building structured, data-driven incentive programs tied to employee cost-savings contributions — fair, consistent across plants, and based on verified financial outcomes rather than manager discretion. If that kind of structure is something {company} is working toward, I will follow up by email today. Thank you.`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Incentive program angle. 20 seconds. The 'fair and consistent across plants' frame addresses a real pain in recognition programs.",
  },
  {
    id: "vs-hr-006",
    channel: "voicemail_script",
    persona: "hr",
    title: "HR voicemail — global engagement at scale",
    body: `Hi {first_name}, Demo BDR with Meridian. Short message — I work with HR leaders at multi-site manufacturers on running employee engagement programs consistently across all plants — same language support, same feedback loop, same metrics — without depending on each site to adapt locally. We work with manufacturers in electronics, industrial equipment, aerospace, and building materials. If consistency at scale is a challenge at {company}, I will send a note today. Thank you.`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Global scale angle. 20 seconds. Best for VP HR at multi-national or multi-plant manufacturers.",
  },
  {
    id: "vs-hr-007",
    channel: "voicemail_script",
    persona: "hr",
    title: "HR voicemail — everyone plays a role",
    body: `Hi {first_name}, Demo BDR with Meridian. Quick message — I work with HR leaders in manufacturing who want to make cost optimization something every employee plays a role in — from the operator scanning a QR code on the production floor to the CFO verifying savings in the G/L. If that kind of organization-wide engagement is a priority at {company}, I will follow up by email today. Thank you.`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Everyone-plays-a-role angle. 20 seconds. The production floor to G/L arc captures the full scope without requiring explanation.",
  },
  {
    id: "vs-hr-008",
    channel: "voicemail_script",
    persona: "hr",
    title: "HR voicemail — follow-up after cold email",
    body: `Hi {first_name}, Demo BDR with Meridian following up on a note I sent last week about employee engagement tied to verified cost outcomes at {company}. One direct question: does HR at {company} have a metric that connects employee contributions to realized savings that Finance can verify — or is that connection still qualitative? I will follow up today. Thank you.`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Follow-up voicemail referencing prior email. 20 seconds. The qualitative vs verified distinction creates a reason to call back.",
  },
  {
    id: "vs-hr-009",
    channel: "voicemail_script",
    persona: "hr",
    title: "HR voicemail — HR as strategic contributor",
    body: `Hi {first_name}, Demo BDR with Meridian. Brief message — I work with HR leaders in manufacturing who are building a measurable, verifiable contribution to the cost-savings program — not a supporting role, but a line item the CFO can see. We have helped HR make this shift at industrial equipment OEMs, aerospace suppliers, medical device manufacturers, and contract electronics companies. If that is a direction {company}'s HR team is working toward, I will follow up by email today. Thank you.`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Strategic contributor angle. 20 seconds. Best for CHRO or VP HR working to elevate HR's standing in the business.",
  },
  {
    id: "vs-hr-010",
    channel: "voicemail_script",
    persona: "hr",
    title: "HR voicemail — breakup",
    body: `Hi {first_name}, Demo BDR with Meridian. I have reached out a few times and will leave it here. If employee engagement tied to measurable cost outcomes — with a feedback loop for every contributor — becomes a live priority at {company}, I am easy to find. I will send one final note today. Thank you for your time.`,
    tokens: ["{first_name}", "{company}", "{rep_signature}"],
    notes: "Day 14 HR breakup voicemail. Under 20 seconds. Leaves with the feedback loop framing — the most distinctive HR angle.",
  },
];

// =============================================================================
// OBJECTION HANDLERS (20 total)
// =============================================================================

export const OBJECTION_HANDLERS: TemplateObjection[] = [
  {
    id: "obj-001",
    objection: "No budget right now",
    response: "I hear that, and I am not asking for a budget commitment today. The manufacturing organizations that have found Profit+ most valuable started with a fifteen-minute conversation to understand whether there was a problem worth solving — and what it was already costing them in unverified savings and manual reconciliation work. Most found the analysis of the current state was useful on its own, independent of a purchase decision. Budget conversations go faster once the cost of the status quo is on the table.",
    pivot_question: "When your CFO last reviewed the savings target, how much of that number was verified against the G/L versus estimated or assumed?",
    category: "budget",
  },
  {
    id: "obj-002",
    objection: "The price is too high",
    response: "That is a fair place to start, and I want to give you a real answer rather than a deflection. The cost of Profit+ is almost always smaller than the cost of what it replaces — specifically the management overhead, the manual reconciliation burden, the double-counted savings that get challenged at year-end, and the realized gap that nobody caught in time. I would rather run the ROI conversation with your numbers than defend a price point in the abstract.",
    pivot_question: "If we could quantify the gap between your validated savings and your realized savings over the last twelve months, what would that number need to look like for a structured platform to make economic sense?",
    category: "budget",
  },
  {
    id: "obj-003",
    objection: "We just spent on another initiative and cannot take on more right now",
    response: "Understood — and I am not suggesting you displace what you just invested in. The question is whether that initiative addresses the savings-tracking and G/L verification problem specifically. Most capital and operational improvement programs create initiatives that still need to be captured, scored, and verified somewhere. If that somewhere is still a spreadsheet or a manual process, the problem Profit+ solves is still on the table regardless of what else you deployed.",
    pivot_question: "Does the initiative you just rolled out give Finance a way to verify savings against the G/L before the number goes on a report, or is that still a manual step?",
    category: "budget",
  },
  {
    id: "obj-004",
    objection: "Not the right time",
    response: "Timing matters, and I will not push against it. What I find useful is understanding what would need to change for the timing to be right — because the cost of the status quo is accumulating in the meantime. Unverified savings, ideas lost between plant and Finance, and a gap-to-goal number that leadership does not fully trust are not problems that get cheaper to solve by waiting.",
    pivot_question: "What would need to be different in the next quarter for a structured savings-tracking initiative to move onto the priority list?",
    category: "timing",
  },
  {
    id: "obj-005",
    objection: "Call me next quarter",
    response: "I can do that. Before I do, I want to make sure I am calling about the right thing when the time comes. The challenges Profit+ addresses — unverified savings, fragmented idea tracking, end-of-quarter reconciliation surprises — tend to be most acute at reporting cycles. A five-minute conversation now would make the next call more useful for both of us.",
    pivot_question: "When you think about the next quarterly savings review, what is the piece of the process that is most likely to produce a number Finance or the board will question?",
    category: "timing",
  },
  {
    id: "obj-006",
    objection: "We are in the middle of year-end and cannot evaluate anything new",
    response: "Year-end is exactly the moment this conversation matters most. The pressure at year-end usually comes from a savings number that is harder to defend than it should be — ideas that are claimed but not verified, a gap between what was projected and what the G/L shows. I am not asking you to make a decision during year-end. I am asking whether the pain you are feeling right now is worth fifteen minutes to understand what causes it.",
    pivot_question: "What is the hardest part of the year-end savings close for your Finance team right now — verification, attribution, or reconciling across plants?",
    category: "timing",
  },
  {
    id: "obj-007",
    objection: "I am not the decision maker",
    response: "I appreciate you being direct about that — it is more useful than a runaround. A decision like this typically sits at the intersection of Finance, Operations, and the executive sponsor of the continuous improvement program. I want to make sure I am talking to the people who feel the problem most directly, and then I can map from there to whoever controls the budget.",
    pivot_question: "Who in your organization owns the savings target and is most directly accountable for the verified-versus-realized gap at the end of the year?",
    category: "authority",
  },
  {
    id: "obj-008",
    objection: "My CFO would have to weigh in on this",
    response: "That makes complete sense, and the CFO conversation is usually the one I most want to have. Profit+ is fundamentally a Finance confidence tool — it gives the CFO a G/L-verified savings number they can present to a board or PE sponsor without manually reconciling it the night before. I would rather have that conversation with the CFO directly than ask you to carry it.",
    pivot_question: "Would it be useful for me to put together a one-page summary framed specifically for the CFO — focused on G/L verification and audit trail rather than the platform features?",
    category: "authority",
  },
  {
    id: "obj-009",
    objection: "We already track our savings",
    response: "Most manufacturing organizations do track savings in some form. The question is whether what you are tracking is verified, structured, and defensible. Tracked and verified are different things. If your savings live in a spreadsheet or a shared folder, Finance is still reconciling manually at the end of every quarter, and there is no systematic audit trail per idea. That is what Profit+ closes — not the tracking itself, but the verification and accountability infrastructure around it.",
    pivot_question: "When Finance reviews the savings number at quarter-end, how much of that reconciliation work is manual — and how often does the number change between the initial report and the final board presentation?",
    category: "need",
  },
  {
    id: "obj-010",
    objection: "We have a continuous improvement program already",
    response: "A continuous improvement program is exactly the context where Profit+ belongs. CI programs generate ideas — Profit+ is the system that captures, scores, routes, verifies, and reports on those ideas so the program has a financial spine. The program produces the work; Profit+ makes that work defensible to Finance and visible to leadership. They are not alternatives to each other.",
    pivot_question: "How does your current CI program connect the ideas generated on the plant floor to a verified savings number in the P&L — what is the process between an approved idea and a confirmed G/L entry?",
    category: "need",
  },
  {
    id: "obj-011",
    objection: "We already have spreadsheets that work fine",
    response: "Spreadsheets work fine until they do not. The moment the program spans more than one site, more than one Finance owner, or survives the departure of the person who built the file, the spreadsheet becomes a liability. There is no audit trail, no lifecycle enforcement, no G/L verification, and no real-time view for leadership. The spreadsheet is not wrong — it is just not designed for what a multi-site savings program actually requires.",
    pivot_question: "What happens to your savings tracking process if the person who owns the spreadsheet leaves or changes roles tomorrow?",
    category: "need",
  },
  {
    id: "obj-012",
    objection: "Our consulting firm handles this",
    response: "Consulting firms drive great initiatives. They also leave. When the engagement ends, what stays behind is usually a report, a methodology, and a spreadsheet. Profit+ is the infrastructure that makes the savings program self-sustaining after the consultants are gone — a platform the internal team owns, Finance can verify, and leadership can see in real time. The two are complementary, not competing.",
    pivot_question: "When the current consulting engagement ends, what does the savings tracking process look like — who owns it, and what tools are they using?",
    category: "need",
  },
  {
    id: "obj-013",
    objection: "I have never heard of the partner team",
    response: "That is a fair starting point, and I would rather earn credibility than ask you to assume it. The external delivery partner built Profit+ specifically for multi-site manufacturing organizations managing cost-optimization programs. The platform is purpose-built for this problem — not adapted from a generic project management or CRM tool. The best way to evaluate whether we are the right fit is to see what the platform does with a real example from your savings program.",
    pivot_question: "What would a credible proof point look like for you — a reference from a similar manufacturing organization, a live walkthrough of the platform, or a breakdown of the G/L verification methodology?",
    category: "trust",
  },
  {
    id: "obj-014",
    objection: "Show me ROI proof",
    response: "I can build that case, and I want to do it with your numbers rather than generic benchmarks. The ROI model for Profit+ looks at three inputs: the current gap between validated and realized savings, the fully loaded cost of manual reconciliation across Finance and Operations, and the value of recaptured ideas that currently fall through the process. In most manufacturing organizations, the gap alone is large enough to justify the investment in the first year.",
    pivot_question: "If you are comfortable sharing a rough number — what is the typical gap between what your savings program projects at the start of the year and what is verified in the P&L by year-end?",
    category: "trust",
  },
  {
    id: "obj-015",
    objection: "We are talking to Brand X",
    response: "Good to know — I would like to understand what they are offering before I position against them. Profit+ is purpose-built for manufacturing cost optimization: idea capture, lifecycle enforcement, G/L verification, and multi-site gap-to-goal reporting. If the other vendor covers that ground end-to-end, that is worth knowing. If they do not, we should talk about what is missing. Either way, the decision should be based on what the platform actually does, not marketing claims.",
    pivot_question: "What does Brand X do well, and what is still unclear or missing from what they have shown you so far?",
    category: "competition",
  },
  {
    id: "obj-016",
    objection: "We have a homegrown system",
    response: "Homegrown systems are common in manufacturing, and most of them were built by someone who has since left. The question is not whether the system exists — it is whether Finance trusts it, whether it enforces a lifecycle, whether it has an audit trail, and whether anyone can maintain it when it breaks. Profit+ is the supported, roadmapped replacement for systems that are half-working and fully unaccountable. We have helped organizations migrate off homegrown tools without losing their historical data.",
    pivot_question: "If the person who built your current system left tomorrow, who would fix it — and how confident are you that Finance fully trusts the numbers it produces?",
    category: "competition",
  },
  {
    id: "obj-017",
    objection: "My team won't use another tool",
    response: "Adoption is the right concern to raise — it is where most implementations fail. Profit+ is designed to reduce friction, not add it. Plant managers and idea owners enter savings ideas through a structured but simple interface. Finance verifies against the G/L through a workflow they already understand. Leadership sees dashboards without chasing updates. The platform works with the existing process rather than requiring people to learn a new one from scratch.",
    pivot_question: "Which role on your team would be hardest to get to adopt a new process — plant floor idea originators, Finance, or operational leadership — and what would make it easier for them?",
    category: "adoption",
  },
  {
    id: "obj-018",
    objection: "We tried a savings tracker before and it failed",
    response: "That is the most important context you can give me, and I want to understand what happened rather than dismiss it. Most failed savings-tracking implementations trace back to one of three things: the tool was too complex for plant-floor adoption, Finance never trusted the output, or there was no enforcement of the idea lifecycle so it became a data dump that nobody maintained. Profit+ is designed around all three failure modes — but the only way to know if it addresses what broke for you is to understand specifically what that was.",
    pivot_question: "What specifically broke down in the previous attempt — was it plant-floor adoption, Finance buy-in, or did the platform just not reflect how your savings program actually works?",
    category: "adoption",
  },
  {
    id: "obj-019",
    objection: "Send me information first",
    response: "I can absolutely follow up with material. The most useful thing I can send is tailored to your specific situation — otherwise it is a generic deck that may not land. A fifteen-minute call would let me make sure what I send is relevant to the way your savings program runs, not just a standard overview. That call also means I can answer questions in real time instead of via email chains.",
    pivot_question: "What specifically would be most useful to see — the G/L verification workflow, the multi-site reporting view, or how the idea lifecycle is enforced across functions?",
    category: "decision_process",
  },
  {
    id: "obj-020",
    objection: "We are not making any decisions until the new fiscal year",
    response: "That is a reasonable position, and I will respect it. What I would find useful is understanding what the evaluation process looks like when the time comes — who is involved, what the decision criteria are, and whether there is anything useful I can prepare between now and then. The organizations that move fastest when the window opens are the ones that did the groundwork before it did.",
    pivot_question: "When the new fiscal year planning cycle starts, who will be in the room for the savings technology evaluation — and what will they need to see to move forward?",
    category: "decision_process",
  },
  {
    id: "obj-021",
    objection: "Let me discuss with the team",
    response: "That makes complete sense, and I would expect it on a decision like this. The most productive internal conversations tend to happen when there is a specific question on the table rather than a concept. The question I would suggest bringing to your team is: between our current savings number and what is verified in the G/L at year-end, what is the gap — and what is it costing us to live with that gap every year?",
    pivot_question: "Who else would be part of that conversation — is it primarily Finance and Operations, or does the executive sponsor of the CI program have a voice in the decision?",
    category: "decision_process",
  },
];

// =============================================================================
// DISCOVERY QUESTIONS (120 total: 40 per persona)
// =============================================================================

export const DISCOVERY_QUESTIONS: TemplateQuestion[] = [
// ─── FINANCE PERSONA (40 questions) ─────────────────────────────────────

  {
    id: "dq-fin-001",
    persona: "finance",
    question: "When your team prepares the pipeline number for a board or executive review, how much of that preparation involves adjusting what comes directly out of the CRM — and what drives those adjustments?",
    why_it_matters: "Reveals whether finance trusts raw CRM data and where the manual reconciliation burden lives.",
    good_answer_reveals: "Finance is doing significant manual adjusting — a clear signal that CRM data quality is low and the forecast process is labor-intensive and subjective.",
    category: "pain",
  },
  {
    id: "dq-fin-002",
    persona: "finance",
    question: "In the last four quarters, how many times did your actual closed revenue land within five percent of your forecast at the beginning of that quarter?",
    why_it_matters: "Establishes a factual baseline for savings program accuracy, which is the core problem Profit+ is built to solve.",
    good_answer_reveals: "If the answer is fewer than three of four quarters, there is a measurable forecast accuracy problem with quantifiable EBITDA implications.",
    category: "pain",
  },
  {
    id: "dq-fin-003",
    persona: "finance",
    question: "How do you currently distinguish between pipeline that is genuinely in motion and pipeline that is stale but still showing as active in your CRM?",
    why_it_matters: "Surfaces whether the organization has any systematic stale deal identification — or if it's all manual and ad hoc.",
    good_answer_reveals: "Most manufacturing organizations rely on manual updates or manager judgment rather than systematic verification — a direct gap Profit+ closes.",
    category: "pain",
  },
  {
    id: "dq-fin-004",
    persona: "finance",
    question: "What does the conversation between your finance team and your sales leadership look like when the Q-end forecast miss is significant — who owns the explanation and what data do they bring?",
    why_it_matters: "Reveals organizational tension between finance and sales, and whether there is a shared data foundation for those conversations.",
    good_answer_reveals: "If those conversations are contentious or rely on competing interpretations of the same CRM data, there is a process and data alignment gap.",
    category: "pain",
  },
  {
    id: "dq-fin-005",
    persona: "finance",
    question: "How does your organization weight the pipeline for purposes of revenue forecasting — is that weighting set by finance, by sales management, or derived from the CRM probability fields?",
    why_it_matters: "Identifies whether pipeline weighting is systematic or negotiated — and who controls the forecast assumptions.",
    good_answer_reveals: "CRM probability fields set by AEs are unreliable; if weighting is based on those, the forecast is systematically optimistic.",
    category: "pain",
  },
  {
    id: "dq-fin-006",
    persona: "finance",
    question: "When you are doing resource allocation planning — headcount, marketing investment, capacity — how far forward can you realistically forecast pipeline with confidence?",
    why_it_matters: "Measures forecast horizon — organizations with poor pipeline integrity can't plan more than one quarter ahead.",
    good_answer_reveals: "Short forecast horizons indicate pipeline quality is too poor to support multi-quarter planning, which creates resource allocation inefficiencies.",
    category: "need",
  },
  {
    id: "dq-fin-007",
    persona: "finance",
    question: "Have you ever had an auditor, investor, or board member question the methodology behind your pipeline number — and if so, what was the basis of that question?",
    why_it_matters: "Establishes external accountability pressure on pipeline methodology, which is a strong driver for process improvement.",
    good_answer_reveals: "Any external scrutiny on pipeline methodology creates urgency for a more defensible, documented qualification process.",
    category: "pain",
  },
  {
    id: "dq-fin-008",
    persona: "finance",
    question: "What is your current sales cycle length from first qualified meeting to close — and has that changed meaningfully over the last twelve months?",
    why_it_matters: "Cycle length trends reveal pipeline velocity problems and the impact of team changes or market shifts.",
    good_answer_reveals: "Lengthening cycle time often signals qualification gaps — deals entering the pipeline that shouldn't, or deals stalling without a systematic escalation.",
    category: "need",
  },
  {
    id: "dq-fin-009",
    persona: "finance",
    question: "How does your finance team currently calculate the cost of carrying pipeline that doesn't convert — AEs' time, management overhead, travel, proposals?",
    why_it_matters: "Most Finance teams have not quantified this cost — surfacing it creates the ROI context for Profit+.",
    good_answer_reveals: "If they haven't calculated it, the number is likely larger than they expect and creates a compelling ROI framing.",
    category: "budget",
  },
  {
    id: "dq-fin-010",
    persona: "finance",
    question: "Who in your organization makes the final call on what goes into the committed pipeline number — and what does that person rely on to make that judgment?",
    why_it_matters: "Identifies the authority structure and whether forecast integrity is centralized or distributed.",
    good_answer_reveals: "If a single person is judgment-calling the number with limited structured data, the forecast is fragile and personality-dependent.",
    category: "authority",
  },
  {
    id: "dq-fin-011",
    persona: "finance",
    question: "What process does your organization use to determine when a deal should be removed from the active pipeline — and who makes that call?",
    why_it_matters: "Stale deal removal discipline is one of the most direct indicators of pipeline hygiene.",
    good_answer_reveals: "Organizations without a systematic removal process accumulate phantom pipeline — and the longer it accumulates, the more distorted the forecast.",
    category: "pain",
  },
  {
    id: "dq-fin-012",
    persona: "finance",
    question: "How much visibility does your finance team have into deal-level pipeline activity between formal pipeline reviews — and is that visibility structured or informal?",
    why_it_matters: "Finance teams with no mid-cycle deal visibility are always reacting to surprises rather than managing them.",
    good_answer_reveals: "Informal or zero mid-cycle visibility means finance is flying blind until the structured review, which limits their ability to adjust plans.",
    category: "need",
  },
  {
    id: "dq-fin-013",
    persona: "finance",
    question: "When you model revenue scenarios for the next twelve months, what assumptions do you make about your sales team's conversion rate from pipeline to close — and how confident are you in those assumptions?",
    why_it_matters: "Conversion rate assumptions are a core input to financial modeling; if they're wrong, every downstream plan is wrong.",
    good_answer_reveals: "Low confidence in conversion assumptions suggests the underlying pipeline data is too unreliable to model from.",
    category: "budget",
  },
  {
    id: "dq-fin-014",
    persona: "finance",
    question: "Has there been a situation in the last year where a deal that was in your committed pipeline was lost or significantly delayed — and looking back, what were the early warning signs that weren't surfaced in time?",
    why_it_matters: "Retrospective analysis reveals specific failure modes in the current pipeline intelligence process.",
    good_answer_reveals: "Almost always, the answer involves signals that existed but were not structured or surfaced in time — exactly the gap Profit+ is designed to close.",
    category: "pain",
  },
  {
    id: "dq-fin-015",
    persona: "finance",
    question: "How is your current CRM implementation performing in terms of data quality — and who is responsible for maintaining the standards of what gets entered and when?",
    why_it_matters: "CRM data quality governance is a prerequisite for reliable pipeline intelligence.",
    good_answer_reveals: "Weak data governance (everyone accountable means no one is accountable) creates a foundation for all downstream pipeline problems.",
    category: "need",
  },
  {
    id: "dq-fin-016",
    persona: "finance",
    question: "How does your organization currently measure sales productivity — and is there a metric that connects selling time or cost to pipeline quality, not just closed revenue?",
    why_it_matters: "Pipeline quality-adjusted productivity metrics are more predictive than closed revenue alone.",
    good_answer_reveals: "Organizations measuring only closed revenue miss the efficiency component — how much it cost to generate each closed deal.",
    category: "need",
  },
  {
    id: "dq-fin-017",
    persona: "finance",
    question: "If your sales forecast improved by fifteen to twenty percent in accuracy, what downstream decisions in your organization would change — and what would be the impact?",
    why_it_matters: "Makes the value of forecast accuracy tangible by connecting it to specific business decisions.",
    good_answer_reveals: "Identifying specific decisions that would change (hiring, capex, investor reporting) makes the ROI concrete and personal.",
    category: "need",
  },
  {
    id: "dq-fin-018",
    persona: "finance",
    question: "How does your board or PE sponsor evaluate the credibility of your pipeline — do they apply their own discount to your numbers, and if so, what does that tell you?",
    why_it_matters: "External discounting of internal pipeline numbers signals a credibility gap that needs structural solution.",
    good_answer_reveals: "If investors are applying a 25 percent haircut to pipeline, the organization is functionally acknowledging a forecast accuracy problem at the highest level.",
    category: "pain",
  },
  {
    id: "dq-fin-019",
    persona: "finance",
    question: "When you're evaluating whether to add a new sales territory, product line, or channel, what pipeline intelligence do you use to make that case — and how confident are you in that data?",
    why_it_matters: "Strategic investment decisions built on unreliable pipeline data produce systematically poor outcomes.",
    good_answer_reveals: "Low confidence in pipeline data for strategic decisions reveals a broader finance impact beyond just Q-end variance.",
    category: "need",
  },
  {
    id: "dq-fin-020",
    persona: "finance",
    question: "Have you ever had a situation where your CRM showed strong pipeline for a specific segment or territory, but the on-the-ground reality was very different — and what did that cost you?",
    why_it_matters: "Real examples of CRM-reality gaps create the most powerful case for structured deal intelligence.",
    good_answer_reveals: "A specific example with a cost quantification makes the problem tangible and moves the conversation from abstract to urgent.",
    category: "pain",
  },
  {
    id: "dq-fin-021",
    persona: "finance",
    question: "What is the process for reconciling pipeline data between your CRM and your ERP or financial planning system — and where are the gaps in that reconciliation?",
    why_it_matters: "System reconciliation gaps create finance risk, especially for revenue recognition and backlog reporting.",
    good_answer_reveals: "Manual reconciliation processes or known gaps are direct evidence that CRM pipeline data is not trusted at the finance system level.",
    category: "pain",
  },
  {
    id: "dq-fin-022",
    persona: "finance",
    question: "How would you describe your organization's current discipline around deal stage definitions — does everyone on the sales team apply the same criteria to move a deal from one stage to the next?",
    why_it_matters: "Inconsistent stage definitions are the root cause of most pipeline inflation problems.",
    good_answer_reveals: "Inconsistent stage criteria mean that a deal in Stage 3 for one AE may be at Stage 1 quality for another — systematically distorting the pipeline.",
    category: "pain",
  },
  {
    id: "dq-fin-023",
    persona: "finance",
    question: "In your experience, what percentage of deals that enter your pipeline as qualified eventually close — and how does that compare to what your AEs would tell you?",
    why_it_matters: "The gap between finance's actual close rate and AE expectations reveals qualification discipline.",
    good_answer_reveals: "A significant gap (e.g., finance sees 20 percent close rate, AEs claim 40 percent) signals systematic over-qualification or optimism bias.",
    category: "pain",
  },
  {
    id: "dq-fin-024",
    persona: "finance",
    question: "When you bring in a new CFO or VP Finance, how long does it typically take that person to trust the pipeline number they're inheriting — and what do they do to build that trust?",
    why_it_matters: "Onboarding time for pipeline trust reveals the informal, undocumented nature of most pipeline qualification processes.",
    good_answer_reveals: "Long onboarding periods for pipeline trust (three-plus months) signal that the process isn't documented or systematic enough to hand off cleanly.",
    category: "pain",
  },
  {
    id: "dq-fin-025",
    persona: "finance",
    question: "How does your organization use pipeline data in compensation modeling — and is there any mechanism to ensure that what gets comp'd reflects deals that were genuinely qualified?",
    why_it_matters: "Comp structures that reward pipeline entry without qualification discipline create perverse incentives for pipeline inflation.",
    good_answer_reveals: "Comp programs tied to pipeline activity without quality gates create structural incentives for AEs to inflate the pipeline — a root-cause problem.",
    category: "need",
  },
  {
    id: "dq-fin-026",
    persona: "finance",
    question: "What is your current cadence for formal pipeline reviews with sales leadership, and what format do those reviews take?",
    why_it_matters: "Review cadence and format reveal how current and structured pipeline intelligence is.",
    good_answer_reveals: "Infrequent reviews or reviews that rely on verbal AE updates rather than structured data indicate low pipeline intelligence maturity.",
    category: "need",
  },
  {
    id: "dq-fin-027",
    persona: "finance",
    question: "If you were to describe your biggest frustration with your current pipeline reporting process in one sentence, what would it be?",
    why_it_matters: "Open frustration question surfaces the most emotionally resonant pain point in the briefest way.",
    good_answer_reveals: "The answer will point directly to the specific failure mode in the current process — forecast variance, data quality, AE credibility, or reconciliation burden.",
    category: "pain",
  },
  {
    id: "dq-fin-028",
    persona: "finance",
    question: "Who in your leadership team is most vocal about the inadequacy of your current pipeline process — and what change have they been pushing for?",
    why_it_matters: "Internal champions and internal frustrations are the most reliable indicators of organizational readiness.",
    good_answer_reveals: "An existing internal champion who has been pushing for process change creates a natural deployment partner within the organization.",
    category: "authority",
  },
  {
    id: "dq-fin-029",
    persona: "finance",
    question: "When you think about the technology decisions your organization has made in the past two years around sales and revenue, which ones delivered on their promise — and which didn't?",
    why_it_matters: "Technology history reveals what drove adoption versus failure, and how skeptical the organization is likely to be.",
    good_answer_reveals: "Technologies that failed tend to have failed for adoption, data quality, or change management reasons — all failure modes Profit+ is specifically designed to avoid.",
    category: "competition",
  },
  {
    id: "dq-fin-030",
    persona: "finance",
    question: "What would a successful pipeline intelligence initiative look like at your organization twelve months from now — what would be different?",
    why_it_matters: "Vision question reveals what success looks like to this buyer and what specific outcomes matter most.",
    good_answer_reveals: "Specific success metrics (forecast accuracy percentage, hours saved, headcount decisions improved) create a shared definition of value.",
    category: "need",
  },
  {
    id: "dq-fin-031",
    persona: "finance",
    question: "Is there a timeline pressure driving any evaluation of pipeline tools right now — an upcoming board review, a new CFO, an audit cycle, or a budget planning process?",
    why_it_matters: "Timeline pressure creates urgency and reveals decision windows.",
    good_answer_reveals: "A specific upcoming event (board review in 60 days, new CFO in 30 days) creates natural urgency that the sales process should align to.",
    category: "timeline",
  },
  {
    id: "dq-fin-032",
    persona: "finance",
    question: "How would a decision to deploy a cost-optimization platform like Profit+ be evaluated and approved in your organization — and who would be in the room for that conversation?",
    why_it_matters: "Reveals the full buying committee and decision process, including potential blockers.",
    good_answer_reveals: "If CFO, CRO, and IT are all required, the sales motion needs to address all three audiences — and the IT path needs to be smooth.",
    category: "decision_process",
  },
  {
    id: "dq-fin-033",
    persona: "finance",
    question: "How does your organization currently handle deals that were once qualified but have since lost momentum — is there a formal deprioritization or re-qualification process?",
    why_it_matters: "Re-qualification processes reveal the organization's ability to manage pipeline hygiene proactively.",
    good_answer_reveals: "No formal deprioritization process means the pipeline grows without shrinking, creating an increasingly unreliable forecast baseline.",
    category: "pain",
  },
  {
    id: "dq-fin-034",
    persona: "finance",
    question: "How much of your finance team's time is consumed each quarter on pipeline-related work — and do you consider that allocation appropriate given the quality of the output?",
    why_it_matters: "Time investment versus quality of output is the clearest framing of the efficiency cost of poor pipeline intelligence.",
    good_answer_reveals: "High time investment with low-quality output makes the ROI case for Profit+ directly — time recaptured multiplied by the hourly cost of Finance staff.",
    category: "budget",
  },
  {
    id: "dq-fin-035",
    persona: "finance",
    question: "In a planning scenario where your sales team misses its number by twenty percent — which happens — how does that impact your ability to meet commitments to investors, creditors, or operating partners?",
    why_it_matters: "Downstream impact of forecast miss creates urgency by connecting revenue variance to financial covenant and investor relationship risk.",
    good_answer_reveals: "Specific downstream commitments at risk (covenant thresholds, investor reporting, capex decisions) make the forecast accuracy problem existential rather than operational.",
    category: "need",
  },
  {
    id: "dq-fin-036",
    persona: "finance",
    question: "How transparent is your sales leadership about deal quality when they're presenting pipeline — and is there a pattern of optimism that you've learned to adjust for?",
    why_it_matters: "Structural optimism from sales leadership is one of the most common and most correctable causes of forecast error.",
    good_answer_reveals: "If finance has learned to apply a standard discount to sales pipeline presentations, that discount is implicit evidence that the current process produces unreliable data.",
    category: "pain",
  },
  {
    id: "dq-fin-037",
    persona: "finance",
    question: "When you think about the organizations in your space that are gaining competitive ground, what do you believe they're doing differently from a revenue operations standpoint?",
    why_it_matters: "Competitive intelligence question that surfaces what the prospect believes drives revenue performance.",
    good_answer_reveals: "Attribution of competitor success to process or technology discipline creates a peer pressure framing that makes the Profit+ conversation more relevant.",
    category: "competition",
  },
  {
    id: "dq-fin-038",
    persona: "finance",
    question: "What is the actual process your sales managers use to prepare for pipeline review meetings — and is there a common framework they apply to evaluate deal health before presenting?",
    why_it_matters: "Manager preparation discipline is a proxy for the quality of pipeline intelligence entering the review.",
    good_answer_reveals: "Inconsistent manager preparation — some do it rigorously, some wing it — means pipeline reviews produce inconsistent outputs that finance can't rely on.",
    category: "pain",
  },
  {
    id: "dq-fin-039",
    persona: "finance",
    question: "Is there a specific deal type, segment, or territory where your pipeline accuracy is consistently worse — and do you have a hypothesis about why?",
    why_it_matters: "Segmented savings accuracy problems reveal specific failure modes that Profit+ can address.",
    good_answer_reveals: "Systematic underperformance in a specific segment often traces back to qualification process gaps or AE experience in that segment — both addressable.",
    category: "pain",
  },
  {
    id: "dq-fin-040",
    persona: "finance",
    question: "If you could change one thing about how your sales team communicates pipeline health to the finance team, what would it be?",
    why_it_matters: "Desired communication change reveals the precise interface problem between sales and finance — and what structured data would resolve it.",
    good_answer_reveals: "The most common answers (more consistency, more data, earlier warning) map directly to Profit+ capabilities.",
    category: "pain",
  },

  // ─── OPERATIONS PERSONA (40 questions) ──────────────────────────────────

  {
    id: "dq-ops-001",
    persona: "operations",
    question: "When you look at your sales team's capacity allocation — the deals they are actively working — what percentage do you believe represents genuinely winnable business, and what gives you confidence in that estimate?",
    why_it_matters: "Establishes whether operations has visibility into selling capacity quality, not just quantity.",
    good_answer_reveals: "Uncertainty in this answer reveals a gap in savings-program visibility at the operations level — the entry point for the Profit+ conversation.",
    category: "pain",
  },
  {
    id: "dq-ops-002",
    persona: "operations",
    question: "How does your organization currently track the elapsed time between key milestones in your sales process — and do you have a clear view of where in the cycle the most time is being lost?",
    why_it_matters: "Cycle time visibility is a core operations metric that most CRMs report poorly.",
    good_answer_reveals: "If the organization can't identify where cycle time is being lost, they can't reduce it — which is a direct performance improvement opportunity.",
    category: "pain",
  },
  {
    id: "dq-ops-003",
    persona: "operations",
    question: "When a senior AE leaves your organization, what is the process for ensuring that the deals they were working, and the context behind them, transfers effectively to the next AE?",
    why_it_matters: "Deal handoff quality reveals how much intelligence lives in individuals versus systems.",
    good_answer_reveals: "If the handoff process relies on meeting notes, AE briefings, or CRM data alone, significant institutional knowledge is being lost — and that loss is quantifiable.",
    category: "pain",
  },
  {
    id: "dq-ops-004",
    persona: "operations",
    question: "How consistent is the sales qualification process across your team — when two different AEs evaluate the same type of deal, are they applying the same criteria?",
    why_it_matters: "Process consistency is foundational to pipeline reliability and sales performance predictability.",
    good_answer_reveals: "Inconsistent qualification criteria mean that pipeline stage means different things to different AEs, making aggregate pipeline data unreliable.",
    category: "pain",
  },
  {
    id: "dq-ops-005",
    persona: "operations",
    question: "What is your current process for identifying and removing stale deals from the active pipeline — and how long does a deal typically sit without meaningful activity before it gets flagged?",
    why_it_matters: "Stale deal discipline is a direct indicator of pipeline health management maturity.",
    good_answer_reveals: "Long or undefined stale thresholds allow phantom pipeline to accumulate — deals that aren't dead but aren't alive, consuming roster capacity without moving.",
    category: "pain",
  },
  {
    id: "dq-ops-006",
    persona: "operations",
    question: "From an operations standpoint, what is the fully loaded cost — AE time, management time, support resources — when a deal that was never going to close makes it all the way to a final proposal?",
    why_it_matters: "Quantifying the cost of a poorly qualified or unverified initiative creates the ROI foundation for Profit+.",
    good_answer_reveals: "When the prospect calculates this (most haven't), the number is typically larger than expected and creates immediate financial urgency.",
    category: "budget",
  },
  {
    id: "dq-ops-007",
    persona: "operations",
    question: "How do you currently manage the balance between allowing AEs enough time to develop deals and cutting losses on deals that aren't progressing — and what signals drive that decision?",
    why_it_matters: "Decision rules for deal deprioritization reveal whether the process is systematic or judgment-based.",
    good_answer_reveals: "If the decision is manager judgment-based without structured criteria, it's inconsistent — some managers cut too early, others too late.",
    category: "pain",
  },
  {
    id: "dq-ops-008",
    persona: "operations",
    question: "When you think about the AEs on your team with the most consistent performance, what do they do differently in the way they manage and qualify their pipeline?",
    why_it_matters: "Identifying what top performers do differently is the basis for process systematization.",
    good_answer_reveals: "Top-performer behaviors that are not yet systematized represent a direct leverage opportunity — encode their process into a tool that every AE uses.",
    category: "need",
  },
  {
    id: "dq-ops-009",
    persona: "operations",
    question: "How does your operations team currently measure sales process compliance — the degree to which AEs are following the methodology rather than freelancing?",
    why_it_matters: "Methodology compliance measurement is a prerequisite for scaling a repeatable sales process.",
    good_answer_reveals: "No systematic compliance measurement means the organization can't distinguish between AE discipline and market conditions as drivers of performance variance.",
    category: "need",
  },
  {
    id: "dq-ops-010",
    persona: "operations",
    question: "When you look at your pipeline at the beginning of a quarter versus what actually closes, where is the biggest gap — are deals being lost, stalling, or simply disappearing from the pipeline without explanation?",
    why_it_matters: "Understanding failure mode type (lost, stalled, or ghost) identifies the specific pipeline process breakdown.",
    good_answer_reveals: "Ghost deals (disappear without a clear lost reason) indicate AE behavior issues and data quality problems; lost deals indicate qualification failures; stalled deals indicate pipeline development discipline gaps.",
    category: "pain",
  },
  {
    id: "dq-ops-011",
    persona: "operations",
    question: "How many active deals is a typical AE in your organization managing at any given time — and do you believe that number reflects an appropriate focus, too much spread, or insufficient pipeline?",
    why_it_matters: "Deal load per AE is a proxy for focus quality — too many deals means no deal gets the attention it needs.",
    good_answer_reveals: "High deal loads with no quality filter means AEs are spreading effort too thin across a pipeline that hasn't been triaged for real probability.",
    category: "need",
  },
  {
    id: "dq-ops-012",
    persona: "operations",
    question: "In your experience, how much of the variance in individual AE performance is attributable to skill or market advantage versus the quality of their pipeline management process?",
    why_it_matters: "Attribution of performance variance guides the solution — if it's mostly process, a systematic process solution has high ROI.",
    good_answer_reveals: "If the prospect believes significant performance variance is process-driven, they're already conceptually aligned with the value of a systematic pipeline intelligence tool.",
    category: "need",
  },
  {
    id: "dq-ops-013",
    persona: "operations",
    question: "What role does your operations team play in the sales forecasting process — are you a consumer of the forecast, a contributor to it, or responsible for its accuracy?",
    why_it_matters: "Operations' role in forecasting determines their stake in improving its accuracy.",
    good_answer_reveals: "If operations is responsible for savings accuracy but lacks the data infrastructure to ensure it, they are a natural buyer for Profit+.",
    category: "authority",
  },
  {
    id: "dq-ops-014",
    persona: "operations",
    question: "How does your organization handle the situation where an AE is working a deal that the manager believes should be deprioritized, but the AE is reluctant to let go — what's the escalation path?",
    why_it_matters: "Organizational dynamics around deal deprioritization reveal management culture and process discipline.",
    good_answer_reveals: "Weak escalation paths lead to zombie deals in the pipeline — deals that are effectively dead but that no one will officially remove.",
    category: "pain",
  },
  {
    id: "dq-ops-015",
    persona: "operations",
    question: "When you look across your sales regions or territories, how consistent is performance — and when there's variance, what's your hypothesis about what's driving it?",
    why_it_matters: "Intra-organization performance variance is a diagnostic signal for process consistency or market advantage gaps.",
    good_answer_reveals: "Process-explained variance is an opportunity to systematize the high-performer approach; market-explained variance requires different interventions.",
    category: "need",
  },
  {
    id: "dq-ops-016",
    persona: "operations",
    question: "How does your team currently handle the transition between a deal that is in the sales pipeline and a deal that is moving into contract or delivery — and where do handoff problems occur?",
    why_it_matters: "Sales-to-delivery handoff failures often trace back to deals that were improperly qualified from the start.",
    good_answer_reveals: "Frequent delivery problems that trace back to oversold or miscalibrated deals indicate a qualification discipline gap that costs more than the pipeline variance.",
    category: "pain",
  },
  {
    id: "dq-ops-017",
    persona: "operations",
    question: "What would it mean operationally if your sales cycle shortened by fifteen to twenty percent — which activities would change and what capacity would that free up?",
    why_it_matters: "Forces the prospect to quantify the operational value of cycle time reduction.",
    good_answer_reveals: "Specific capacity and resource implications (e.g., same team handles more volume, or headcount plan changes) make the cycle time improvement concrete and valuable.",
    category: "need",
  },
  {
    id: "dq-ops-018",
    persona: "operations",
    question: "How does your organization currently gather and act on competitive intelligence during the sales process — and is that process consistent across the team?",
    why_it_matters: "Competitive intelligence discipline is a signal for overall sales process maturity.",
    good_answer_reveals: "Inconsistent competitive intelligence gathering means some AEs are navigating competitive situations blind — a qualification and risk assessment gap.",
    category: "competition",
  },
  {
    id: "dq-ops-019",
    persona: "operations",
    question: "When you think about the deals your team lost in the last two quarters that you were confident were going to close — what were the failure modes and how early could those failures have been detected?",
    why_it_matters: "Retrospective deal loss analysis surfaces the specific types of intelligence that would have changed the outcome.",
    good_answer_reveals: "Early detectable signals that were not surfaced (e.g., budget freeze, project halt, program leadership change) map directly to the risk monitoring Profit+ surfaces through lifecycle tracking.",
    category: "pain",
  },
  {
    id: "dq-ops-020",
    persona: "operations",
    question: "How does your organization currently measure whether your sales methodology is being applied — is there a way to see, at the deal level, which elements of your process were completed?",
    why_it_matters: "Methodology audit capability is a foundation for performance coaching and process improvement.",
    good_answer_reveals: "No deal-level methodology visibility means coaching is based on outcomes (won/lost) rather than process (did they complete the right steps), which is reactive rather than preventive.",
    category: "need",
  },
  {
    id: "dq-ops-021",
    persona: "operations",
    question: "How does headcount planning for your sales organization relate to pipeline forecast — is next year's hiring plan grounded in a view of pipeline capacity and conversion assumptions?",
    why_it_matters: "Headcount planning quality is a downstream consequence of pipeline intelligence quality.",
    good_answer_reveals: "Hiring plans based on unreliable pipeline forecasts create systematic over- or under-investment in sales capacity.",
    category: "budget",
  },
  {
    id: "dq-ops-022",
    persona: "operations",
    question: "What is the most operationally disruptive thing that happens when a large deal that was in the committed pipeline falls out unexpectedly?",
    why_it_matters: "Operational disruption from forecast misses is often larger than the revenue impact — it affects hiring, delivery capacity, investor communications.",
    good_answer_reveals: "Specific operational disruptions (delivery ramp reversed, headcount offer rescinded, investor call rescheduled) make the cost of forecast miss concrete and personal.",
    category: "pain",
  },
  {
    id: "dq-ops-023",
    persona: "operations",
    question: "How does your organization approach the scaling question when an AE who has been a top performer is promoted into a management role — and what happens to process consistency during that transition?",
    why_it_matters: "Rep-to-manager transition is one of the most common sources of process knowledge loss.",
    good_answer_reveals: "If the organization promotes based on performance but doesn't systematize the performer's process before the promotion, they lose both the performer and the process.",
    category: "need",
  },
  {
    id: "dq-ops-024",
    persona: "operations",
    question: "When your leadership team talks about improving sales performance, is the conversation more often about talent — hire better people — or process — build a better system?",
    why_it_matters: "The talent-vs-process attribution is a proxy for organizational readiness to adopt a systematic tool.",
    good_answer_reveals: "Organizations that attribute performance primarily to talent are harder to sell process tools to; those that attribute it to process are naturally aligned with Profit+'s value proposition.",
    category: "authority",
  },
  {
    id: "dq-ops-025",
    persona: "operations",
    question: "How does your operations team currently interface with your CRM data — are you building reports and analytics on top of it, or is the CRM largely owned and managed by the sales team?",
    why_it_matters: "Operations' CRM interface determines how much analytical capability is currently applied to pipeline data.",
    good_answer_reveals: "Operations teams without structured savings analytics means no systematic verification is being applied — a direct Profit+ opportunity.",
    category: "need",
  },
  {
    id: "dq-ops-026",
    persona: "operations",
    question: "What is your current process for ensuring that the deals coming into your pipeline are genuine — that they meet a real qualification bar before resources are committed to developing them?",
    why_it_matters: "Front-end qualification discipline is the most cost-effective place to improve pipeline quality.",
    good_answer_reveals: "Weak front-end qualification (AEs self-qualify, no systematic review) means the pipeline fills with deals that shouldn't be there — and the cost compounds as they age.",
    category: "pain",
  },
  {
    id: "dq-ops-027",
    persona: "operations",
    question: "If you were to redesign your pipeline review process from scratch, what would you keep, what would you eliminate, and what information would you add that you don't currently have?",
    why_it_matters: "Design-from-scratch question reveals the prospect's vision for an ideal savings-tracking system — and maps directly to Profit+ capabilities.",
    good_answer_reveals: "The gap between current process and desired process is the exact value proposition to present — Profit+ delivers what they would design if they could start over.",
    category: "need",
  },
  {
    id: "dq-ops-028",
    persona: "operations",
    question: "How does your organization currently handle the situation where a new AE takes over an account that was previously managed by a high performer — what happens to that account's win probability?",
    why_it_matters: "Account continuity and AE transition quality reveal institutional knowledge management gaps.",
    good_answer_reveals: "Significant win probability drop on account transitions indicates that deal success is AE-dependent rather than process-dependent — a structural fragility.",
    category: "pain",
  },
  {
    id: "dq-ops-029",
    persona: "operations",
    question: "What is your organization's current sales-to-operations handoff process, and where are the most common misalignments between what sales committed to and what the delivery team can actually produce?",
    why_it_matters: "Sales-ops misalignment is often rooted in qualification problems — deals that were never properly scoped or validated before commitment.",
    good_answer_reveals: "Systematic misalignment (delivery always surprised by what sales sold) indicates that the sales qualification process is not capturing the right intelligence before commitment.",
    category: "pain",
  },
  {
    id: "dq-ops-030",
    persona: "operations",
    question: "How often does your leadership team debate the same pipeline deals in multiple review cycles — and what does it tell you that those debates recur without resolution?",
    why_it_matters: "Recurring pipeline debates signal that the same deals lack clear qualification criteria — no one can definitively say yes or no based on available data.",
    good_answer_reveals: "Recurring savings debates are the most visible symptom of a verification framework gap — Profit+'s structured lifecycle and G/L verification eliminates most of them.",
    category: "pain",
  },
  {
    id: "dq-ops-031",
    persona: "operations",
    question: "When your organization evaluates new sales technology, who is involved in the decision, and what are the criteria that tend to drive the final recommendation?",
    why_it_matters: "Decision process and criteria reveal buying committee structure and what the winning argument needs to be.",
    good_answer_reveals: "Operations-weighted buying committees tend to evaluate on process fit and adoption risk; finance-weighted committees evaluate on ROI; IT-weighted committees evaluate on integration and security.",
    category: "decision_process",
  },
  {
    id: "dq-ops-032",
    persona: "operations",
    question: "What is your timeline pressure for improving pipeline intelligence — is this a current priority or something that will need to wait for a different planning cycle?",
    why_it_matters: "Timeline qualification determines urgency and decision window.",
    good_answer_reveals: "A current priority with a named timeline is a qualified deal; a future consideration is a pipeline nurture — both require different sales motions.",
    category: "timeline",
  },
  {
    id: "dq-ops-033",
    persona: "operations",
    question: "How does your organization currently use pipeline velocity data — close rate trends, deal aging, time-in-stage — to inform operational decisions?",
    why_it_matters: "Velocity data usage reveals analytics maturity and whether the organization is already thinking in the direction Profit+ enables.",
    good_answer_reveals: "Low velocity data usage indicates untapped analytical capability; high usage with poor data quality indicates a foundation improvement opportunity.",
    category: "need",
  },
  {
    id: "dq-ops-034",
    persona: "operations",
    question: "What is your view on the quality of your current CRM implementation — does it reflect the actual state of your pipeline, or has it drifted from reality over time?",
    why_it_matters: "CRM drift is one of the most common and most damaging pipeline intelligence problems.",
    good_answer_reveals: "Acknowledged system drift is a direct admission that the current tool is not trustworthy — which positions Profit+ as the corrective platform.",
    category: "pain",
  },
  {
    id: "dq-ops-035",
    persona: "operations",
    question: "How does your organization currently identify which accounts in your pipeline have the highest strategic value — and does that prioritization actually influence how AE time is allocated?",
    why_it_matters: "Account prioritization discipline determines whether team members are working the right deals.",
    good_answer_reveals: "If prioritization is informal or doesn't effectively influence time allocation, AE effort is distributed by default rather than by strategy.",
    category: "need",
  },
  {
    id: "dq-ops-036",
    persona: "operations",
    question: "When you're managing capacity for a quarter, what are the leading indicators you rely on to predict whether the team will hit their number — and how reliable have those indicators been?",
    why_it_matters: "Leading indicator reliability is the core of predictable revenue operations.",
    good_answer_reveals: "Unreliable leading indicators (which most organizations have) mean capacity planning is reactive rather than predictive — a fundamental ops weakness.",
    category: "pain",
  },
  {
    id: "dq-ops-037",
    persona: "operations",
    question: "If you could see a real-time score for every active deal in your pipeline — a score that reflected the quality of the qualification, the depth of stakeholder engagement, and the risk of the deal stalling — how would that change how you manage your team?",
    why_it_matters: "Vision question that makes the Profit+ value proposition concrete and personal to the operations leader.",
    good_answer_reveals: "A strong positive reaction to this framing indicates the prospect understands the value and is already imagining the use case — a buying signal.",
    category: "need",
  },
  {
    id: "dq-ops-038",
    persona: "operations",
    question: "What is your biggest operational concern about the next twelve months from a revenue execution standpoint — not pipeline volume, but execution quality?",
    why_it_matters: "Execution quality concern is the most direct path to the Profit+ value proposition.",
    good_answer_reveals: "Any execution quality concern (process consistency, manager visibility, idea lifecycle gaps) maps directly to what Profit+ is designed to address.",
    category: "pain",
  },
  {
    id: "dq-ops-039",
    persona: "operations",
    question: "How does your organization currently manage competitive intelligence at the deal level — do team members have a consistent way to assess competitive risk on each opportunity?",
    why_it_matters: "Deal-level competitive intelligence is a qualification gate that most organizations handle inconsistently.",
    good_answer_reveals: "No systematic competitive assessment at the deal level means deals can advance through the pipeline with unacknowledged competitive risk — a late-stage loss risk.",
    category: "competition",
  },
  {
    id: "dq-ops-040",
    persona: "operations",
    question: "If you had to choose between adding two more sales team members and improving the quality of execution of your current team by twenty percent — which would you choose, and why?",
    why_it_matters: "This choice question reveals whether the prospect is resource-focused or process-focused — critical for framing the Profit+ value proposition.",
    good_answer_reveals: "A preference for execution improvement over headcount addition is a direct alignment with Profit+'s value — the platform improves execution discipline across the existing team.",
    category: "need",
  },

  // ─── HR PERSONA (40 questions) ───────────────────────────────────────────

  {
    id: "dq-hr-001",
    persona: "hr",
    question: "When an AE leaves your organization, what is your current process for capturing and transferring the deal intelligence they had — and how much of that knowledge do you believe is actually retained?",
    why_it_matters: "Process knowledge retention after a team member departs is a core human capital problem that Profit+ addresses by systematizing what individuals know.",
    good_answer_reveals: "Low retention confidence directly quantifies the knowledge loss problem and creates an ROI basis for a systematic solution.",
    category: "pain",
  },
  {
    id: "dq-hr-002",
    persona: "hr",
    question: "What is your organization's current time-to-productivity for new sales hires — from start date to first independent close — and how does that compare to what you budgeted for in the hiring plan?",
    why_it_matters: "Ramp time is a quantifiable talent investment metric with direct cost and ROI implications.",
    good_answer_reveals: "If actual ramp time exceeds planned ramp time, there is a concrete and quantifiable investment efficiency problem that Profit+ can address.",
    category: "pain",
  },
  {
    id: "dq-hr-003",
    persona: "hr",
    question: "How do your sales managers currently know when an AE is struggling — what are the signals they look for, and how early do those signals typically appear?",
    why_it_matters: "Early warning signal quality determines whether underperformance is caught in time for a developmental intervention or only after it's a termination scenario.",
    good_answer_reveals: "Late-stage signal detection (Q-end miss rather than mid-quarter deal risk) means the coaching window is already closed — a development and retention problem.",
    category: "pain",
  },
  {
    id: "dq-hr-004",
    persona: "hr",
    question: "In your experience, what percentage of AE terminations in the last two years were performance-related versus voluntary departures — and for the voluntary departures, what do exit interviews suggest were the primary drivers?",
    why_it_matters: "Voluntary departure reasons are the most accurate indicator of the engagement and process quality problems driving attrition.",
    good_answer_reveals: "Process frustration, poor management visibility, and lack of structured support are the most common voluntary departure drivers — all of which Profit+ addresses structurally.",
    category: "pain",
  },
  {
    id: "dq-hr-005",
    persona: "hr",
    question: "How does your organization currently evaluate the effectiveness of sales manager development programs — what metrics do you use and how do those metrics connect to revenue outcomes?",
    why_it_matters: "Manager development ROI measurement reveals whether HR is connecting talent investment to revenue performance.",
    good_answer_reveals: "Weak or absent connection between manager development and revenue outcomes indicates that HR's talent investment lacks a performance feedback loop.",
    category: "need",
  },
  {
    id: "dq-hr-006",
    persona: "hr",
    question: "What does your organization's new hire onboarding for sales team members focus on — and how is deal qualification and pipeline management methodology transmitted to new team members?",
    why_it_matters: "Onboarding quality for methodology transmission determines how quickly new team members can execute independently.",
    good_answer_reveals: "Informal methodology transfer (shadowing, classroom, CRM data review) without a live tool to work from creates a knowledge gap that extends the ramp period.",
    category: "pain",
  },
  {
    id: "dq-hr-007",
    persona: "hr",
    question: "How does your organization currently structure the coaching cadence between sales managers and their direct reports — and is that coaching based on deal-level intelligence or on pipeline summary data?",
    why_it_matters: "Coaching quality is determined by the specificity and timeliness of the information managers have.",
    good_answer_reveals: "Summary-level coaching (pipeline review meeting) rather than deal-level coaching (specific deal risk, specific qualification gap) is generically helpful but not performance-changing.",
    category: "need",
  },
  {
    id: "dq-hr-008",
    persona: "hr",
    question: "When you look at your top-performing sales team members versus the median performers, what specifically are the top performers doing in their pipeline management process that others are not?",
    why_it_matters: "Top-performer differentiation reveals which behaviors, if systematized, would lift median performance.",
    good_answer_reveals: "Top-performer behaviors that are identified but not yet systematized represent a direct leverage opportunity — encode their process into a tool that every AE uses.",
    category: "need",
  },
  {
    id: "dq-hr-009",
    persona: "hr",
    question: "How does your organization approach the question of equitable performance standards in your sales team — specifically, whether the criteria for advancement and recognition are applied consistently across team members?",
    why_it_matters: "Equitable process standards are a retention, engagement, and DEI issue in sales organizations.",
    good_answer_reveals: "Inconsistent standards (advancement based on manager relationships or visibility rather than objective performance) are a retention risk for underrepresented or newer team members.",
    category: "need",
  },
  {
    id: "dq-hr-010",
    persona: "hr",
    question: "What is the most common reason managers give for placing a sales team member on a performance improvement plan — and is there a pattern in when those plans tend to succeed versus fail?",
    why_it_matters: "PIP patterns reveal whether underperformance is caught early enough and whether the root cause is correctly diagnosed.",
    good_answer_reveals: "PIPs that fail tend to fail because the diagnosis was wrong (individual behavior vs. process gap) or the intervention was too late — both of which structured data from Profit+ helps prevent.",
    category: "pain",
  },
  {
    id: "dq-hr-011",
    persona: "hr",
    question: "How does your organization calculate the fully loaded cost of a sales team member hire — recruiting, onboarding, ramp, opportunity cost of early losses — and has that calculation influenced your hiring strategy?",
    why_it_matters: "Full-cost hiring analysis creates the ROI context for investing in tools that reduce ramp time and attrition.",
    good_answer_reveals: "If the fully loaded cost is known and is substantial, any technology that materially reduces ramp time or attrition has a clear and significant ROI.",
    category: "budget",
  },
  {
    id: "dq-hr-012",
    persona: "hr",
    question: "When your organization promotes a high-performing team member into a sales management role, how do you evaluate whether that person is ready to be a manager — and what are the most common failure modes in that transition?",
    why_it_matters: "Rep-to-manager transition is one of the most costly talent processes when managed poorly.",
    good_answer_reveals: "Common failure modes (high performer with no teachable process; success too personal to replicate; managers lacking the data to coach) map directly to what Profit+ provides: structured, idea-level intelligence for coaching and development.",
    category: "need",
  },
  {
    id: "dq-hr-013",
    persona: "hr",
    question: "How does your talent strategy for the sales function align with your broader organizational growth goals — and where are the biggest capability gaps you're trying to close?",
    why_it_matters: "Capability gap identification reveals what specific talent investments HR is prioritizing — and where Profit+ can accelerate those investments.",
    good_answer_reveals: "Capability gaps in process discipline, lifecycle management, or manager coaching are directly addressable with Profit+ — connecting the HR agenda to the technology solution.",
    category: "need",
  },
  {
    id: "dq-hr-014",
    persona: "hr",
    question: "In your organization, who owns the accountability for sales team member performance — is it primarily the team member, primarily the manager, or is there a shared model?",
    why_it_matters: "Accountability structure determines who is motivated to act on deal-level intelligence and who needs to be part of the buying conversation.",
    good_answer_reveals: "Manager-accountable models need to give managers the data to execute that accountability — which is exactly what Profit+'s idea-level visibility and lifecycle tracking provides.",
    category: "authority",
  },
  {
    id: "dq-hr-015",
    persona: "hr",
    question: "What is your organization's current approach to sales compensation design — and is there a mechanism to reward pipeline quality, not just pipeline volume or closed revenue?",
    why_it_matters: "Comp design that rewards pipeline quality creates aligned incentives for using a pipeline intelligence tool.",
    good_answer_reveals: "Comp structures that reward savings claims without verification create incentives to inflate both volume and value — which Profit+'s structured G/L verification helps counterbalance.",
    category: "need",
  },
  {
    id: "dq-hr-016",
    persona: "hr",
    question: "How does your HR team currently measure whether your investment in sales training and enablement is translating into pipeline performance improvement?",
    why_it_matters: "Training ROI measurement is a critical gap for most HR teams — it's hard to measure without structured pipeline data.",
    good_answer_reveals: "Organizations that can't measure training ROI in pipeline terms are making training investment decisions without feedback — a systematic underinvestment risk.",
    category: "need",
  },
  {
    id: "dq-hr-017",
    persona: "hr",
    question: "When you survey your sales team members on engagement, what themes show up most consistently — and how do those themes translate into the actions leadership takes?",
    why_it_matters: "Engagement survey themes in sales organizations consistently point to process and management quality issues.",
    good_answer_reveals: "Process frustration, management quality, and lack of direction are the most common engagement themes in savings programs — all of which Profit+ structurally addresses.",
    category: "pain",
  },
  {
    id: "dq-hr-018",
    persona: "hr",
    question: "How does your organization currently differentiate between a team member who is underperforming because of skill gaps versus one who is underperforming because of process or support gaps?",
    why_it_matters: "Correct diagnosis of underperformance drives the right intervention — training versus process tool versus manager coaching.",
    good_answer_reveals: "Inability to distinguish between skill and process gaps leads to training interventions for process problems, which is expensive and ineffective.",
    category: "need",
  },
  {
    id: "dq-hr-019",
    persona: "hr",
    question: "What is the typical conversation your HR team has with sales leadership when turnover spikes in the sales function — who takes accountability, and what does the action plan look like?",
    why_it_matters: "Accountability and response patterns for sales attrition reveal organizational readiness to address the structural root causes.",
    good_answer_reveals: "If the action plan focuses on recruiting rather than process improvement, the organization is treating symptoms rather than causes — the most common and expensive mistake.",
    category: "pain",
  },
  {
    id: "dq-hr-020",
    persona: "hr",
    question: "In your organization, how much authority does HR have over the tools and processes used in the sales function — or is technology decision-making entirely owned by sales or operations?",
    why_it_matters: "HR authority over sales and operations technology is a critical factor in determining whether HR can be an effective champion for Profit+.",
    good_answer_reveals: "HR with advisory or veto influence on sales technology decisions is a meaningful champion; HR without that influence needs a sales or ops co-champion to be effective.",
    category: "authority",
  },
  {
    id: "dq-hr-021",
    persona: "hr",
    question: "How does your organization's approach to sales team development differ between field team members, inside team members, and business development team members — and does the technology infrastructure support those different development paths?",
    why_it_matters: "Differentiated development paths reveal whether the organization is sophisticated enough to need role-specific pipeline intelligence.",
    good_answer_reveals: "Organizations with well-differentiated development paths by role are more ready for a structured platform like Profit+ than those with a one-size-fits-all approach.",
    category: "need",
  },
  {
    id: "dq-hr-022",
    persona: "hr",
    question: "When you make a hiring decision for a senior sales manager or director, what do you look for in terms of their ability to coach using data — and how many candidates actually demonstrate that capability?",
    why_it_matters: "Data-based coaching as a hiring criterion reveals whether the organization values and can find managers who will actually use Profit+'s visibility tools.",
    good_answer_reveals: "If data-based coaching is valued but rarely found, the organization will appreciate a tool that makes any manager better at data-based coaching — lowering the talent bar for that capability.",
    category: "need",
  },
  {
    id: "dq-hr-023",
    persona: "hr",
    question: "How does your HR team currently partner with the finance team on workforce planning for the sales function — and where are the biggest gaps in the data those conversations rely on?",
    why_it_matters: "HR-finance partnership quality on sales workforce planning determines the sophistication of talent investment decisions.",
    good_answer_reveals: "HR-Finance partnership gaps (HR does not have savings productivity data, Finance does not have team capability data) are directly addressed by Profit+'s structured lifecycle and verification intelligence.",
    category: "need",
  },
  {
    id: "dq-hr-024",
    persona: "hr",
    question: "In your experience, what is the relationship between sales team culture — specifically how team members collaborate, share intelligence, and support each other — and revenue performance?",
    why_it_matters: "Sales culture and deal intelligence sharing quality are closely connected; a tool that makes intelligence visible to the team rather than siloed in team members improves collaboration.",
    good_answer_reveals: "Organizations that value collaborative improvement cultures will see Profit+ as a culture enabler — shared savings intelligence and lifecycle visibility create the conditions for cross-plant collaboration.",
    category: "need",
  },
  {
    id: "dq-hr-025",
    persona: "hr",
    question: "When you think about the talent investment your organization has made in the sales function over the last three years, what has produced the most durable return — compensation changes, training programs, technology, or structural process changes?",
    why_it_matters: "Investment attribution reveals what the prospect believes drives durable performance improvement.",
    good_answer_reveals: "Attribution to structural process changes is the most direct alignment with Profit+'s value proposition — a systematic platform creates more durable improvement than training or consulting alone.",
    category: "need",
  },
  {
    id: "dq-hr-026",
    persona: "hr",
    question: "How does your organization currently handle the knowledge management challenge of a growing sales team — as you add more team members, how do you ensure that institutional knowledge is accessible to everyone?",
    why_it_matters: "Knowledge management at scale is a structural challenge that becomes more expensive as the team grows.",
    good_answer_reveals: "Organizations without structured knowledge management tools are reinventing the wheel on every new hire — a compounding inefficiency as the team scales.",
    category: "need",
  },
  {
    id: "dq-hr-027",
    persona: "hr",
    question: "What is your current process for identifying high-potential sales team members early — and is there a structured way to accelerate their development before they get frustrated and leave?",
    why_it_matters: "High-potential identification and acceleration are critical for retention in competitive talent markets.",
    good_answer_reveals: "Organizations without structured high-potential development paths lose their best early-career team members to competitors who offer clearer growth paths.",
    category: "need",
  },
  {
    id: "dq-hr-028",
    persona: "hr",
    question: "How does your organization currently prevent best-practice deal qualification knowledge from being concentrated in a small number of people — and what happens to that knowledge when those people leave?",
    why_it_matters: "Concentration of qualification expertise is a structural fragility that most organizations underestimate.",
    good_answer_reveals: "If the organization's deal qualification expertise walks out the door with two or three key people, the business has a structural talent risk that compensation cannot solve.",
    category: "pain",
  },
  {
    id: "dq-hr-029",
    persona: "hr",
    question: "What is your timeline and budget framework for HR-led initiatives in the sales enablement and talent development space for the next twelve months?",
    why_it_matters: "Timeline and budget qualification determines whether this is a current opportunity or a future pipeline.",
    good_answer_reveals: "A named budget and a current planning cycle indicate a qualified opportunity; a future consideration requires a nurture strategy.",
    category: "budget",
  },
  {
    id: "dq-hr-030",
    persona: "hr",
    question: "How does your organization evaluate whether a sales methodology or framework it has invested in is actually being applied in practice — is there any systematic measurement of methodology adherence?",
    why_it_matters: "Methodology adherence measurement is a gap in most organizations — training investment is made without verifying application.",
    good_answer_reveals: "No systematic methodology adherence measurement means the organization can't determine whether its training ROI is real or assumed.",
    category: "need",
  },
  {
    id: "dq-hr-031",
    persona: "hr",
    question: "When you think about the HR technology decisions your organization has made that directly impacted sales performance, which ones had the most meaningful effect — and what made them work?",
    why_it_matters: "Successful technology references reveal what drove adoption and what the organization values in a technology investment.",
    good_answer_reveals: "Technologies that worked tended to integrate into existing workflows, solve a specific and measurable problem, and have manager buy-in — all of which Profit+ is designed to provide.",
    category: "competition",
  },
  {
    id: "dq-hr-032",
    persona: "hr",
    question: "How does your organization currently measure and report on the return on talent investment in the sales function — is there a metric that connects HR programs to revenue outcomes?",
    why_it_matters: "HR ROI measurement for talent investments in the savings function determines whether HR can make a financial case for platforms like Profit+.",
    good_answer_reveals: "HR teams with savings outcome measurement capability are natural buyers; those without that capability are building towards it and Profit+ provides the data foundation.",
    category: "need",
  },
  {
    id: "dq-hr-033",
    persona: "hr",
    question: "What would a successful partnership between HR and sales leadership look like in terms of driving sales performance — and where is the current relationship strongest and weakest?",
    why_it_matters: "HR-sales leadership partnership quality determines HR's ability to influence the tools and processes that drive sales performance.",
    good_answer_reveals: "Strong HR-sales relationships create opportunities for HR to be a champion for Profit+; weak relationships reveal what needs to change before HR can influence the decision.",
    category: "authority",
  },
  {
    id: "dq-hr-034",
    persona: "hr",
    question: "In your organization, what is the leading cause of extended ramp time for new sales hires — is it primarily knowledge gaps, process gaps, relationship-building, or something else?",
    why_it_matters: "Ramp time root cause identification points to the intervention with the highest leverage.",
    good_answer_reveals: "Process gaps (no systematic lifecycle framework, no verification tool) are directly addressable with Profit+; knowledge gaps require different interventions.",
    category: "pain",
  },
  {
    id: "dq-hr-035",
    persona: "hr",
    question: "How does your organization handle the situation where a sales team member who is struggling believes they are performing well — and what is the manager's toolkit for having that conversation with data?",
    why_it_matters: "Data-backed performance conversations are critical for both development and retention — team members who receive clear feedback can improve; those who don't leave or stay unproductive.",
    good_answer_reveals: "Managers without deal-level performance data can't have effective data-backed conversations — they're relying on impressions and outcomes rather than process evidence.",
    category: "need",
  },
  {
    id: "dq-hr-036",
    persona: "hr",
    question: "When you think about the next wave of talent entering your sales team — early career, diverse backgrounds, different learning styles — how does your current onboarding and enablement model serve that population?",
    why_it_matters: "Enablement model fit for next-generation talent reveals whether the current approach can scale to a more diverse team.",
    good_answer_reveals: "Traditional onboarding (shadowing senior team members, reviewing historical data) serves experienced hires better than early-career talent — a systematic platform like Profit+ narrows that gap.",
    category: "need",
  },
  {
    id: "dq-hr-037",
    persona: "hr",
    question: "How does your organization identify and address the risk of a sales team becoming overly dependent on one or two high performers — and what is the resilience strategy if those individuals leave?",
    why_it_matters: "Key-person dependency in a sales team is a structural business risk that HR is well-positioned to address.",
    good_answer_reveals: "Organizations with no key-person risk mitigation strategy are highly vulnerable to attrition in their top tier — Profit+ helps by systematizing what those individuals know and encoding it in the platform.",
    category: "pain",
  },
  {
    id: "dq-hr-038",
    persona: "hr",
    question: "What is your organization's current view on the role of AI and structured data tools in the sales development process — is there organizational appetite for those tools, or skepticism?",
    why_it_matters: "Organizational readiness for AI-adjacent tools determines the adoption risk and the selling motion required.",
    good_answer_reveals: "Strong appetite means lower adoption risk; strong skepticism requires a framing that focuses on the process and methodology rather than the AI component.",
    category: "adoption",
  },
  {
    id: "dq-hr-039",
    persona: "hr",
    question: "In your experience, what is the most effective intervention when a sales manager is consistently undercoaching their team — and has your organization ever tried to address that structurally rather than individually?",
    why_it_matters: "Structural versus individual interventions for management quality reveal organizational maturity in addressing systematic coaching failures.",
    good_answer_reveals: "Organizations that have tried individual interventions for management quality without structural success are natural candidates for a structural platform like Profit+.",
    category: "pain",
  },
  {
    id: "dq-hr-040",
    persona: "hr",
    question: "If your HR team could provide sales leadership with one piece of data about their team that they don't currently have access to, what would it be — and why would that data be valuable?",
    why_it_matters: "Data gap identification reveals what HR believes would change decision quality for sales leadership.",
    good_answer_reveals: "The most common answers (idea-level progress data, lifecycle adherence data, early gap signals) map directly to what Profit+ produces — making the value proposition personally relevant.",
    category: "need",
  }
];

// =============================================================================
// BATTLE CARDS (4 total)
// =============================================================================

export const BATTLE_CARDS: TemplateBattleCard[] = [
  {
    id: "bc-spreadsheets",
    competitor: "spreadsheets",
    positioning: "Spreadsheets break the moment the savings program grows past one site or one finance owner. There is no audit trail, no version control, no G/L verification, and no enforcement of a consistent idea lifecycle. Profit+ replaces the spreadsheet with a system of record that Finance can defend to the board — not a reworked template, but a structured platform that captures every idea, routes it through a defined lifecycle (Idea \u2192 Validate \u2192 Evaluate \u2192 Implement \u2192 Executed \u2192 Realized), and verifies savings against the actual G/L before the number goes on a report. The spreadsheet works until it doesn't — and in a multi-site manufacturing environment, it usually doesn't.",
    strengths: [
      "Single source of truth across every plant: every idea lives in one place, not scattered across plant-level files and email threads.",
      "Idea lifecycle enforced: Profit+ enforces Idea \u2192 Validate \u2192 Evaluate \u2192 Implement \u2192 Executed \u2192 Realized — no idea moves forward without the right sign-offs in place.",
      "G/L verification by Finance built in: savings are not recognized until Finance verifies them against the actual general ledger — the number the board sees is defensible.",
      "Audit trail per idea: every action, comment, approval, and change is time-stamped and attributed — no more 'who approved this' conversations at quarter-end.",
      "Role-based access: BDRs, AEs, plant managers, Finance, and executives each see what they need — nothing more, nothing less.",
      "Real-time gap-to-goal dashboards: leadership sees progress against the savings target in real time, not the day after the quarter closes.",
      "No broken formulas at quarter-end: the platform does not break when someone adds a column, renames a tab, or leaves the company.",
    ],
    weaknesses: [
      "Very small organizations — single site, one finance owner — may not yet feel the pain acutely enough to justify the change. The spreadsheet may genuinely be sufficient until scale creates the problem.",
      "Teams that have never enforced a structured idea lifecycle will face a culture-change conversation. Profit+ supports that transition but does not substitute for it — change management is a real cost.",
    ],
    pivot: "Lead with the 'the spreadsheet works until it doesn't' framing. Ask how many plants are in the program, how many people touch the savings file, and what happens when the person who owns it leaves. Ask when the CFO last had to defend a savings number and could not trace it to a verified G/L entry. In most cases, the answer is 'last quarter' — and that is where the conversation starts.",
  },

  {
    id: "bc-manual-records",
    competitor: "manual_records",
    positioning: "Manual tracking — paper binders, sticky notes, whiteboard tallies, hallway conversations — gets the team to a number that everyone on the leadership team secretly distrusts. Ideas get lost in transit between shifts. Savings get double-counted across plants. No one can say with confidence what has been verified versus assumed. Profit+ captures every idea digitally at the source, structures the handoff between idea owner, validator, implementer, and Finance, and produces a defensible audit trail that withstands CFO scrutiny. The move from manual to structured is not a technology conversation — it is an accountability conversation.",
    strengths: [
      "Every idea captured digitally at the source: plant floor, engineering, procurement, maintenance — ideas entered when they occur, not recalled in a monthly meeting.",
      "Deduplication across sites: Profit+ flags when the same idea is being pursued at multiple plants so effort is coordinated, not duplicated.",
      "Structured handoff between roles: idea owner, validator, implementer, and Finance each have a defined role in the lifecycle — no more 'I thought you were handling that' gaps.",
      "Real-time visibility replaces the weekly update call: leadership sees where every idea stands without having to ask — the platform is the update.",
      "Defensible audit trail: every transition, approval, and rejection is logged — when the CFO asks how a number was reached, the answer is a click away.",
    ],
    weaknesses: [
      "Organizations with a deeply entrenched paper-based culture may need a longer change-management runway before the platform produces its full value. The technology is straightforward; the behavior change is not.",
      "Very small teams may find the structured lifecycle heavier than their current volume warrants. If the entire savings program is one person at one site tracking three ideas, the overhead may not be justified yet.",
    ],
    pivot: "Ask the CFO or COO when they last had to defend a savings number and could not trace it back to a verified G/L entry. The answer is almost always 'last quarter.' Then ask what that cost them in credibility with the board, their PE sponsor, or their own finance team. That is the value of accountability infrastructure — and Profit+ is how you build it.",
  },

  {
    id: "bc-homegrown",
    competitor: "homegrown",
    positioning: "The homegrown tool was built five years ago by someone who left. It half-works, nobody will touch the code, Finance trusts it less every quarter, and it has never been updated to reflect how the savings program actually operates today. Profit+ is the supported, maintained, multi-tenant platform that replaces the orphaned tool without losing the historical data. It brings a real product roadmap, a vendor support relationship, and a standardized lifecycle that does not depend on a single internal developer who may not be there next year.",
    strengths: [
      "Vendor-supported with a real roadmap: Profit+ is maintained, updated, and supported — there is no single internal developer whose departure kills the system.",
      "No dependence on legacy code: the system is replaced, not patched — the organization stops accumulating technical debt in the savings tracking function.",
      "Standardized lifecycle replaces ad-hoc workflows: Profit+ enforces Idea \u2192 Validate \u2192 Evaluate \u2192 Implement \u2192 Executed \u2192 Realized — replacing whatever process the homegrown tool was built around.",
      "Finance verification built in: G/L verification is a first-class feature, not a manual step bolted onto a system that was never designed for it.",
      "Data migration path from legacy systems: The partner team has supported transitions from homegrown tools before — historical data is not abandoned.",
      "Multi-site by default: Profit+ is designed for multi-plant manufacturing organizations, not extended from a single-site tool.",
    ],
    weaknesses: [
      "Organizations with heavily customized homegrown logic — unusual scoring models, non-standard approval workflows, plant-specific configurations — may need a discovery session to map what migrates cleanly and what needs to be rebuilt.",
      "Switching cost is real: training, change management, and the transition period where two systems briefly coexist require organizational commitment. This should be acknowledged upfront, not minimized.",
    ],
    pivot: "Ask one question: 'If the person who built that system left tomorrow, who fixes it?' The silence that follows is the deal. Then ask what the system does not do well today — what workarounds the team has built around it, what data Finance does not trust, and what it would take to replace it. The answer to those questions is the scope of what Profit+ delivers.",
  },

  {
    id: "bc-brand-x",
    competitor: "brand_x",
    positioning: "As far as the partner team knows, no other vendor covers the full Profit+ scope end-to-end: idea capture, scoring and prioritization, structured lifecycle enforcement (Idea \u2192 Validate \u2192 Evaluate \u2192 Implement \u2192 Executed \u2192 Realized), G/L verification by Finance, and real-time gap-to-goal reporting across multiple sites. If a prospect names a vendor the partner team does not recognize, the right move is to gather intelligence before positioning against it. Defending against a competitor you do not understand is a credibility risk. Curiosity is more effective than defense.",
    strengths: [
      "Ask what the prospect likes about it: the answer tells you what they value and what the bar is — and often surfaces gaps they have already noticed.",
      "Ask what is missing: prospects who are evaluating alternatives already have a list of unmet needs — find out what is on it.",
      "Ask what it costs and who uses it day-to-day: implementation cost and daily-user experience are the two most reliable indicators of whether the system is actually embedded or just purchased.",
      "Ask what the implementation took: long or painful implementations are a recurring objection risk that Profit+ can address directly.",
      "Get the intel back to the partner team: unknown vendors surfacing in manufacturing cost-optimization should be documented — if this becomes a pattern, the partner team needs to know.",
    ],
    weaknesses: [
      "If the unknown vendor genuinely covers the same ground as Profit+ (rare but possible), shift the conversation to an integration-or-replacement frame: what does the prospect not get from the current vendor, and is Profit+ complementary or superior on those dimensions.",
      "Do not position against a vendor you have not researched. A wrong claim — 'oh, they don't do G/L verification' — that the prospect knows is false ends the credibility of the conversation.",
    ],
    pivot: "Lead with curiosity, not defense. The script is: 'Tell me about that — what does it do well, what is missing, and what does it cost?' Get three answers before saying anything about Profit+. Then position on the gaps. If there are no gaps, that is useful information too — bring it back to the partner team to investigate the vendor and build a response.",
  },
];
